<template>
  <div
    class="planning-shell"
    @mousemove.passive="onMouseMove"
    @mouseup="onMouseUp"
    @mouseleave="onMouseUp"
    @touchmove.passive="onTouchMove"
    @touchend="onTouchEnd"
  >
    <!-- ── TOOLBAR (teleported to tabs bar) ── -->
    <Teleport to="#gantt-toolbar-slot">
      <template v-if="ganttEditMode">
        <label class="tab-action-cb-label" title="Selecionar todas">
          <input
            ref="selectAllEl"
            type="checkbox"
            class="tab-action-cb"
            :checked="isAllSelected"
            @change="toggleSelectAll"
          />
        </label>
        <span class="tab-action-hint">{{ selectedGanttIds.size }} sel.</span>
        <button
          v-if="selectedGanttIds.size > 0 && onDeleteTask && !isAnalyst"
          class="tab-action-btn tab-action-btn--danger"
          type="button"
          @click="deleteSelected"
        >
          Apagar
        </button>
        <button class="tab-action-btn" type="button" @click="cancelEditMode">Cancelar</button>
      </template>

      <!-- ── SELECTION MODE (tasks selected in view mode) ── -->
      <template v-else-if="selectedGanttIds.size > 0">
        <span class="tab-action-hint">{{ selectedGanttIds.size }} sel.</span>
        <div class="group-toolbar-wrap">
          <button
            v-if="accessMode === 'edit' && !isAnalyst && onSaveGroup"
            class="tab-action-btn tab-action-btn--group"
            type="button"
            @click="showGroupForm = !showGroupForm"
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" style="margin-right:4px;flex-shrink:0"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/><path d="M14 17h7M17 14v7"/></svg>
            Agrupar
          </button>
          <div v-if="showGroupForm" class="group-form-popover" @click.stop>
            <div class="group-form-title">Criar grupo</div>
            <input
              v-model="groupForm.name"
              class="group-form-input"
              placeholder="Nome do grupo (ex: Fundação)"
              autofocus
              @keydown.enter.prevent="saveGroup"
            />
            <select v-model="groupForm.responsibleUsername" class="group-form-input">
              <option value="">Sem responsável</option>
              <option v-for="u in assignableUsers" :key="u.username" :value="u.username">{{ u.name }}</option>
            </select>
            <div class="group-form-actions">
              <button class="group-form-btn group-form-btn--primary" type="button" :disabled="!groupForm.name.trim() || groupSaving" @click="saveGroup">
                {{ groupSaving ? 'Salvando…' : 'Criar grupo' }}
              </button>
              <button class="group-form-btn" type="button" @click="showGroupForm = false">Cancelar</button>
            </div>
          </div>
        </div>
        <button
          v-if="selectedGanttIds.size > 0 && onDeleteTask && !isAnalyst"
          class="tab-action-btn tab-action-btn--danger"
          type="button"
          @click="deleteSelected"
        >Apagar</button>
        <button class="tab-action-btn" type="button" @click="clearSelection">Cancelar</button>
      </template>

      <template v-else>
        <div class="filter-wrap" ref="filterRef">
          <button class="tab-action-btn" :class="{ active: showFilters }" type="button" @click="showFilters = !showFilters">
            Filtros<span v-if="activeFilterCount > 0" class="filter-count-badge">{{ activeFilterCount }}</span>
          </button>
          <div v-if="showFilters" class="filter-backdrop" @click="showFilters = false"></div>
          <div v-if="showFilters" class="filter-dropdown" role="dialog" aria-label="Filtros do planejamento">
            <div class="filter-sheet-grip" aria-hidden="true"></div>
            <div class="filter-sheet-head">
              <span class="filter-sheet-title">Filtros</span>
              <button class="filter-sheet-close" type="button" aria-label="Fechar filtros" @click="showFilters = false">✕</button>
            </div>
            <div class="filter-sheet-body">
              <div class="filter-section">
                <span class="filter-section-label">Visualização</span>
                <div class="filter-zoom-row">
                  <button class="zoom-btn" :class="{ active: zoom === 'day' }" type="button" @click="setZoom('day')">Dia</button>
                  <button class="zoom-btn" :class="{ active: zoom === 'week' }" type="button" @click="setZoom('week')">Semana</button>
                  <button class="zoom-btn" :class="{ active: zoom === 'month' }" type="button" @click="setZoom('month')">Mês</button>
                  <button class="zoom-btn" type="button" @click="scrollToToday">Hoje</button>
                </div>
                <div class="filter-scale-row">
                  <input
                    type="range"
                    min="25"
                    max="350"
                    step="5"
                    v-model.number="zoomScale"
                    class="scale-slider"
                  />
                  <span class="scale-value">{{ zoomScale }}%</span>
                </div>
              </div>
              <div class="filter-section">
                <span class="filter-section-label">Período</span>
                <div class="filter-shortcuts">
                  <button class="filter-chip" type="button" @click="applyThisWeek">Essa semana</button>
                  <button class="filter-chip" type="button" @click="applyNextWeek">Próx. semana</button>
                  <button class="filter-chip" type="button" @click="applyLast2Weeks">Últ. 2 semanas</button>
                  <button class="filter-chip" type="button" @click="applyLastMonth">Último mês</button>
                </div>
                <div class="filter-dates">
                  <div class="filter-field">
                    <label>De</label>
                    <input v-model="filters.dateFrom" type="date" class="filter-input" />
                  </div>
                  <div class="filter-field">
                    <label>Até</label>
                    <input v-model="filters.dateTo" type="date" class="filter-input" />
                  </div>
                </div>
              </div>
              <div class="filter-section">
                <span class="filter-section-label">Filtrar por</span>
                <div class="filter-field">
                  <label>Responsável</label>
                  <select v-model="filters.assignee" class="filter-input">
                    <option value="">Todos</option>
                    <option v-for="u in assigneeOptions" :key="u" :value="u">{{ u }}</option>
                  </select>
                </div>
                <div v-if="groups.length > 0" class="filter-field">
                  <label>Grupo</label>
                  <select v-model="filters.groupId" class="filter-input">
                    <option value="">Todos</option>
                    <option v-for="g in groups" :key="g.id" :value="g.id">{{ g.name }}</option>
                  </select>
                </div>
                <label class="filter-check-row">
                  <input type="checkbox" v-model="filters.hideCompleted" />
                  <span>Ocultar concluídas</span>
                </label>
                <label class="filter-check-row">
                  <input type="checkbox" v-model="filters.showOverdue" />
                  <span>Mostrar atrasadas</span>
                </label>
              </div>
              <template v-if="accessMode === 'edit' && !isAnalyst">
                <div class="filter-divider"></div>
                <button
                  class="toolbar-btn filter-import-btn"
                  type="button"
                  @click="openImportModal(); showFilters = false"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style="flex-shrink:0"><path d="M7 1v8M4 6l3 3 3-3M2 11h10" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  Importar tarefas
                </button>
              </template>
            </div>
            <div class="filter-sheet-foot">
              <button class="filter-foot-btn" type="button" :disabled="activeFilterCount === 0" @click="clearFilters">Limpar</button>
              <button class="filter-foot-btn filter-foot-btn--primary" type="button" @click="showFilters = false">
                Ver {{ filteredRows.length + undatedRows.length }} tarefa{{ (filteredRows.length + undatedRows.length) === 1 ? '' : 's' }}
              </button>
            </div>
          </div>
        </div>
        <button
          class="tab-action-btn tab-action-btn--share"
          type="button"
          @click.stop="openShareModal"
        >
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style="margin-right:5px;flex-shrink:0"><circle cx="10.5" cy="2.5" r="1.8" stroke="currentColor" stroke-width="1.3"/><circle cx="10.5" cy="10.5" r="1.8" stroke="currentColor" stroke-width="1.3"/><circle cx="2.5" cy="6.5" r="1.8" stroke="currentColor" stroke-width="1.3"/><path d="M4.2 5.6l4.6-2.3M4.2 7.4l4.6 2.3" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
          Compartilhar
        </button>
        <button
          v-if="projectId"
          class="tab-action-btn tab-action-btn--report"
          type="button"
          @click.stop="openGanttReport"
        >
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" style="margin-right:5px;flex-shrink:0"><rect x="2" y="1.5" width="9" height="10" rx="1.2" stroke="currentColor" stroke-width="1.3"/><path d="M4 4.5h5M4 6.5h5M4 8.5h3" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>
          Relatório
        </button>
      </template>
    </Teleport>

    <!-- ── CONTENT ROW (gantt + panel) ── -->
    <div class="planning-row" :class="{ 'has-panel': showPanel }">
      <div class="planning-content">

        <!-- ── TOPBAR: search + status chips + overall progress ── -->
        <div v-if="rows.length > 0" class="planning-topbar">
          <div class="topbar-search">
            <svg class="topbar-search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.3-4.3"/></svg>
            <input v-model="filters.search" type="text" class="topbar-search-input" placeholder="Buscar tarefa..." />
            <button v-if="filters.search" class="topbar-search-clear" type="button" title="Limpar busca" @click="filters.search = ''">✕</button>
          </div>
          <div class="status-chips">
            <button class="status-chip" :class="{ active: filters.status === '' }" type="button" @click="filters.status = ''">
              Todas <span class="chip-count">{{ statusCounts.total }}</span>
            </button>
            <button class="status-chip status-chip--idle" :class="{ active: filters.status === 'idle' }" type="button" @click="setStatusFilter('idle')">
              Não iniciadas <span class="chip-count">{{ statusCounts.idle }}</span>
            </button>
            <button class="status-chip status-chip--progress" :class="{ active: filters.status === 'progress' }" type="button" @click="setStatusFilter('progress')">
              Em andamento <span class="chip-count">{{ statusCounts.progress }}</span>
            </button>
            <button class="status-chip status-chip--done" :class="{ active: filters.status === 'done' }" type="button" @click="setStatusFilter('done')">
              Concluídas <span class="chip-count">{{ statusCounts.done }}</span>
            </button>
            <button class="status-chip status-chip--late" :class="{ active: filters.status === 'late' }" type="button" @click="setStatusFilter('late')">
              Atrasadas <span class="chip-count">{{ statusCounts.late }}</span>
            </button>
            <button v-if="statusCounts.nodate > 0" class="status-chip status-chip--nodate" :class="{ active: filters.status === 'nodate' }" type="button" @click="setStatusFilter('nodate')">
              Sem data <span class="chip-count">{{ statusCounts.nodate }}</span>
            </button>
          </div>
          <div class="topbar-overall" title="Progresso médio de todas as tarefas">
            <div class="topbar-overall-track"><div class="topbar-overall-fill" :style="{ width: overallPct + '%' }"></div></div>
            <span class="topbar-overall-pct">{{ overallPct }}%</span>
          </div>
        </div>

        <!-- ── SKELETON (carregamento inicial) ── -->
        <div v-if="loading" class="gantt-skeleton">
          <div v-for="i in 6" :key="i" class="gantt-skeleton-row">
            <SkeletonBlock width="180px" height="13px" />
            <SkeletonBlock :width="skeletonBarWidth(i)" height="20px" radius="4px" />
          </div>
        </div>

        <!-- ── EMPTY STATE ── -->
        <div v-else-if="filteredRows.length === 0 && undatedRows.length === 0" class="empty-state">
          <p>{{ rows.length === 0 ? 'Nenhuma tarefa no planejamento.' : 'Nenhuma tarefa encontrada com os filtros atuais.' }}</p>
          <button
            v-if="accessMode === 'edit' && !isAnalyst && rows.length === 0"
            class="empty-add-btn"
            type="button"
            @click="openCreatePanel"
          >+ Adicionar tarefa</button>
          <button
            v-else-if="rows.length > 0"
            class="empty-add-btn"
            type="button"
            @click="clearFilters"
          >Limpar filtros</button>
        </div>

        <!-- ── MAIN BODY ── -->
        <div v-if="filteredRows.length > 0" class="planning-body">

      <!-- Left label column -->
      <div class="gantt-labels" ref="labelsRef" :style="{ width: labelsWidth + 'px', minWidth: labelsWidth + 'px' }">
        <div class="label-header">
          <label v-if="ganttEditMode" class="lbl-cb-label" title="Selecionar todas" @click.stop>
            <input
              ref="selectAllEl"
              type="checkbox"
              class="lbl-cb"
              :checked="isAllSelected"
              @change="toggleSelectAll"
            />
          </label>
          <span class="label-header-title">Tarefa</span>
          <div class="label-header-actions">
            <button
              v-if="accessMode === 'edit' && !ganttEditMode && !isAnalyst"
              class="label-hdr-btn"
              type="button"
              title="Adicionar tarefa"
              @click.stop="openCreatePanel"
            >+</button>
            <button
              v-if="accessMode === 'edit' && !ganttEditMode && !isAnalyst"
              class="label-hdr-btn"
              type="button"
              title="Editar tarefas"
              @click.stop="ganttEditMode = true"
            >Editar</button>
          </div>
          <span v-if="!ganttEditMode" class="label-header-progress">%</span>
        </div>
        <div
          v-for="row in filteredRows"
          :key="'lbl-' + row.id"
          class="label-row"
          :class="{
            selected: selectedTaskId === row.id && selectedGanttIds.size <= 1,
            'label-row--checked': ganttEditMode && selectedGanttIds.has(row.id),
            'label-row--multi': !ganttEditMode && selectedGanttIds.has(row.id)
          }"
          :style="{ height: rowHeightPx + 'px', borderLeftColor: (!ganttEditMode && selectedGanttIds.has(row.id)) ? (row.color || 'var(--accent)') : (row.groupId && groupById.get(row.groupId) ? groupById.get(row.groupId)!.color : 'transparent') }"
          @click="handleRowClick(row.id)"
        >
          <label v-if="ganttEditMode" class="lbl-cb-label" @click.stop>
            <input
              type="checkbox"
              class="lbl-cb"
              :checked="selectedGanttIds.has(row.id)"
              @change="toggleSelectRow(row.id)"
            />
          </label>
          <div class="label-name" :title="row.title">{{ row.title }}</div>
          <div class="label-meta">
            <span v-if="row.groupId && groupById.get(row.groupId)" class="group-badge" :style="{ background: groupById.get(row.groupId)!.color + '22', color: groupById.get(row.groupId)!.color }">{{ groupById.get(row.groupId)!.name }}</span>
            <span v-else>{{ assigneeName(row.assigneeUsername) }}</span>
          </div>
          <div v-if="!ganttEditMode" class="label-progress-wrap">
            <div class="label-progress-track">
              <div class="label-progress-fill" :style="{ width: latestProgressPct(row) + '%', background: row.color || 'var(--accent)' }"></div>
            </div>
            <span class="label-progress-pct">{{ latestProgressPct(row) }}%</span>
          </div>
        </div>
      </div>

      <!-- Resize handle between labels and timeline -->
      <div class="label-resize-handle" @mousedown.stop="startLabelResize" @touchstart.stop.prevent="startLabelResizeTouch"></div>

      <!-- Scrollable timeline -->
      <div class="gantt-scroll" ref="scrollRef" @scroll.passive="syncLabelScroll" :class="{ 'gantt-scroll--hidden': panelExpanded }">
        <div class="timeline" :style="{ width: totalWidthPx + 'px' }">

          <!-- Sticky header -->
          <div class="timeline-header" :style="{ width: totalWidthPx + 'px' }">
            <!-- Month groups -->
            <div class="header-groups" :style="{ height: '26px' }">
              <div
                v-for="grp in headerGroups"
                :key="grp.key"
                class="header-group"
                :style="{ left: grp.leftPx + 'px', width: grp.widthPx + 'px' }"
              >{{ grp.label }}</div>
            </div>
            <!-- Day cells -->
            <div class="header-days" :style="{ height: '26px' }">
              <div
                v-for="day in headerDays"
                :key="'hd-' + day.key"
                class="header-day"
                :class="{ today: day.isToday, weekend: day.isWeekend, blocked: day.isBlocked }"
                :style="{ width: dayWidthPx + 'px' }"
              >{{ day.label }}</div>
            </div>
          </div>

          <!-- Timeline body -->
          <div
            class="timeline-body"
            :style="{ width: totalWidthPx + 'px', height: timelineBodyHeightPx + 'px' }"
          >
            <!-- Weekend columns -->
            <div
              v-for="day in weekendDays"
              :key="'wknd-' + day.key"
              class="col-weekend"
              :style="{ left: day.leftPx + 'px', width: dayWidthPx + 'px', height: timelineBodyHeightPx + 'px' }"
            ></div>
            <!-- Blocked date columns -->
            <div
              v-for="day in blockedDayCells"
              :key="'blk-' + day.key"
              class="col-blocked"
              :style="{ left: day.leftPx + 'px', width: dayWidthPx + 'px', height: timelineBodyHeightPx + 'px' }"
            ></div>
            <!-- Today line -->
            <div
              v-if="todayLeftPx !== null"
              class="today-line"
              :style="{ left: todayLeftPx + 'px', height: timelineBodyHeightPx + 'px' }"
            >
              <span class="today-label">Hoje</span>
            </div>

            <!-- Track rows -->
            <div
              v-for="(row, index) in filteredRows"
              :key="'track-' + row.id"
              class="track-row"
              :class="{ selected: selectedTaskId === row.id && selectedGanttIds.size <= 1, 'track-row--multi': !ganttEditMode && selectedGanttIds.has(row.id) }"
              :style="{ top: index * rowHeightPx + 'px', height: rowHeightPx + 'px' }"
            >
              <div
                class="bar"
                :class="{
                  selected: selectedTaskId === row.id && selectedGanttIds.size <= 1,
                  'bar--multi': !ganttEditMode && selectedGanttIds.has(row.id),
                  dragging: dragState !== null && dragState.taskId === row.id
                }"
                :style="computedBarStyle(row)"
                @mousedown.stop="startDrag($event, row)"
                @click.stop="handleRowClick(row.id)"
              >
                <!-- Progress fill -->
                <div class="bar-progress" :style="{ width: latestProgressPct(row) + '%' }"></div>
                <!-- Today marker within bar -->
                <div
                  v-if="todayBarPct(row) !== null && todayBarPct(row)! > 0 && todayBarPct(row)! < 100"
                  class="bar-today-marker"
                  :style="{ left: todayBarPct(row) + '%' }"
                ></div>
                <!-- Percentage centered, can overflow narrow bars -->
                <span class="bar-center-pct">{{ latestProgressPct(row) }}%</span>
              </div>
              <!-- Overdue extension bar -->
              <div
                v-if="overdueBarStyle(row).display !== 'none'"
                class="bar bar-overdue"
                :style="overdueBarStyle(row)"
                @click.stop="handleRowClick(row.id)"
              ></div>
            </div>

            <!-- Dependency arrows SVG -->
            <svg
              class="deps-svg"
              :style="{ width: totalWidthPx + 'px', height: timelineBodyHeightPx + 'px' }"
              overflow="visible"
            >
              <defs>
                <marker id="dep-arrow" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
                  <path d="M0,1 L0,6 L6,3.5 z" fill="rgba(120,120,130,0.7)" />
                </marker>
              </defs>
              <path
                v-for="dep in dependencyLines"
                :key="dep.key"
                :d="dep.d"
                fill="none"
                stroke="rgba(120,120,130,0.55)"
                stroke-width="1.5"
                stroke-dasharray="5,3"
                marker-end="url(#dep-arrow)"
              />
            </svg>
          </div>
        </div>
      </div>
        </div> <!-- /planning-body -->

        <!-- ── UNDATED TASKS SECTION ── -->
        <div v-if="undatedRows.length > 0" class="undated-section">
          <button class="undated-header" type="button" @click="undatedCollapsed = !undatedCollapsed">
            <svg class="undated-chevron" :class="{ open: !undatedCollapsed }" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
            <span class="undated-title">Sem data definida</span>
            <span class="undated-count">{{ undatedRows.length }}</span>
            <span class="undated-hint">atividades aguardando agendamento</span>
          </button>
          <div v-if="!undatedCollapsed" class="undated-list">
            <div
              v-for="row in undatedRows"
              :key="'nd-' + row.id"
              class="undated-row"
              :class="{
                selected: selectedTaskId === row.id && selectedGanttIds.size <= 1,
                'undated-row--multi': !ganttEditMode && selectedGanttIds.has(row.id),
                'undated-row--checked': ganttEditMode && selectedGanttIds.has(row.id)
              }"
              @click="handleRowClick(row.id)"
            >
              <label v-if="ganttEditMode" class="lbl-cb-label" @click.stop>
                <input type="checkbox" class="lbl-cb" :checked="selectedGanttIds.has(row.id)" @change="toggleSelectRow(row.id)" />
              </label>
              <span class="undated-dot" :style="{ background: row.color || 'var(--accent)' }"></span>
              <div class="undated-info">
                <div class="undated-name" :title="row.title">{{ row.title }}</div>
                <div class="undated-meta">
                  <span v-if="row.groupId && groupById.get(row.groupId)" class="group-badge" :style="{ background: groupById.get(row.groupId)!.color + '22', color: groupById.get(row.groupId)!.color }">{{ groupById.get(row.groupId)!.name }}</span>
                  <span>{{ assigneeName(row.assigneeUsername) }}</span>
                </div>
              </div>
              <span class="status-pill" :class="'status-pill--' + statusOf(row)">{{ statusLabel(statusOf(row)) }}</span>
              <div class="undated-progress">
                <div class="undated-progress-track">
                  <div class="undated-progress-fill" :style="{ width: latestProgressPct(row) + '%', background: row.color || 'var(--accent)' }"></div>
                </div>
                <span class="undated-progress-pct">{{ latestProgressPct(row) }}%</span>
              </div>
              <button
                v-if="accessMode === 'edit' && !isAnalyst && !ganttEditMode"
                class="undated-schedule-btn"
                type="button"
                title="Definir data para esta atividade"
                @click.stop="openScheduleTask(row.id)"
              >
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18"/></svg>
                Agendar
              </button>
            </div>
          </div>
        </div>
      </div> <!-- /planning-content -->

      <!-- ── SIDE PANEL ── -->
      <div v-if="showPanel" class="edit-panel" :class="{ 'edit-panel--expanded': panelExpanded }">
        <div class="panel-header">
          <span
            class="panel-title"
            :style="{ borderLeft: '3px solid ' + (panelMode === 'create' ? 'var(--accent)' : (selectedTask?.color || 'var(--accent)')) }"
          >
            {{ panelMode === 'create' ? 'Nova tarefa' : selectedTask?.title }}
          </span>
          <button class="panel-expand-btn" type="button" :title="panelExpanded ? 'Recolher' : 'Expandir'" @click="panelExpanded = !panelExpanded">
            <span v-if="panelExpanded">⇥</span><span v-else>⇤</span>
          </button>
          <button class="panel-close" type="button" @click="closePanel">✕</button>
        </div>

        <div class="panel-body">
          <!-- View mode: info + actions -->
          <template v-if="panelMode === 'view' && selectedTask">
            <div class="panel-info">
              <div class="info-row"><span>Responsavel</span><strong>{{ assigneeName(selectedTask.assigneeUsername) }}</strong></div>
              <div v-if="isUndated(selectedTask)" class="info-row">
                <span>Datas</span>
                <strong class="info-nodate">Sem data definida</strong>
              </div>
              <template v-else>
                <div class="info-row"><span>Inicio</span><strong>{{ formatDate(selectedTask.plannedStart) }}</strong></div>
                <div class="info-row"><span>Fim</span><strong>{{ formatDate(selectedTask.plannedEnd) }}</strong></div>
              </template>
              <div class="info-row"><span>Status</span><span class="status-pill" :class="'status-pill--' + statusOf(selectedTask)">{{ statusLabel(statusOf(selectedTask)) }}</span></div>
              <div class="info-row"><span>Progresso</span><strong>{{ latestProgressPct(selectedTask) }}%</strong></div>
              <div v-if="selectedTask.description" class="info-row info-row--block">
                <span>Descricao</span>
                <p class="info-description">{{ selectedTask.description }}</p>
              </div>
              <div v-if="selectedTask.dependencyTaskIds && selectedTask.dependencyTaskIds.length > 0" class="info-row info-row--block">
                <span>Pendências</span>
                <div class="info-dep-tags">
                  <span
                    v-for="depId in selectedTask.dependencyTaskIds"
                    :key="depId"
                    class="info-dep-tag"
                  >{{ tasks.find(t => t.id === depId)?.title || '?' }}</span>
                </div>
              </div>
              <div v-if="selectedTask.infraestruturaNecessaria" class="info-row info-row--block">
                <span>Infraestrutura necessária</span>
                <p class="info-description">{{ selectedTask.infraestruturaNecessaria }}</p>
              </div>
              <div v-if="selectedTask.requiredTools && selectedTask.requiredTools.length > 0" class="info-row info-row--block">
                <span>Ferramentas necessárias</span>
                <div class="info-tool-section">
                  <div
                    v-for="tool in selectedTask.requiredTools"
                    :key="tool.itemId"
                    class="info-tool-item"
                  >
                    <span class="info-tool-tag">
                      {{ warehouseItems.find(t => t.id === tool.itemId)?.name || tool.itemId }} × {{ tool.quantity }}
                    </span>
                    <span
                      :class="['info-tool-status', 'status-' + (selectedTask.toolRequests?.find(r => r.itemId === tool.itemId)?.status || 'pendente')]"
                    >
                      {{ selectedTask.toolRequests?.find(r => r.itemId === tool.itemId)?.status || 'pendente' }}
                    </span>
                  </div>
                </div>
              </div>
              <div v-if="selectedTask.requiredMachinery && selectedTask.requiredMachinery.length > 0" class="info-row info-row--block">
                <span>Maquinário necessário</span>
                <div class="info-machinery-section">
                  <div
                    v-for="mach in selectedTask.requiredMachinery"
                    :key="mach.machineryId"
                    class="info-machinery-item"
                  >
                    <span class="info-machinery-tag">
                      {{ heavyMachinery.find(m => m.id === mach.machineryId)?.title || mach.machineryId }}
                    </span>
                  </div>
                </div>
              </div>
              <div v-if="selectedTask.teamIds && selectedTask.teamIds.length > 0" class="info-row info-row--block">
                <span>Equipes</span>
                <div class="info-team-tags">
                  <span
                    v-for="teamId in selectedTask.teamIds"
                    :key="teamId"
                    class="info-team-tag"
                  >{{ teams.find(t => t.id === teamId)?.name || '?' }}</span>
                </div>
              </div>
            </div>
            <div class="panel-track">
              <div class="panel-progress-bar">
                <div class="panel-progress-fill" :style="{ width: latestProgressPct(selectedTask) + '%', background: selectedTask.color || 'var(--accent)' }"></div>
              </div>
            </div>
            <div class="panel-actions">
              <button class="btn btn-primary" type="button" @click="openProgressMode">Atualizar progresso</button>
              <button v-if="accessMode === 'edit' && !isAnalyst" class="btn btn-ghost" type="button" @click="openEditMode">Editar tarefa</button>
              <button v-if="accessMode === 'edit' && !isAnalyst" class="btn btn-ghost" type="button" @click="openBlockedDaysModal">Dias não trabalhados</button>
            </div>

            <!-- Progress history -->
            <div v-if="selectedTask.progressUpdates && selectedTask.progressUpdates.length > 0" class="progress-history">
              <h5 class="progress-history-title">Histórico de progresso</h5>
              <div
                v-for="(upd, idx) in [...(selectedTask.progressUpdates || [])].reverse()"
                :key="upd.id || idx"
                class="progress-history-item"
                :class="{ 'progress-history-item--open': isProgressExpanded(upd, idx) }"
              >
                <div class="progress-history-head">
                  <button
                    type="button"
                    class="progress-history-row"
                    :class="{ 'progress-history-row--static': !hasProgressDetails(upd) }"
                    @click="toggleProgressExpanded(upd, idx)"
                  >
                    <span class="progress-history-pct">{{ upd.progressPercent ?? 0 }}%</span>
                    <span class="progress-history-date">{{ formatDate(upd.progressDate) }}</span>
                    <span class="progress-history-badges">
                      <svg v-if="upd.notes" class="progress-badge-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      <svg v-if="upd.photoPath" class="progress-badge-icon" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>
                    </span>
                    <svg v-if="hasProgressDetails(upd)" class="progress-history-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"/></svg>
                  </button>
                  <button
                    v-if="accessMode === 'edit' && upd.id && onDeleteProgress && !isAnalyst"
                    class="progress-history-del"
                    type="button"
                    title="Remover"
                    @click="deleteProgressUpdate(upd.id!)"
                  >✕</button>
                </div>
                <div v-if="isProgressExpanded(upd, idx) && hasProgressDetails(upd)" class="progress-history-detail">
                  <p v-if="upd.notes" class="progress-detail-note">{{ upd.notes }}</p>
                  <img
                    v-if="upd.photoPath"
                    :src="resolvePhotoPath(upd.photoPath)"
                    class="progress-detail-photo"
                    alt="Foto da atualização"
                    @click="lightboxSrc = resolvePhotoPath(upd.photoPath)"
                  />
                </div>
              </div>
            </div>

            <!-- Comments -->
            <div class="comments-section">
              <h5 class="progress-history-title">Comentários <span class="comments-count">{{ taskComments.length }}</span></h5>
              <div v-if="commentsLoading" class="comments-loading">Carregando…</div>
              <div v-else-if="taskComments.length === 0" class="comments-empty">Nenhum comentário ainda.</div>
              <div v-else class="comments-list">
                <div v-for="c in taskComments" :key="c.id" class="comment-item">
                  <div class="comment-avatar" :style="{ background: commentAvatarColor(c.authorName) }">
                    <img v-if="c.authorAvatar" :src="$resolveUrl(c.authorAvatar)" class="comment-avatar-img" />
                    <span v-else>{{ commentInitials(c.authorName) }}</span>
                  </div>
                  <div class="comment-body">
                    <div class="comment-meta">
                      <span class="comment-author">{{ c.authorName }}</span>
                      <span class="comment-date">{{ formatDate(c.createdAt.slice(0,10)) }}</span>
                    </div>
                    <p class="comment-text">{{ c.message }}</p>
                  </div>
                  <button v-if="c.authorUsername === currentUsername || isAdmin" type="button" class="comment-del" @click="deleteComment(c.id)">✕</button>
                </div>
              </div>
              <div class="comment-input-row">
                <textarea v-model="newComment" class="comment-input" placeholder="Escrever comentário…" rows="2" @keydown.ctrl.enter="submitComment" />
                <button class="btn btn-primary comment-send" type="button" :disabled="!newComment.trim()" @click="submitComment">Enviar</button>
              </div>
            </div>
          </template>

          <!-- Progress update form -->
          <template v-else-if="panelMode === 'progress'">
            <h4 class="panel-section-title">Atualizar progresso</h4>
            <div class="form-grid">
              <div class="input-field">
                <label>Data</label>
                <input v-model="progressForm.date" type="date" />
              </div>
              <div class="input-field">
                <label>Progresso (%)</label>
                <input v-model.number="progressForm.percent" type="number" min="0" max="100" />
              </div>
              <div class="input-field">
                <label>Observacoes</label>
                <input v-model="progressForm.notes" type="text" placeholder="Opcional" />
              </div>
              <div class="input-field full-width">
                <label>Foto (opcional)</label>
                <div class="photo-upload-area" @click="progressPhotoInput?.click()">
                  <img v-if="progressPhotoPreview" :src="progressPhotoPreview" class="photo-preview" />
                  <span v-else class="photo-upload-hint">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none"><rect x="1" y="4" width="18" height="13" rx="2" stroke="currentColor" stroke-width="1.4"/><circle cx="10" cy="10.5" r="3" stroke="currentColor" stroke-width="1.4"/><path d="M7 4l1.5-2.5h3L13 4" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
                    Adicionar foto
                  </span>
                  <button v-if="progressPhotoPreview" type="button" class="photo-remove" @click.stop="progressPhotoPreview = ''; progressPhotoData = ''">✕</button>
                </div>
                <input ref="progressPhotoInput" type="file" accept="image/*" style="display:none" @change="onProgressPhoto" />
              </div>
            </div>
            <div class="panel-actions">
              <button class="btn btn-primary" type="button" @click="submitProgress">Salvar</button>
              <button class="btn btn-ghost" type="button" @click="panelMode = 'view'">Cancelar</button>
            </div>
          </template>

          <!-- Edit task form -->
          <template v-else-if="panelMode === 'edit'">
            <h4 class="panel-section-title">Editar tarefa</h4>
            <div class="form-grid">
              <div class="input-field">
                <label>Titulo</label>
                <input v-model="editForm.title" type="text" />
              </div>
              <div class="input-field">
                <label>Descricao</label>
                <input v-model="editForm.description" type="text" />
              </div>
              <div class="input-field">
                <label>Responsavel</label>
                <select v-model="editForm.assigneeUsername">
                  <option v-for="u in assignableUsers" :key="u.username" :value="u.username">{{ u.name }}</option>
                </select>
              </div>
              <div class="input-field">
                <label>Duracao (dias)</label>
                <input v-model.number="editForm.durationDays" type="number" min="1" />
              </div>
              <div class="input-field">
                <label>Cor</label>
                <div class="color-picker-wrap">
                  <input v-model="editForm.color" type="color" class="color-input" @change="useColor(editForm.color)" />
                  <div v-if="recentColors.length" class="recent-colors">
                    <span class="recent-colors-label">Recentes</span>
                    <button v-for="c in recentColors" :key="c" type="button" class="recent-color-swatch" :style="{ background: c }" :title="c" @click="editForm.color = c" />
                  </div>
                </div>
              </div>
              <div class="input-field">
                <label>Data de inicio forcada</label>
                <input v-model="editForm.forcedStartDate" type="date" :disabled="editForm.noDate" :class="{ 'input-disabled': editForm.noDate }" />
                <label class="nodate-check">
                  <input type="checkbox" v-model="editForm.noDate" />
                  <span>Sem data definida</span>
                </label>
                <p v-if="editForm.noDate" class="nodate-hint">A atividade ficará na lista "Sem data definida", fora da linha do tempo.</p>
              </div>
              <div class="input-field">
                <label>Dias de trabalho</label>
                <div class="workdays-row">
                  <label v-for="wd in weekdays" :key="wd.value" class="wd-check">
                    <input type="checkbox" :value="wd.value" v-model="editForm.workDays" />
                    {{ wd.label }}
                  </label>
                </div>
              </div>
              <div class="input-field">
                <label>Pendências</label>
                <div class="dep-combo">
                  <div v-if="editForm.dependencyTaskIds.length > 0" class="dep-tags">
                    <span v-for="id in editForm.dependencyTaskIds" :key="id" class="dep-tag">
                      {{ tasks.find(t => t.id === id)?.title || id }}
                      <button type="button" class="dep-tag-remove" @click="removeDep(id)">✕</button>
                    </span>
                  </div>
                  <div class="dep-search-wrap">
                    <input
                      v-model="depSearch"
                      type="text"
                      class="dep-search-input"
                      placeholder="Buscar tarefa..."
                      autocomplete="off"
                      @focus="depSearchOpen = true"
                      @blur="closeDepSearch"
                    />
                    <div v-if="depSearchOpen && depSearchResults.length > 0" class="dep-search-dropdown">
                      <div
                        v-for="t in depSearchResults"
                        :key="t.id"
                        class="dep-search-item"
                        @mousedown.prevent="addDep(t.id)"
                      >{{ t.title }}</div>
                    </div>
                    <div v-if="depSearchOpen && depSearchResults.length === 0 && depSearch" class="dep-search-dropdown">
                      <div class="dep-search-empty">Nenhuma tarefa encontrada</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="input-field full-width">
                <label>Ferramentas necessárias</label>
                <div class="tool-combo">
                  <div v-if="editForm.requiredTools.length > 0" class="tool-tags">
                    <span v-for="tool in editForm.requiredTools" :key="tool.itemId" class="tool-tag">
                      {{ warehouseItems.find(t => t.id === tool.itemId)?.name || tool.itemId }} × {{ tool.quantity }}
                      <button type="button" class="tool-tag-remove" @click="removeTool(tool.itemId)">✕</button>
                    </span>
                  </div>
                  <div class="tool-search-wrap">
                    <input
                      v-model="toolSearch"
                      type="text"
                      class="tool-search-input"
                      placeholder="Buscar ferramenta..."
                      autocomplete="off"
                      @focus="toolSearchOpen = true"
                      @blur="closeToolSearch"
                    />
                    <div v-if="toolSearchOpen && toolSearchResults.length > 0" class="tool-search-dropdown">
                      <div
                        v-for="item in toolSearchResults"
                        :key="item.id"
                        class="tool-search-item"
                        @mousedown.prevent="selectedToolForQuantity = item.id"
                      >
                        <span>{{ item.name }}</span>
                        <span class="tool-operational">({{ item.operationalUnits }} disponível)</span>
                      </div>
                    </div>
                    <div v-if="toolSearchOpen && toolSearchResults.length === 0 && toolSearch" class="tool-search-dropdown">
                      <div class="tool-search-empty">Nenhuma ferramenta encontrada</div>
                    </div>
                  </div>
                  <div v-if="selectedToolForQuantity" class="tool-quantity-input-wrap">
                    <input
                      v-model.number="toolQuantityInput"
                      type="number"
                      min="1"
                      class="tool-quantity-input"
                      placeholder="Qtd"
                      @keyup.enter="addTool(selectedToolForQuantity, toolQuantityInput)"
                    />
                    <button
                      type="button"
                      class="tool-add-btn"
                      @click="addTool(selectedToolForQuantity, toolQuantityInput)"
                    >Adicionar</button>
                  </div>
                </div>
              </div>
              <div class="input-field full-width">
                <label>Infraestrutura necessária</label>
                <textarea v-model="editForm.infraestruturaNecessaria" placeholder="Descreva a infraestrutura necessária para esta tarefa..." rows="3"></textarea>
              </div>
              <div class="input-field full-width">
                <label>Arquivos linkados</label>
                <div class="file-combo">
                  <div v-if="editForm.linkedFileIds.length > 0" class="file-tags">
                    <span v-for="id in editForm.linkedFileIds" :key="id" class="file-tag">
                      {{ projectFiles.find(f => f.id === id)?.name || id }}
                      <button type="button" class="file-tag-remove" @click="removeLinkedFile(id)">✕</button>
                    </span>
                  </div>
                  <div class="file-search-wrap">
                    <input
                      v-model="fileSearch"
                      type="text"
                      class="file-search-input"
                      placeholder="Buscar arquivo..."
                      autocomplete="off"
                      @focus="fileSearchOpen = true"
                      @blur="closeFileSearch"
                    />
                    <div v-if="fileSearchOpen && fileSearchResults.length > 0" class="file-search-dropdown">
                      <div
                        v-for="f in fileSearchResults"
                        :key="f.id"
                        class="file-search-item"
                        @mousedown.prevent="addLinkedFile(f.id)"
                      >{{ f.name }}</div>
                    </div>
                    <div v-if="fileSearchOpen && fileSearchResults.length === 0 && fileSearch" class="file-search-dropdown">
                      <div class="file-search-empty">Nenhum arquivo encontrado</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="input-field full-width">
                <label>Equipes</label>
                <div class="team-combo">
                  <div v-if="editForm.teamIds.length > 0" class="team-tags">
                    <span v-for="id in editForm.teamIds" :key="id" class="team-tag">
                      {{ teams.find(t => t.id === id)?.name || id }}
                      <button type="button" class="team-tag-remove" @click="removeTeam(id)">✕</button>
                    </span>
                  </div>
                  <div class="team-search-wrap">
                    <input
                      v-model="teamSearch"
                      type="text"
                      class="team-search-input"
                      placeholder="Buscar equipe..."
                      autocomplete="off"
                      @focus="teamSearchOpen = true"
                      @blur="closeTeamSearch"
                    />
                    <div v-if="teamSearchOpen && teamSearchResults.length > 0" class="team-search-dropdown">
                      <div
                        v-for="t in teamSearchResults"
                        :key="t.id"
                        class="team-search-item"
                        @mousedown.prevent="addTeam(t.id)"
                      >{{ t.name }}</div>
                    </div>
                    <div v-if="teamSearchOpen && teamSearchResults.length === 0 && teamSearch" class="team-search-dropdown">
                      <div class="team-search-empty">Nenhuma equipe encontrada</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="input-field full-width">
                <label>Maquinário pesado necessário</label>
                <div v-if="editForm.requiredMachinery.length > 0" class="machinery-tags">
                  <span v-for="item in editForm.requiredMachinery" :key="item.machineryId" class="machinery-tag">
                    {{ heavyMachinery.find(m => m.id === item.machineryId)?.title || item.machineryId }}
                    <button type="button" class="machinery-tag-remove" @click="removeMachinery(item.machineryId)">✕</button>
                  </span>
                </div>
                <div class="machinery-search-wrap">
                  <input
                    v-model="machinerySearch"
                    type="text"
                    class="machinery-search-input"
                    placeholder="Buscar maquinário..."
                    autocomplete="off"
                    @focus="machinerySearchOpen = true"
                    @blur="closeMachinerySearch"
                  />
                  <div v-if="machinerySearchOpen && machinerySearchResults.length > 0" class="machinery-search-dropdown">
                    <div
                      v-for="item in machinerySearchResults"
                      :key="item.id"
                      class="machinery-search-item"
                      @mousedown.prevent="addMachinery(item.id, 1)"
                    >
                      <span>{{ item.title }}</span>
                      <span class="machinery-category">({{ item.categoryName }})</span>
                    </div>
                  </div>
                  <div v-if="machinerySearchOpen && machinerySearchResults.length === 0 && machinerySearch" class="machinery-search-dropdown">
                    <div class="machinery-search-empty">Nenhum maquinário encontrado</div>
                  </div>
                </div>
              </div>
            </div>
            <p v-if="editError" class="edit-error">{{ editError }}</p>
            <div class="panel-actions">
              <button class="btn btn-primary" type="button" @click="submitEdit">Salvar</button>
              <button class="btn btn-ghost" type="button" @click="panelMode = 'view'">Cancelar</button>
            </div>
          </template>

          <!-- Create task form -->
          <template v-else-if="panelMode === 'create'">
            <h4 class="panel-section-title">Nova tarefa</h4>
            <div class="form-grid">
              <div class="input-field">
                <label>Titulo</label>
                <input v-model="editForm.title" type="text" placeholder="Descrição da tarefa" />
              </div>
              <div class="input-field">
                <label>Descricao</label>
                <input v-model="editForm.description" type="text" placeholder="Observação (opcional)" />
              </div>
              <div class="input-field">
                <label>Responsavel</label>
                <select v-model="editForm.assigneeUsername">
                  <option value="">Selecione responsavel</option>
                  <option v-for="u in assignableUsers" :key="u.username" :value="u.username">{{ u.name }}</option>
                </select>
              </div>
              <div class="input-field">
                <label>Duracao (dias)</label>
                <input v-model.number="editForm.durationDays" type="number" min="1" />
              </div>
              <div class="input-field">
                <label>Cor</label>
                <div class="color-picker-wrap">
                  <input v-model="editForm.color" type="color" class="color-input" @change="useColor(editForm.color)" />
                  <div v-if="recentColors.length" class="recent-colors">
                    <span class="recent-colors-label">Recentes</span>
                    <button v-for="c in recentColors" :key="c" type="button" class="recent-color-swatch" :style="{ background: c }" :title="c" @click="editForm.color = c" />
                  </div>
                </div>
              </div>
              <div class="input-field">
                <label>Data de inicio forcada</label>
                <input v-model="editForm.forcedStartDate" type="date" :disabled="editForm.noDate" :class="{ 'input-disabled': editForm.noDate }" />
                <label class="nodate-check">
                  <input type="checkbox" v-model="editForm.noDate" />
                  <span>Sem data definida</span>
                </label>
                <p v-if="editForm.noDate" class="nodate-hint">A atividade ficará na lista "Sem data definida", fora da linha do tempo.</p>
              </div>
              <div class="input-field">
                <label>Dias de trabalho</label>
                <div class="workdays-row">
                  <label v-for="wd in weekdays" :key="wd.value" class="wd-check">
                    <input type="checkbox" :value="wd.value" v-model="editForm.workDays" />
                    {{ wd.label }}
                  </label>
                </div>
              </div>
              <div class="input-field" v-if="tasks.length > 0">
                <label>Pendências</label>
                <div class="dep-combo">
                  <div v-if="editForm.dependencyTaskIds.length > 0" class="dep-tags">
                    <span v-for="id in editForm.dependencyTaskIds" :key="id" class="dep-tag">
                      {{ tasks.find(t => t.id === id)?.title || id }}
                      <button type="button" class="dep-tag-remove" @click="removeDep(id)">✕</button>
                    </span>
                  </div>
                  <div class="dep-search-wrap">
                    <input
                      v-model="depSearch"
                      type="text"
                      class="dep-search-input"
                      placeholder="Buscar tarefa..."
                      autocomplete="off"
                      @focus="depSearchOpen = true"
                      @blur="closeDepSearch"
                    />
                    <div v-if="depSearchOpen && depSearchResults.length > 0" class="dep-search-dropdown">
                      <div
                        v-for="t in depSearchResults"
                        :key="t.id"
                        class="dep-search-item"
                        @mousedown.prevent="addDep(t.id)"
                      >{{ t.title }}</div>
                    </div>
                    <div v-if="depSearchOpen && depSearchResults.length === 0 && depSearch" class="dep-search-dropdown">
                      <div class="dep-search-empty">Nenhuma tarefa encontrada</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="input-field full-width">
                <label>Ferramentas necessárias</label>
                <div class="tool-combo">
                  <div v-if="editForm.requiredTools.length > 0" class="tool-tags">
                    <span v-for="tool in editForm.requiredTools" :key="tool.itemId" class="tool-tag">
                      {{ warehouseItems.find(t => t.id === tool.itemId)?.name || tool.itemId }} × {{ tool.quantity }}
                      <button type="button" class="tool-tag-remove" @click="removeTool(tool.itemId)">✕</button>
                    </span>
                  </div>
                  <div class="tool-search-wrap">
                    <input
                      v-model="toolSearch"
                      type="text"
                      class="tool-search-input"
                      placeholder="Buscar ferramenta..."
                      autocomplete="off"
                      @focus="toolSearchOpen = true"
                      @blur="closeToolSearch"
                    />
                    <div v-if="toolSearchOpen && toolSearchResults.length > 0" class="tool-search-dropdown">
                      <div
                        v-for="item in toolSearchResults"
                        :key="item.id"
                        class="tool-search-item"
                        @mousedown.prevent="selectedToolForQuantity = item.id"
                      >
                        <span>{{ item.name }}</span>
                        <span class="tool-operational">({{ item.operationalUnits }} disponível)</span>
                      </div>
                    </div>
                    <div v-if="toolSearchOpen && toolSearchResults.length === 0 && toolSearch" class="tool-search-dropdown">
                      <div class="tool-search-empty">Nenhuma ferramenta encontrada</div>
                    </div>
                  </div>
                  <div v-if="selectedToolForQuantity" class="tool-quantity-input-wrap">
                    <input
                      v-model.number="toolQuantityInput"
                      type="number"
                      min="1"
                      class="tool-quantity-input"
                      placeholder="Qtd"
                      @keyup.enter="addTool(selectedToolForQuantity, toolQuantityInput)"
                    />
                    <button
                      type="button"
                      class="tool-add-btn"
                      @click="addTool(selectedToolForQuantity, toolQuantityInput)"
                    >Adicionar</button>
                  </div>
                </div>
              </div>
              <div class="input-field full-width">
                <label>Infraestrutura necessária</label>
                <textarea v-model="editForm.infraestruturaNecessaria" placeholder="Descreva a infraestrutura necessária para esta tarefa..." rows="3"></textarea>
              </div>
              <div class="input-field full-width">
                <label>Arquivos linkados</label>
                <div class="file-combo">
                  <div v-if="editForm.linkedFileIds.length > 0" class="file-tags">
                    <span v-for="id in editForm.linkedFileIds" :key="id" class="file-tag">
                      {{ projectFiles.find(f => f.id === id)?.name || id }}
                      <button type="button" class="file-tag-remove" @click="removeLinkedFile(id)">✕</button>
                    </span>
                  </div>
                  <div class="file-search-wrap">
                    <input
                      v-model="fileSearch"
                      type="text"
                      class="file-search-input"
                      placeholder="Buscar arquivo..."
                      autocomplete="off"
                      @focus="fileSearchOpen = true"
                      @blur="closeFileSearch"
                    />
                    <div v-if="fileSearchOpen && fileSearchResults.length > 0" class="file-search-dropdown">
                      <div
                        v-for="f in fileSearchResults"
                        :key="f.id"
                        class="file-search-item"
                        @mousedown.prevent="addLinkedFile(f.id)"
                      >{{ f.name }}</div>
                    </div>
                    <div v-if="fileSearchOpen && fileSearchResults.length === 0 && fileSearch" class="file-search-dropdown">
                      <div class="file-search-empty">Nenhum arquivo encontrado</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="input-field full-width">
                <label>Equipes</label>
                <div class="team-combo">
                  <div v-if="editForm.teamIds.length > 0" class="team-tags">
                    <span v-for="id in editForm.teamIds" :key="id" class="team-tag">
                      {{ teams.find(t => t.id === id)?.name || id }}
                      <button type="button" class="team-tag-remove" @click="removeTeam(id)">✕</button>
                    </span>
                  </div>
                  <div class="team-search-wrap">
                    <input
                      v-model="teamSearch"
                      type="text"
                      class="team-search-input"
                      placeholder="Buscar equipe..."
                      autocomplete="off"
                      @focus="teamSearchOpen = true"
                      @blur="closeTeamSearch"
                    />
                    <div v-if="teamSearchOpen && teamSearchResults.length > 0" class="team-search-dropdown">
                      <div
                        v-for="t in teamSearchResults"
                        :key="t.id"
                        class="team-search-item"
                        @mousedown.prevent="addTeam(t.id)"
                      >{{ t.name }}</div>
                    </div>
                    <div v-if="teamSearchOpen && teamSearchResults.length === 0 && teamSearch" class="team-search-dropdown">
                      <div class="team-search-empty">Nenhuma equipe encontrada</div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="input-field full-width">
                <label>Maquinário pesado necessário</label>
                <div v-if="editForm.requiredMachinery.length > 0" class="machinery-tags">
                  <span v-for="item in editForm.requiredMachinery" :key="item.machineryId" class="machinery-tag">
                    {{ heavyMachinery.find(m => m.id === item.machineryId)?.title || item.machineryId }}
                    <button type="button" class="machinery-tag-remove" @click="removeMachinery(item.machineryId)">✕</button>
                  </span>
                </div>
                <div class="machinery-search-wrap">
                  <input
                    v-model="machinerySearch"
                    type="text"
                    class="machinery-search-input"
                    placeholder="Buscar maquinário..."
                    autocomplete="off"
                    @focus="machinerySearchOpen = true"
                    @blur="closeMachinerySearch"
                  />
                  <div v-if="machinerySearchOpen && machinerySearchResults.length > 0" class="machinery-search-dropdown">
                    <div
                      v-for="item in machinerySearchResults"
                      :key="item.id"
                      class="machinery-search-item"
                      @mousedown.prevent="addMachinery(item.id, 1)"
                    >
                      <span>{{ item.title }}</span>
                      <span class="machinery-category">({{ item.categoryName }})</span>
                    </div>
                  </div>
                  <div v-if="machinerySearchOpen && machinerySearchResults.length === 0 && machinerySearch" class="machinery-search-dropdown">
                    <div class="machinery-search-empty">Nenhum maquinário encontrado</div>
                  </div>
                </div>
              </div>
            </div>
            <p v-if="editError" class="edit-error">{{ editError }}</p>
            <div class="panel-actions">
              <button class="btn btn-primary" type="button" @click="submitCreate">Criar tarefa</button>
              <button class="btn btn-ghost" type="button" @click="closePanel">Cancelar</button>
            </div>
          </template>
        </div>
      </div>

    </div> <!-- /planning-row -->

    <div v-if="showImportModal" class="import-overlay" @click.self="closeImportModal">
      <div class="import-modal">
        <div class="import-modal-head">
          <div>
            <h3>Importar tarefas do Excel</h3>
            <p class="import-subtitle">Envie uma planilha `.xlsx` e revise os itens antes de criar as tarefas no planejamento.</p>
          </div>
          <button class="panel-close" type="button" @click="closeImportModal">✕</button>
        </div>

        <div class="import-upload-card">
          <input ref="importFileInput" class="hidden-file-input" type="file" accept=".xlsx,.xls" @change="handleImportFile" />
          <div class="import-upload-actions">
            <button class="btn btn-primary" type="button" @click="triggerImportFile">Enviar arquivo Excel</button>
            <button class="btn btn-ghost" type="button" @click="downloadImportTemplate">Baixar template</button>
          </div>
          <p class="import-helper">Colunas: A Titulo, B Descricao, C Duracao em Dias, D Data de inicio, E Cor.</p>
          <button class="import-template-link" type="button" @click="downloadImportTemplate">Baixar template de importacao</button>
        </div>

        <p v-if="importError" class="import-error">{{ importError }}</p>

        <div v-if="importDrafts.length > 0" class="import-review">
          <div class="import-review-head">
            <div><strong>{{ importDrafts.length }}</strong> tarefa(s) pronta(s) para revisar</div>
            <button class="btn btn-ghost" type="button" @click="advancedImportMode = !advancedImportMode">
              {{ advancedImportMode ? 'Ocultar informações adicionais' : 'Modificar informações adicionais' }}
            </button>
          </div>

          <div class="import-table-wrap">
            <table class="import-table">
              <thead>
                <tr>
                  <th>Titulo</th>
                  <th>Descricao</th>
                  <th>Dias</th>
                  <th>Inicio</th>
                  <th>Cor</th>
                  <th>Responsavel</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <template v-for="draft in importDrafts" :key="draft.id">
                  <tr>
                    <td>
                      <input v-if="advancedImportMode" v-model="draft.title" type="text" class="import-cell-input" />
                      <template v-else>{{ draft.title }}</template>
                    </td>
                    <td>
                      <textarea v-if="advancedImportMode" v-model="draft.description" class="import-cell-textarea"></textarea>
                      <template v-else>{{ draft.description || '-' }}</template>
                    </td>
                    <td>
                      <input v-if="advancedImportMode" v-model.number="draft.durationDays" type="number" min="1" class="import-cell-input import-cell-input--sm" />
                      <template v-else>{{ draft.durationDays }}</template>
                    </td>
                    <td>
                      <input v-if="advancedImportMode" v-model="draft.forcedStartDate" type="date" class="import-cell-input" />
                      <template v-else>{{ formatDatePt(draft.forcedStartDate) }}</template>
                    </td>
                    <td>
                      <input v-if="advancedImportMode" v-model="draft.color" type="color" class="color-input" />
                      <span v-else class="import-color-chip"><i :style="{ background: draft.color }"></i>{{ draft.color }}</span>
                    </td>
                    <td>
                      <select v-model="draft.assigneeUsername" class="import-cell-input">
                        <option value="">Selecione</option>
                        <option v-for="user in assignableUsers" :key="user.username" :value="user.username">{{ user.name }}</option>
                      </select>
                    </td>
                    <td class="import-actions-col">
                      <button class="link link-inline" type="button" @click="removeImportedTask(draft.id)">Remover</button>
                    </td>
                  </tr>
                  <tr v-if="advancedImportMode" class="import-advanced-row">
                    <td colspan="7">
                      <div class="import-advanced-grid">
                        <div class="input-field">
                          <label>Dias de trabalho</label>
                          <div class="workdays-row">
                            <label v-for="wd in weekdays" :key="`${draft.id}-${wd.value}`" class="wd-check">
                              <input type="checkbox" :checked="draft.workDays.includes(wd.value)" @change="toggleImportedWorkday(draft.id, wd.value)" />
                              {{ wd.label }}
                            </label>
                          </div>
                        </div>
                        <div class="input-field">
                          <label>Pendencia</label>
                          <select v-model="draft.dependencyTaskIds" multiple class="import-multi-select">
                            <option v-for="task in dependencyOptionsForImport(draft.id)" :key="task.id" :value="task.id">{{ task.title }}</option>
                          </select>
                        </div>
                        <div class="input-field">
                          <label>Infraestrutura necessaria</label>
                          <textarea v-model="draft.infraestruturaNecessaria" class="import-cell-textarea"></textarea>
                        </div>
                        <div class="input-field">
                          <label>Arquivos linkados</label>
                          <select v-model="draft.linkedFileIds" multiple class="import-multi-select">
                            <option v-for="file in projectFiles" :key="file.id" :value="file.id">{{ file.name }}</option>
                          </select>
                        </div>
                        <div class="input-field">
                          <label>Equipes</label>
                          <select v-model="draft.teamIds" multiple class="import-multi-select">
                            <option v-for="team in teams" :key="team.id" :value="team.id">{{ team.name }}</option>
                          </select>
                        </div>
                        <div class="input-field">
                          <label>Ferramentas necessarias</label>
                          <div class="import-inline-row">
                            <select v-model="draft.toolToAdd">
                              <option value="">Selecione</option>
                              <option v-for="item in availableToolsForImport(draft)" :key="item.id" :value="item.id">{{ item.name }}</option>
                            </select>
                            <input v-model.number="draft.toolQuantity" type="number" min="1" class="import-qty-input" />
                            <button class="btn btn-ghost" type="button" @click="addImportedTool(draft.id)">Adicionar</button>
                          </div>
                          <div v-if="draft.requiredTools.length > 0" class="import-tag-list">
                            <div v-for="tool in draft.requiredTools" :key="`${draft.id}-${tool.itemId}`" class="import-tag-item">
                              <span>{{ toolName(tool.itemId) }}</span>
                              <input v-model.number="tool.quantity" type="number" min="1" class="import-qty-input" />
                              <button class="link link-inline" type="button" @click="removeImportedTool(draft.id, tool.itemId)">Remover</button>
                            </div>
                          </div>
                        </div>
                        <div class="input-field">
                          <label>Maquinario pesado necessario</label>
                          <div class="import-inline-row">
                            <select v-model="draft.machineryToAdd">
                              <option value="">Selecione</option>
                              <option v-for="item in availableMachineryForImport(draft)" :key="item.id" :value="item.id">{{ item.title }}</option>
                            </select>
                            <input v-model.number="draft.machineryQuantity" type="number" min="1" class="import-qty-input" />
                            <button class="btn btn-ghost" type="button" @click="addImportedMachinery(draft.id)">Adicionar</button>
                          </div>
                          <div v-if="draft.requiredMachinery.length > 0" class="import-tag-list">
                            <div v-for="machine in draft.requiredMachinery" :key="`${draft.id}-${machine.machineryId}`" class="import-tag-item">
                              <span>{{ machineryLabel(machine.machineryId) }}</span>
                              <input v-model.number="machine.quantity" type="number" min="1" class="import-qty-input" />
                              <button class="link link-inline" type="button" @click="removeImportedMachinery(draft.id, machine.machineryId)">Remover</button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>

          <div class="modal-footer">
            <button class="btn btn-primary" type="button" :disabled="isImporting" @click="confirmImportTasks">
              {{ isImporting ? 'Importando...' : 'Importar para o planejamento' }}
            </button>
            <button class="btn btn-ghost" type="button" @click="closeImportModal">Cancelar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Blocked days modal -->
    <div v-if="showBlockedDaysModal" class="modal-overlay" @click.self="showBlockedDaysModal = false">
      <div class="modal-card">
        <div class="modal-header">
          <h3>Dias não trabalhados</h3>
          <button class="panel-close" type="button" @click="showBlockedDaysModal = false">✕</button>
        </div>
        <p class="hint">Dias sem trabalho (feriados, paradas programadas). Aparecem como faixas cinzas no cronograma.</p>
        <div class="blocked-add-row">
          <input v-model="newBlockedDate" type="date" class="filter-input" />
          <button class="btn btn-primary" type="button" @click="addBlockedDate">Adicionar</button>
        </div>
        <div class="blocked-list">
          <div v-if="localBlockedDates.length === 0" class="hint">Nenhum dia bloqueado.</div>
          <div v-for="d in localBlockedDates" :key="d" class="blocked-item">
            <span>{{ formatDate(d) }}</span>
            <button class="remove-btn" type="button" @click="removeBlockedDate(d)">✕</button>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-primary" type="button" @click="saveBlockedDatesLocal">Salvar</button>
          <button class="btn btn-ghost" type="button" @click="showBlockedDaysModal = false">Cancelar</button>
        </div>
      </div>
    </div>
  </div>

  <!-- Photo lightbox -->
  <Teleport to="body">
    <div v-if="lightboxSrc" class="lightbox-overlay" @click="lightboxSrc = ''">
      <img :src="lightboxSrc" class="lightbox-img" @click.stop />
      <button class="lightbox-close" @click="lightboxSrc = ''">✕</button>
    </div>
  </Teleport>

  <!-- ── GANTT REPORT MODAL ── -->
  <Teleport to="body">
    <div v-if="showGanttReport" class="report-backdrop" @click.self="showGanttReport = false">
      <div class="report-modal">
        <div class="report-modal-header">
          <span class="report-modal-title">
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" style="flex-shrink:0"><rect x="2" y="1.5" width="11" height="12" rx="1.5" stroke="currentColor" stroke-width="1.4"/><path d="M4.5 5h6M4.5 7.5h6M4.5 10h4" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
            Relatório
          </span>
          <div class="report-modal-controls">
            <div v-if="!reportOnlyNoDate" class="report-week-nav">
              <button class="report-week-btn" type="button" @click="reportPrevWeek">‹</button>
              <span class="report-week-label">{{ reportWeekLabel }}</span>
              <button class="report-week-btn" type="button" @click="reportNextWeek" :disabled="!reportHasCustomRange && reportWeekOffset >= 0">›</button>
            </div>
            <span v-else class="report-week-label report-week-label--nodate">{{ reportWeekLabel }}</span>
            <button class="report-modal-close" type="button" @click="showGanttReport = false">✕</button>
          </div>
        </div>

        <!-- Interval + mode controls -->
        <div class="report-range-row">
          <template v-if="!reportOnlyNoDate">
            <label class="report-range-label">De</label>
            <input v-model="reportRange.from" type="date" class="report-range-input" />
            <label class="report-range-label">Até</label>
            <input v-model="reportRange.to" type="date" class="report-range-input" />
            <button
              class="report-range-btn"
              type="button"
              :disabled="!reportRange.from || !reportRange.to"
              @click="applyReportRange"
            >Aplicar</button>
            <button
              v-if="reportHasCustomRange"
              class="report-range-btn report-range-btn--ghost"
              type="button"
              @click="clearReportRange"
            >Semana</button>
          </template>
          <span v-else class="report-range-hint">Somente atividades sem data definida</span>
          <button
            class="report-range-btn report-range-btn--nodate"
            :class="{ active: reportOnlyNoDate }"
            type="button"
            @click="toggleReportNoDate"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><path d="M16 2v4M8 2v4M3 10h18M9 15.5h6"/></svg>
            {{ reportOnlyNoDate ? 'Voltar ao período' : 'Somente sem data' }}
          </button>
        </div>

        <div v-if="reportLoading" class="report-loading">
          <span class="report-spinner"></span>
          Gerando relatório…
        </div>
        <div v-else-if="reportError" class="report-error">{{ reportError }}</div>
        <div v-else-if="reportData" class="report-body">
          <!-- Summary chips -->
          <div class="report-summary-row">
            <span class="report-chip report-chip--total">{{ reportData._preview.summary.total }} tarefas ativas</span>
            <span class="report-chip report-chip--done">{{ reportData._preview.summary.completed }} concluídas</span>
            <span class="report-chip report-chip--late" v-if="reportData._preview.summary.overdue > 0">{{ reportData._preview.summary.overdue }} atrasadas</span>
            <span class="report-chip report-chip--pending" v-if="reportData._preview.summary.notStarted > 0">{{ reportData._preview.summary.notStarted }} não iniciadas</span>
            <span class="report-chip report-chip--nodate" v-if="(reportData._preview.summary.noDate || 0) > 0">{{ reportData._preview.summary.noDate }} sem data</span>
          </div>

          <!-- Tasks in period -->
          <h4 class="report-section-title">{{ reportOnlyNoDate ? 'Atividades sem data' : (reportHasCustomRange ? 'No período' : 'Esta semana') }}</h4>
          <div v-if="reportData._preview.tasks.length === 0" class="report-empty">{{ reportOnlyNoDate ? 'Nenhuma atividade sem data.' : 'Nenhuma tarefa ativa neste período.' }}</div>
          <div v-for="task in reportData._preview.tasks" :key="task.id" class="report-task-card">
            <div class="report-task-header">
              <div class="report-task-left">
                <span class="report-task-name">{{ task.title }}</span>
                <span v-if="task.groupName" class="report-task-group">{{ task.groupName }}</span>
              </div>
              <div class="report-task-right">
                <span class="report-task-pct" :class="taskPctClass(task.progressPercent, task.noDate ? undefined : task.plannedEnd)">
                  {{ task.progressPercent }}%
                </span>
                <span v-if="task.noDate" class="report-task-dates report-task-dates--nodate">Sem data</span>
                <span v-else class="report-task-dates">{{ fmtDateShort(task.plannedStart) }} → {{ fmtDateShort(task.plannedEnd) }}</span>
              </div>
            </div>
            <div class="report-progress-bar">
              <div class="report-progress-fill" :style="{ width: task.progressPercent + '%' }" :class="taskPctClass(task.progressPercent, task.noDate ? undefined : task.plannedEnd)"></div>
            </div>
            <div v-if="task.progressUpdates.length > 0" class="report-updates">
              <div v-for="upd in [...task.progressUpdates].reverse()" :key="upd.id" class="report-update-row">
                <span class="report-update-meta">{{ fmtDateShort(upd.progressDate) }} · {{ upd.progressPercent }}%</span>
                <span v-if="upd.notes" class="report-update-note">{{ upd.notes }}</span>
                <img
                  v-if="upd.photoPath"
                  :src="resolvePhotoPath(upd.photoPath || '')"
                  class="report-update-photo"
                  @click="lightboxSrc = resolvePhotoPath(upd.photoPath || '')"
                />
              </div>
            </div>
          </div>

          <!-- Upcoming tasks -->
          <template v-if="reportData._preview.upcoming.length > 0">
            <h4 class="report-section-title">Próximas a finalizar</h4>
            <div v-for="task in reportData._preview.upcoming" :key="task.id" class="report-upcoming-row">
              <span class="report-upcoming-name">{{ task.title }}</span>
              <span class="report-upcoming-date">Previsto: {{ fmtDateShort(task.plannedEnd) }}</span>
            </div>
          </template>

          <!-- Print preview action -->
          <div class="report-print-row">
            <button
              class="report-print-btn"
              type="button"
              :disabled="printSaving"
              @click="openPrintPreview"
            >
              <svg v-if="!printSaving" width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><path d="M2 5h10v6H2zM4 5V2h6v3M4 11v1h6v-1"/><circle cx="10" cy="8" r=".8" fill="currentColor" stroke="none"/></svg>
              <span v-if="printSaving" class="report-spinner" style="width:13px;height:13px;border-width:1.5px;margin-right:6px;"></span>
              {{ printSaving ? 'Preparando…' : 'Visualizar Impressão / PDF' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- ── GANTT PRINT PREVIEW ── -->
  <Teleport to="body">
    <div v-if="showPrintPreview && printSubmission" class="gantt-print-overlay">
      <SubmissionPrintPreview
        :submission="printSubmission"
        @close="showPrintPreview = false"
      />
    </div>
  </Teleport>

  <!-- ── SHARE MODAL ── -->
  <Teleport to="body">
    <div v-if="showShareModal" class="share-backdrop" @click.self="closeShareModal">
      <div class="share-modal">
        <div class="share-modal-header">
          <span class="share-modal-title">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style="flex-shrink:0"><circle cx="13" cy="3" r="2.2" stroke="currentColor" stroke-width="1.4"/><circle cx="13" cy="13" r="2.2" stroke="currentColor" stroke-width="1.4"/><circle cx="3" cy="8" r="2.2" stroke="currentColor" stroke-width="1.4"/><path d="M5.1 7l5.7-2.8M5.1 9l5.7 2.8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>
            Compartilhar diagrama
          </span>
          <button class="share-modal-close" type="button" @click="closeShareModal">✕</button>
        </div>

        <!-- Tab: Exportar -->
        <div class="share-section">
          <p class="share-section-label">Exportar</p>
          <div class="share-action-row">
            <button class="share-action-btn" type="button" @click="exportGanttExcel(); closeShareModal()">
              <span class="share-action-icon share-action-icon--excel">
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><rect width="16" height="16" rx="3" fill="#107C41"/><path d="M3 4h4v8H3zm5 0h5v8H8" fill="white" opacity=".15"/><path d="M4 6.5l1.5 3L7 6.5M9.5 6.5v3M9.5 9h2" stroke="white" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </span>
              <div class="share-action-text">
                <span class="share-action-label">Exportar Excel</span>
                <span class="share-action-sub">Gantt editável (.xlsx)</span>
              </div>
            </button>
            <button class="share-action-btn" type="button" @click="exportGantt(); closeShareModal()">
              <span class="share-action-icon share-action-icon--pdf">
                <svg width="18" height="18" viewBox="0 0 16 16" fill="none"><rect width="16" height="16" rx="3" fill="#C00000"/><path d="M4 3h5l3 3v7H4V3z" fill="white" opacity=".15"/><path d="M9 3v3h3M5.5 9h5M5.5 11h3" stroke="white" stroke-width="1.2" stroke-linecap="round"/></svg>
              </span>
              <div class="share-action-text">
                <span class="share-action-label">Exportar PDF</span>
                <span class="share-action-sub">Visualização para impressão</span>
              </div>
            </button>
          </div>
        </div>

        <div class="share-divider"></div>

        <!-- Tab: Visualização externa -->
        <div class="share-section">
          <p class="share-section-label">Compartilhar visualização</p>
          <div class="share-action-row">
            <button class="share-action-btn" type="button" @click="startShareLink('link')">
              <span class="share-action-icon share-action-icon--link">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M7.5 10.5a4.5 4.5 0 0 0 6.36.13l1.5-1.5a4.5 4.5 0 0 0-6.36-6.36L7.87 3.9" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/><path d="M10.5 7.5a4.5 4.5 0 0 0-6.36-.13l-1.5 1.5a4.5 4.5 0 0 0 6.36 6.36l1.12-1.12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
              </span>
              <div class="share-action-text">
                <span class="share-action-label">Gerar link</span>
                <span class="share-action-sub">Link de acesso temporário, sem login</span>
              </div>
            </button>
            <button class="share-action-btn" type="button" @click="startShareLink('email')">
              <span class="share-action-icon share-action-icon--email">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><rect x="2" y="4" width="14" height="10" rx="2" stroke="currentColor" stroke-width="1.5"/><path d="M2 6l7 5 7-5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
              </span>
              <div class="share-action-text">
                <span class="share-action-label">Enviar por e-mail</span>
                <span class="share-action-sub">Envia o link para um endereço</span>
              </div>
            </button>
          </div>
        </div>

        <!-- Manage access button (visible after at least one share) -->
        <div v-if="shareLinks.length > 0" class="share-section share-section--manage">
          <button class="share-manage-btn" type="button" @click="showManageModal = true; showShareModal = false">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5.5" stroke="currentColor" stroke-width="1.3"/><path d="M7 4v3l2 1" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg>
            Gerenciar acessos temporários ({{ shareLinks.filter(l => !l.revokedAt).length }} ativo{{ shareLinks.filter(l => !l.revokedAt).length !== 1 ? 's' : '' }})
          </button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- ── DURATION PICKER MODAL ── -->
  <Teleport to="body">
    <div v-if="showDurationModal" class="share-backdrop" @click.self="showDurationModal = false">
      <div class="share-modal share-modal--sm">
        <div class="share-modal-header">
          <span class="share-modal-title">{{ durationTarget === 'email' ? 'Enviar por e-mail' : 'Gerar link de acesso' }}</span>
          <button class="share-modal-close" type="button" @click="showDurationModal = false">✕</button>
        </div>
        <div class="share-section">
          <div v-if="durationTarget === 'email'" class="input-field" style="margin-bottom:14px">
            <label>E-mail do destinatário</label>
            <input v-model="shareEmail" type="email" placeholder="exemplo@email.com" class="share-input" />
          </div>
          <p class="share-section-label">Validade do acesso</p>
          <div class="duration-presets">
            <button
              v-for="p in durationPresets"
              :key="p.value"
              class="duration-btn"
              :class="{ active: selectedDuration === p.value }"
              type="button"
              @click="selectedDuration = p.value"
            >{{ p.label }}</button>
          </div>
        </div>
        <div class="share-modal-footer">
          <button class="btn btn-primary" type="button" :disabled="shareLoading || (durationTarget === 'email' && !shareEmail.trim())" @click="confirmShare">
            {{ shareLoading ? 'Gerando…' : (durationTarget === 'email' ? 'Enviar' : 'Gerar link') }}
          </button>
          <button class="btn btn-ghost" type="button" @click="showDurationModal = false">Cancelar</button>
        </div>
        <p v-if="shareError" class="share-error">{{ shareError }}</p>
      </div>
    </div>
  </Teleport>

  <!-- ── GENERATED LINK MODAL ── -->
  <Teleport to="body">
    <div v-if="generatedLink" class="share-backdrop" @click.self="generatedLink = ''">
      <div class="share-modal share-modal--sm">
        <div class="share-modal-header">
          <span class="share-modal-title">Link gerado com sucesso</span>
          <button class="share-modal-close" type="button" @click="generatedLink = ''">✕</button>
        </div>
        <div class="share-section">
          <p class="share-section-label">Copie e compartilhe este link</p>
          <div class="share-link-row">
            <input :value="generatedLink" readonly class="share-link-input" @click="($event.target as HTMLInputElement).select()" />
            <button class="share-copy-btn" type="button" @click="copyLink(generatedLink)">
              {{ linkCopied ? '✓ Copiado' : 'Copiar' }}
            </button>
          </div>
          <p v-if="generatedExpiry" class="share-expiry-note">
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><circle cx="6" cy="6" r="5" stroke="currentColor" stroke-width="1.2"/><path d="M6 3.5v2.5l1.5 1" stroke="currentColor" stroke-width="1.2" stroke-linecap="round"/></svg>
            Válido até {{ generatedExpiry }}
          </p>
        </div>
        <div class="share-modal-footer">
          <button class="btn btn-ghost" type="button" @click="generatedLink = ''">Fechar</button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- ── MANAGE ACCESS MODAL ── -->
  <Teleport to="body">
    <div v-if="showManageModal" class="share-backdrop" @click.self="showManageModal = false">
      <div class="share-modal share-modal--lg">
        <div class="share-modal-header">
          <span class="share-modal-title">Acessos temporários</span>
          <button class="share-modal-close" type="button" @click="showManageModal = false">✕</button>
        </div>
        <div class="manage-list">
          <div v-if="shareLinks.length === 0" class="manage-empty">Nenhum acesso gerado ainda.</div>
          <div v-for="link in shareLinks" :key="link.id" class="manage-item" :class="{ revoked: !!link.revokedAt, expired: isExpired(link) && !link.revokedAt }">
            <div class="manage-item-info">
              <div class="manage-item-hash">
                <span v-if="link.email" class="manage-chip manage-chip--email">{{ link.email }}</span>
                <span v-else class="manage-chip">Link</span>
                <code class="manage-hash-code">…{{ link.id.slice(-6) }}</code>
              </div>
              <div class="manage-item-meta">
                <span>Criado {{ formatDatePt(link.createdAt.slice(0,10)) }}</span>
                <span v-if="link.revokedAt" class="manage-status manage-status--revoked">Revogado</span>
                <span v-else-if="isExpired(link)" class="manage-status manage-status--expired">Expirado</span>
                <span v-else-if="link.expiresAt" class="manage-status manage-status--active">Válido até {{ formatDatePt(link.expiresAt.slice(0,10)) }}</span>
                <span v-else class="manage-status manage-status--active">Sem expiração</span>
              </div>
            </div>
            <div class="manage-item-actions" v-if="!link.revokedAt">
              <button class="manage-btn" type="button" @click="copyLink(buildShareUrl(link.id))">
                {{ copiedHash === link.id ? '✓' : 'Copiar' }}
              </button>
              <button class="manage-btn manage-btn--extend" type="button" @click="startExtend(link)">Extender</button>
              <button class="manage-btn manage-btn--revoke" type="button" @click="revokeLink(link.id)">Encerrar</button>
            </div>
          </div>
        </div>
        <div class="share-modal-footer">
          <button class="btn btn-ghost" type="button" @click="showManageModal = false">Fechar</button>
        </div>
      </div>
    </div>
  </Teleport>

  <!-- ── EXTEND ACCESS MODAL ── -->
  <Teleport to="body">
    <div v-if="extendingLink" class="share-backdrop" @click.self="extendingLink = null">
      <div class="share-modal share-modal--sm">
        <div class="share-modal-header">
          <span class="share-modal-title">Extender acesso</span>
          <button class="share-modal-close" type="button" @click="extendingLink = null">✕</button>
        </div>
        <div class="share-section">
          <p class="share-section-label">Nova validade</p>
          <div class="duration-presets">
            <button
              v-for="p in durationPresets"
              :key="p.value"
              class="duration-btn"
              :class="{ active: extendDuration === p.value }"
              type="button"
              @click="extendDuration = p.value"
            >{{ p.label }}</button>
          </div>
        </div>
        <div class="share-modal-footer">
          <button class="btn btn-primary" type="button" @click="confirmExtend">Confirmar</button>
          <button class="btn btn-ghost" type="button" @click="extendingLink = null">Cancelar</button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { computed, defineComponent, nextTick, onBeforeUnmount, onMounted, ref, watch, watchEffect } from 'vue'
import { apiFetch, getApiBase } from '@/store/api'
import { sessionStore } from '@/store/session'
import { submissionsStore } from '@/store/submissions'
import type { Submission } from '@/store/submissions'
import { uiStore } from '@/store/ui'
import { newId } from '@/utils/id'
import * as XLSX from 'xlsx'
import SubmissionPrintPreview from '@/components/SubmissionPrintPreview.vue'
import SkeletonBlock from '@/components/skeleton/SkeletonBlock.vue'

interface GanttRow {
  id: string
  title: string
  description?: string
  assigneeUsername: string
  plannedStart: string
  plannedEnd: string
  color?: string
  groupId?: string
  workDays?: string[]
  dependencyTaskIds?: string[]
  progressUpdates?: Array<{ id?: string; progressDate: string; notes?: string; progressPercent?: number; photoPath?: string | null }>
  infraestruturaNecessaria?: string
  linkedFileIds?: string[]
  teamIds?: string[]
  requiredTools?: Array<{ itemId: string; quantity: number }>
  toolRequests?: Array<{ itemId: string; quantity: number; status: string }>
  requiredMachinery?: Array<{ machineryId: string; quantity: number }>
  noDate?: number | boolean
  createdBy?: string
  createdByName?: string
}

interface TaskGroup {
  id: string
  name: string
  responsibleUsername: string
  color: string
}

interface AssignableUser { username: string; name: string }
interface HeavyMachineryItem { id: string; title: string; categoryId: string; categoryName?: string }
interface ImportedTaskDraft {
  id: string
  title: string
  description: string
  durationDays: number
  forcedStartDate: string
  color: string
  assigneeUsername: string
  workDays: string[]
  dependencyTaskIds: string[]
  requiredTools: Array<{ itemId: string; quantity: number }>
  requiredMachinery: Array<{ machineryId: string; quantity: number }>
  infraestruturaNecessaria: string
  linkedFileIds: string[]
  teamIds: string[]
  toolToAdd: string
  toolQuantity: number
  machineryToAdd: string
  machineryQuantity: number
}

type ZoomLevel = 'day' | 'week' | 'month'
type PanelMode = 'view' | 'progress' | 'edit' | 'create'

const DAY_MS = 86400000

function parseDate(value: string): Date | null {
  const raw = `${value || ''}`.slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return null
  const d = new Date(`${raw}T00:00:00`)
  return isNaN(d.getTime()) ? null : d
}

function toDateStr(d: Date): string {
  return d.toISOString().slice(0, 10)
}

function addDays(d: Date, n: number): Date {
  return new Date(d.getTime() + n * DAY_MS)
}

function diffDays(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / DAY_MS)
}

// Count working days between two dates (inclusive), excluding weekends and blocked dates
function countWorkingDays(start: Date, end: Date, workDays: string[], blockedDates: string[]): number {
  const allowedDays = new Set(workDays.map(d => d.toLowerCase().slice(0, 3)))
  const blocked = new Set(blockedDates)
  const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']

  let count = 0
  const cursor = new Date(start.getTime())
  const endTime = new Date(end.getTime())

  while (cursor.getTime() <= endTime.getTime()) {
    const dateStr = toDateStr(cursor)
    const dayKey = WEEKDAY_KEYS[cursor.getDay()]

    if (allowedDays.has(dayKey) && !blocked.has(dateStr)) {
      count += 1
    }

    cursor.setDate(cursor.getDate() + 1)
  }

  return Math.max(1, count)
}

function formatDatePt(iso: string): string {
  const d = parseDate(iso)
  if (!d) return iso
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`
}

function normalizeImportedColor(value: unknown): string {
  const raw = `${value || ''}`.trim()
  if (!raw) return '#2f6fce'
  const withHash = raw.startsWith('#') ? raw : `#${raw}`
  return /^#[0-9A-Fa-f]{6}$/.test(withHash) ? withHash : '#2f6fce'
}

function normalizeImportedDate(value: unknown): string {
  if (value instanceof Date && !Number.isNaN(value.getTime())) return toDateStr(value)
  if (typeof value === 'number') {
    const parsed = XLSX.SSF.parse_date_code(value)
    if (parsed) {
      const d = new Date(parsed.y, parsed.m - 1, parsed.d)
      if (!Number.isNaN(d.getTime())) return toDateStr(d)
    }
  }
  const text = `${value || ''}`.trim()
  if (!text) return ''
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text
  const br = text.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (br) return `${br[3]}-${br[2]}-${br[1]}`
  return ''
}

export default defineComponent({
  name: 'ProjectGanttSection',
  components: { SubmissionPrintPreview, SkeletonBlock },
  props: {
    loading: { type: Boolean, default: false },
    rows: { type: Array as () => GanttRow[], default: () => [] },
    tasks: { type: Array as () => GanttRow[], default: () => [] },
    assigneeName: { type: Function, required: true },
    assignableUsers: { type: Array as () => AssignableUser[], default: () => [] },
    accessMode: { type: String as () => 'read' | 'edit', default: 'read' },
    blockedDates: { type: Array as () => string[], default: () => [] },
    projectId: { type: String, default: '' },
    onSaveTask: { type: Function, default: null },
    onImportTasks: { type: Function, default: null },
    onUpdateProgress: { type: Function, default: null },
    onSaveBlockedDates: { type: Function, default: null },

    onDeleteTask: { type: Function, default: null },
    onDeleteProgress: { type: Function, default: null },
    projectName: { type: String, default: '' },
    systemName: { type: String, default: '' },
    logoText: { type: String, default: 'ObraTycoon by GusMachado' },
    isAnalyst: { type: Boolean, default: false },
    openNewTask: { type: Boolean, default: false },
    groups: { type: Array as () => TaskGroup[], default: () => [] },
    onSaveGroup: { type: Function, default: null },
    onDeleteGroup: { type: Function, default: null }
  },
  setup(props) {
    // ── DOM REFS ──
    const scrollRef = ref<HTMLElement | null>(null)
    const labelsRef = ref<HTMLElement | null>(null)
    const filterRef = ref<HTMLElement | null>(null)
    const exportMenuRef = ref<HTMLElement | null>(null)
    const selectAllEl = ref<HTMLInputElement | null>(null)
    const showExportMenu = ref(false)

    // ── ZOOM ──
    const zoom = ref<ZoomLevel>('week')
    const zoomScale = ref(100) // 25–350 %
    const ZOOM_BASE_PX = 22   // px por dia a 100 %
    // Thresholds: <=66 → month, <=160 → week, >160 → day
    const ZOOM_PRESETS: Record<ZoomLevel, number> = { day: 200, week: 100, month: 45 }
    const dayWidthPx = computed(() => Math.max(2, Math.round(ZOOM_BASE_PX * zoomScale.value / 100)))

    const zoomLevelFromScale = (scale: number): ZoomLevel => {
      if (scale <= 66) return 'month'
      if (scale <= 160) return 'week'
      return 'day'
    }

    watch(zoomScale, (val) => {
      zoom.value = zoomLevelFromScale(val)
    })

    const setZoom = (z: ZoomLevel) => {
      const todayPx = todayLeftPx.value
      zoom.value = z
      zoomScale.value = ZOOM_PRESETS[z]
      nextTick(() => {
        if (todayPx !== null && scrollRef.value) {
          scrollRef.value.scrollLeft = Math.max(0, todayPx - scrollRef.value.clientWidth / 2)
        }
      })
    }

    // ── LAYOUT CONSTANTS ──
    const headerHeightPx = 52
    const rowHeightPx = 68
    const barHeightPx = 44
    const barTopPx = (rowHeightPx - barHeightPx) / 2

    // ── FILTERS ──
    const showFilters = ref(false)
    const filters = ref({ dateFrom: '', dateTo: '', assignee: '', groupId: '', search: '', status: '', hideCompleted: false, showOverdue: false })

    const assigneeOptions = computed(() => {
      const set = new Set<string>()
      props.rows.forEach((r) => set.add(String(props.assigneeName(r.assigneeUsername) || r.assigneeUsername)))
      return Array.from(set).sort()
    })

    // ── STATUS HELPERS ──
    type TaskStatus = 'idle' | 'progress' | 'done' | 'late'

    const isUndated = (row: GanttRow): boolean =>
      Number(row.noDate || 0) === 1 || !parseDate(row.plannedStart) || !parseDate(row.plannedEnd)

    const statusOf = (row: GanttRow): TaskStatus => {
      const pct = latestProgressPct(row)
      if (pct >= 100) return 'done'
      const end = row.plannedEnd?.slice(0, 10) || ''
      if (!isUndated(row) && end && end < todayStr) return 'late'
      if (pct > 0) return 'progress'
      return 'idle'
    }

    const STATUS_LABELS: Record<TaskStatus, string> = {
      idle: 'Não iniciada',
      progress: 'Em andamento',
      done: 'Concluída',
      late: 'Atrasada'
    }
    const statusLabel = (s: TaskStatus) => STATUS_LABELS[s]

    // Filters that apply to every task, dated or not (dates are handled separately)
    const matchesBaseFilters = (row: GanttRow): boolean => {
      if (filters.value.groupId) {
        if ((row.groupId || '') !== filters.value.groupId) return false
      }
      if (filters.value.assignee) {
        const name = String(props.assigneeName(row.assigneeUsername) || row.assigneeUsername)
        if (name !== filters.value.assignee) return false
      }
      const q = filters.value.search.trim().toLowerCase()
      if (q && !row.title.toLowerCase().includes(q) && !(row.description || '').toLowerCase().includes(q)) return false
      if (filters.value.status) {
        if (filters.value.status === 'nodate') {
          if (!isUndated(row)) return false
        } else if (statusOf(row) !== filters.value.status) {
          return false
        }
      }
      if (filters.value.hideCompleted && latestProgressPct(row) >= 100) return false
      return true
    }

    const filteredRows = computed(() =>
      props.rows.filter((row) => {
        if (isUndated(row)) return false
        if (!matchesBaseFilters(row)) return false
        const start = row.plannedStart?.slice(0, 10) || ''
        const end = row.plannedEnd?.slice(0, 10) || ''
        const dateFilterActive = !!(filters.value.dateFrom || filters.value.dateTo)
        if (dateFilterActive) {
          const outOfRange =
            (filters.value.dateFrom && end && end < filters.value.dateFrom) ||
            (filters.value.dateTo && start && start > filters.value.dateTo)
          if (outOfRange) {
            if (filters.value.showOverdue) {
              const isOverdue = latestProgressPct(row) < 100 && end && end < todayStr
              if (!isOverdue) return false
            } else {
              return false
            }
          }
        }
        return true
      })
    )

    // Tasks without a defined date, shown in a dedicated section below the timeline
    const undatedRows = computed(() =>
      props.rows.filter((row) => isUndated(row) && matchesBaseFilters(row))
    )

    // All rows visible in the UI (timeline + undated section) — used by selection
    const visibleRows = computed(() => [...filteredRows.value, ...undatedRows.value])

    const undatedCollapsed = ref(false)

    // ── SUMMARY / KPI ──
    const statusCounts = computed(() => {
      const c = { total: props.rows.length, idle: 0, progress: 0, done: 0, late: 0, nodate: 0 }
      props.rows.forEach((r) => {
        if (isUndated(r)) {
          c.nodate += 1
          return
        }
        c[statusOf(r)] += 1
      })
      return c
    })

    const overallPct = computed(() => {
      if (!props.rows.length) return 0
      const sum = props.rows.reduce((acc, r) => acc + latestProgressPct(r), 0)
      return Math.round(sum / props.rows.length)
    })

    const setStatusFilter = (s: string) => {
      filters.value.status = filters.value.status === s ? '' : s
    }

    // ── GROUPS ──
    const showGroupForm = ref(false)
    const groupSaving = ref(false)
    const groupForm = ref({ name: '', responsibleUsername: '' })

    const groupById = computed(() => {
      const map = new Map<string, TaskGroup>()
      props.groups.forEach((g) => map.set(g.id, g))
      return map
    })

    const saveGroup = async () => {
      if (!groupForm.value.name.trim() || !props.onSaveGroup) return
      groupSaving.value = true
      try {
        await props.onSaveGroup({
          name: groupForm.value.name.trim(),
          responsibleUsername: groupForm.value.responsibleUsername,
          taskIds: Array.from(selectedGanttIds.value)
        })
        showGroupForm.value = false
        groupForm.value = { name: '', responsibleUsername: '' }
        selectedGanttIds.value = new Set()
        selectedTaskId.value = ''
      } finally {
        groupSaving.value = false
      }
    }

    const removeGroup = async (groupId: string) => {
      if (!props.onDeleteGroup) return
      if (!await uiStore.confirm('Remover este grupo? As tarefas não serão apagadas.')) return
      await props.onDeleteGroup(groupId)
    }

    const clearSelection = () => {
      selectedGanttIds.value = new Set()
      selectedTaskId.value = ''
      showGroupForm.value = false
    }

    // ── GANTT EDIT MODE ──
    const ganttEditMode = ref(false)
    const selectedGanttIds = ref(new Set<string>())

    const isAllSelected = computed(
      () => visibleRows.value.length > 0 && selectedGanttIds.value.size === visibleRows.value.length
    )
    const isSomeSelected = computed(
      () => selectedGanttIds.value.size > 0 && !isAllSelected.value
    )

    watchEffect(() => {
      if (selectAllEl.value) {
        selectAllEl.value.indeterminate = isSomeSelected.value
      }
    })

    const toggleSelectRow = (id: string) => {
      const next = new Set(selectedGanttIds.value)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      selectedGanttIds.value = next
    }

    const toggleSelectAll = () => {
      if (isAllSelected.value) {
        selectedGanttIds.value = new Set()
      } else {
        selectedGanttIds.value = new Set(visibleRows.value.map((r) => r.id))
      }
    }

    const cancelEditMode = () => {
      ganttEditMode.value = false
      selectedGanttIds.value = new Set()
    }

    const deleteSelected = async () => {
      if (props.isAnalyst) return
      if (!props.onDeleteTask) return
      const ids = Array.from(selectedGanttIds.value)
      if (ids.length === 0) return
      if (!await uiStore.confirm(`Deseja apagar ${ids.length} tarefa${ids.length !== 1 ? 's' : ''}?`)) return
      for (const id of ids) {
        await props.onDeleteTask(id)
      }
      if (ganttEditMode.value) cancelEditMode()
      else clearSelection()
    }

    const getWeekSunday = (d: Date): Date => addDays(d, -d.getDay())

    const applyThisWeek = () => {
      const sun = getWeekSunday(new Date())
      filters.value.dateFrom = toDateStr(sun)
      filters.value.dateTo = toDateStr(addDays(sun, 6))
    }

    const applyLast2Weeks = () => {
      const sun = getWeekSunday(new Date())
      filters.value.dateFrom = toDateStr(addDays(sun, -14))
      filters.value.dateTo = toDateStr(addDays(sun, -1))
    }

    const applyLastMonth = () => {
      const today = new Date()
      const firstOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1)
      const lastOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0)
      filters.value.dateFrom = toDateStr(firstOfLastMonth)
      filters.value.dateTo = toDateStr(lastOfLastMonth)
    }

    const applyNextWeek = () => {
      const sun = getWeekSunday(new Date())
      const nextSun = addDays(sun, 7)
      filters.value.dateFrom = toDateStr(nextSun)
      filters.value.dateTo = toDateStr(addDays(nextSun, 6))
    }

    const clearFilters = () => {
      filters.value = { dateFrom: '', dateTo: '', assignee: '', groupId: '', search: '', status: '', hideCompleted: false, showOverdue: false }
    }

    const activeFilterCount = computed(() => {
      const f = filters.value
      let n = 0
      if (f.dateFrom || f.dateTo) n += 1
      if (f.assignee) n += 1
      if (f.groupId) n += 1
      if (f.hideCompleted) n += 1
      if (f.showOverdue) n += 1
      return n
    })

    // ── DATE RANGE ──
    const tasksRange = computed(() => {
      const starts = filteredRows.value.map((r) => parseDate(r.plannedStart)).filter(Boolean) as Date[]
      const ends = filteredRows.value.map((r) => parseDate(r.plannedEnd)).filter(Boolean) as Date[]
      if (!starts.length || !ends.length) return null
      return {
        start: new Date(Math.min(...starts.map((d) => d.getTime()))),
        end: new Date(Math.max(...ends.map((d) => d.getTime())))
      }
    })

    const activeRange = computed(() => {
      const fr = parseDate(filters.value.dateFrom)
      const to = parseDate(filters.value.dateTo)
      const today = parseDate(todayStr)
      if (fr && to) {
        const start = fr <= to ? fr : to
        let end = fr <= to ? to : fr
        // When showing overdue tasks, extend the range to include today so overdue bars reach today
        if (filters.value.showOverdue && today && today > end) {
          end = addDays(today, 1)
        }
        return { start, end }
      }
      const r = tasksRange.value
      if (!r) return null
      // Extend end to include today (for overdue bars) + 5 day buffer
      const rawEnd = today && today > r.end ? today : r.end
      return { start: addDays(r.start, -5), end: addDays(rawEnd, 5) }
    })

    const totalDays = computed(() => {
      if (!activeRange.value) return 1
      return Math.max(1, diffDays(activeRange.value.start, activeRange.value.end) + 1)
    })

    const totalWidthPx = computed(() => Math.max(800, totalDays.value * dayWidthPx.value))
    const timelineBodyHeightPx = computed(() => filteredRows.value.length * rowHeightPx)

    // ── TODAY ──
    const todayStr = toDateStr(new Date())
    const todayLeftPx = computed((): number | null => {
      if (!activeRange.value) return null
      const today = parseDate(todayStr)
      if (!today) return null
      const offset = diffDays(activeRange.value.start, today)
      if (offset < 0 || offset > totalDays.value) return null
      return offset * dayWidthPx.value + dayWidthPx.value / 2
    })

    // ── HEADER ──
    const headerDays = computed(() => {
      if (!activeRange.value) return []
      const rangeStart = activeRange.value.start
      return Array.from({ length: totalDays.value }, (_, i) => {
        const d = addDays(rangeStart, i)
        const iso = toDateStr(d)
        const dow = d.getDay() // 0=sun, 6=sat
        const isWeekend = dow === 0 || dow === 6
        const isToday = iso === todayStr
        const isBlocked = props.blockedDates.includes(iso)
        let label: string
        if (zoom.value === 'day') {
          label = String(d.getDate())
        } else if (zoom.value === 'week') {
          label = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S'][dow]
        } else {
          // month: only show day number on 1st and 8th to avoid clutter
          label = d.getDate() === 1 ? String(d.getDate()) : ''
        }
        return { key: iso + '-' + i, iso, label, isWeekend, isToday, isBlocked, leftPx: i * dayWidthPx.value }
      })
    })

    const headerGroups = computed(() => {
      if (!activeRange.value) return []
      const rangeStart = activeRange.value.start
      const groups: Array<{ key: string; label: string; leftPx: number; widthPx: number }> = []
      let i = 0
      while (i < totalDays.value) {
        const d = addDays(rangeStart, i)
        let label: string
        let groupEndDay: Date

        if (zoom.value === 'day' || zoom.value === 'month') {
          // Group by month
          const year = d.getFullYear()
          const month = d.getMonth()
          const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']
          label = `${monthNames[month]} ${year}`
          groupEndDay = new Date(year, month + 1, 0) // last day of month
        } else {
          // week zoom: group by week (Mon-Sun)
          const dow = d.getDay()
          const monday = addDays(d, -((dow + 6) % 7))
          const sunday = addDays(monday, 6)
          const weekNum = Math.ceil((diffDays(rangeStart, monday) + 1) / 7)
          label = `Sem ${weekNum}`
          groupEndDay = sunday
        }

        const endOfRange = addDays(rangeStart, totalDays.value - 1)
        const effectiveEnd = groupEndDay < endOfRange ? groupEndDay : endOfRange
        const spanDays = diffDays(d, effectiveEnd) + 1
        groups.push({ key: `grp-${i}`, label, leftPx: i * dayWidthPx.value, widthPx: spanDays * dayWidthPx.value })
        i += spanDays
      }
      return groups
    })

    const weekendDays = computed(() => headerDays.value.filter((d) => d.isWeekend && !d.isBlocked))
    const blockedDayCells = computed(() => headerDays.value.filter((d) => d.isBlocked))

    // ── BAR METRICS ──
    const barMetrics = (row: GanttRow, dragOffsetDays = 0) => {
      if (!activeRange.value) return null
      const rawStart = parseDate(row.plannedStart)
      const rawEnd = parseDate(row.plannedEnd)
      if (!rawStart || !rawEnd) return null
      const start = addDays(rawStart, dragOffsetDays)
      const end = addDays(rawEnd, dragOffsetDays)
      const rangeStart = activeRange.value.start
      const rangeEnd = activeRange.value.end
      const visStart = start < rangeStart ? rangeStart : start
      const visEnd = end > rangeEnd ? rangeEnd : end
      if (visEnd < visStart) return null
      const leftDays = Math.max(0, diffDays(rangeStart, visStart))
      // Count working days for the visual width (excluding weekends and blocked dates)
      const workDays = row.workDays || ['mon', 'tue', 'wed', 'thu', 'fri']
      const widthDays = countWorkingDays(visStart, visEnd, workDays, props.blockedDates)
      return {
        leftPx: leftDays * dayWidthPx.value,
        widthPx: Math.max(18, widthDays * dayWidthPx.value - 4)
      }
    }

    const computedBarStyle = (row: GanttRow) => {
      const dragOffset = (dragState.value && dragState.value.taskId === row.id) ? dragState.value.offsetDays : 0
      const metrics = barMetrics(row, dragOffset)
      if (!metrics) return { display: 'none' }
      const color = row.color || '#2f6fce'
      return {
        left: `${metrics.leftPx}px`,
        width: `${metrics.widthPx}px`,
        top: `${barTopPx}px`,
        height: `${barHeightPx}px`,
        background: color,
        '--bar-color': color
      }
    }

    // ── PROGRESS ──
    const latestProgressPct = (row: GanttRow): number => {
      const updates = row.progressUpdates || []
      if (!updates.length) return 0
      return Number(updates[updates.length - 1].progressPercent || 0)
    }

    const todayBarPct = (row: GanttRow): number | null => {
      const start = parseDate(row.plannedStart)
      const end = parseDate(row.plannedEnd)
      const today = parseDate(todayStr)
      if (!start || !end || !today) return null
      const total = diffDays(start, end)
      if (total <= 0) return null
      const elapsed = diffDays(start, today)
      return Math.min(100, Math.max(0, (elapsed / total) * 100))
    }

    const overdueBarStyle = (row: GanttRow): Record<string, string> => {
      if (!activeRange.value) return { display: 'none' }
      const end = parseDate(row.plannedEnd)
      const today = parseDate(todayStr)
      if (!end || !today) return { display: 'none' }

      const pct = latestProgressPct(row)
      let extensionEnd: Date

      if (pct >= 100) {
        const updates = row.progressUpdates || []
        const last = updates[updates.length - 1]
        if (!last) return { display: 'none' }
        const completionDate = parseDate(last.progressDate)
        if (!completionDate || completionDate <= end) return { display: 'none' }
        extensionEnd = completionDate
      } else {
        if (today <= end) return { display: 'none' }
        extensionEnd = today
      }

      const rangeStart = activeRange.value.start
      const rangeEnd = activeRange.value.end

      const visEnd = extensionEnd > rangeEnd ? rangeEnd : extensionEnd

      // If the normal bar is visible, start flush with its right edge (avoids gap due to working-day width)
      // If the task ended before the visible range, start at the range start (task is fully in the past)
      const barM = barMetrics(row)
      let overdueLeftPx: number
      if (barM) {
        overdueLeftPx = barM.leftPx + barM.widthPx + 4
      } else {
        // Task is entirely before the range; clamp overdue start to rangeStart
        const overdueStart = addDays(end, 1)
        const clampedStart = overdueStart < rangeStart ? rangeStart : overdueStart
        if (clampedStart > rangeEnd) return { display: 'none' }
        overdueLeftPx = Math.max(0, diffDays(rangeStart, clampedStart)) * dayWidthPx.value
      }

      const overdueRightPx = (diffDays(rangeStart, visEnd) + 1) * dayWidthPx.value

      if (overdueRightPx <= overdueLeftPx) return { display: 'none' }

      const color = row.color || '#2f6fce'

      return {
        left: `${overdueLeftPx}px`,
        width: `${Math.max(6, overdueRightPx - overdueLeftPx)}px`,
        top: `${barTopPx}px`,
        height: `${barHeightPx}px`,
        background: `linear-gradient(rgba(200, 30, 30, 0.5), rgba(200, 30, 30, 0.5)), ${color}`,
        '--bar-color': color
      }
    }

    // ── DEPENDENCY LINES ──
    const dependencyLines = computed(() => {
      const lines: Array<{ key: string; d: string }> = []
      if (!activeRange.value) return lines

      const rowIndex = new Map<string, number>()
      filteredRows.value.forEach((r, i) => rowIndex.set(r.id, i))

      filteredRows.value.forEach((row) => {
        const deps = row.dependencyTaskIds || []
        deps.forEach((depId) => {
          const depRow = filteredRows.value.find((r) => r.id === depId)
          if (!depRow) return
          const fromMetrics = barMetrics(depRow)
          const toMetrics = barMetrics(row)
          if (!fromMetrics || !toMetrics) return
          const fromIndex = rowIndex.get(depId)
          const toIndex = rowIndex.get(row.id)
          if (fromIndex === undefined || toIndex === undefined) return

          const x1 = fromMetrics.leftPx + fromMetrics.widthPx
          const y1 = fromIndex * rowHeightPx + rowHeightPx / 2
          const x2 = toMetrics.leftPx
          const y2 = toIndex * rowHeightPx + rowHeightPx / 2
          const cx = Math.abs(x2 - x1) * 0.45
          const d = `M ${x1},${y1} C ${x1 + cx},${y1} ${x2 - cx},${y2} ${x2},${y2}`
          lines.push({ key: `${depId}->${row.id}`, d })
        })
      })
      return lines
    })

    // ── DRAG & DROP ──
    const dragState = ref<{ taskId: string; startX: number; offsetDays: number } | null>(null)
    const labelResizeState = ref<{ startX: number; startWidth: number } | null>(null)
    const labelsWidth = ref(220)
    const panelExpanded = ref(false)

    const startDrag = (event: MouseEvent, row: GanttRow) => {
      if (props.accessMode !== 'edit') return
      event.preventDefault()
      dragState.value = { taskId: row.id, startX: event.clientX, offsetDays: 0 }
    }

    const startLabelResize = (event: MouseEvent) => {
      event.preventDefault()
      labelResizeState.value = { startX: event.clientX, startWidth: labelsWidth.value }
    }

    const startLabelResizeTouch = (event: TouchEvent) => {
      labelResizeState.value = { startX: event.touches[0].clientX, startWidth: labelsWidth.value }
    }

    const onTouchMove = (event: TouchEvent) => {
      if (!labelResizeState.value) return
      const dx = event.touches[0].clientX - labelResizeState.value.startX
      labelsWidth.value = Math.max(120, Math.min(600, labelResizeState.value.startWidth + dx))
    }

    const onTouchEnd = () => {
      if (labelResizeState.value) {
        labelResizeState.value = null
      }
    }

    const onMouseMove = (event: MouseEvent) => {
      if (labelResizeState.value) {
        const dx = event.clientX - labelResizeState.value.startX
        labelsWidth.value = Math.max(120, Math.min(600, labelResizeState.value.startWidth + dx))
        return
      }
      if (!dragState.value) return
      const dx = event.clientX - dragState.value.startX
      const days = Math.round(dx / dayWidthPx.value)
      dragState.value = { ...dragState.value, offsetDays: days }
    }

    const onMouseUp = async () => {
      if (labelResizeState.value) {
        labelResizeState.value = null
        return
      }
      if (!dragState.value || dragState.value.offsetDays === 0) {
        dragState.value = null
        return
      }
      const { taskId, offsetDays } = dragState.value
      dragState.value = null

      const row = props.rows.find((r) => r.id === taskId)
      if (!row || !props.onSaveTask) return
      const origStart = parseDate(row.plannedStart)
      if (!origStart) return
      const newStart = addDays(origStart, offsetDays)
      const fullTask = props.tasks.find((t) => t.id === taskId)
      if (!fullTask) return
      await props.onSaveTask(taskId, {
        title: fullTask.title,
        description: fullTask.description || '',
        durationDays: (() => { const s = parseDate(fullTask.plannedStart); const e = parseDate(fullTask.plannedEnd); return s && e ? diffDays(s, e) + 1 : 1 })(),
        workDays: (fullTask as GanttRow).workDays || ['mon', 'tue', 'wed', 'thu', 'fri'],
        dependencyTaskIds: (fullTask as GanttRow).dependencyTaskIds || [],
        requiredTools: (fullTask as GanttRow).requiredTools || [],
        requiredMachinery: (fullTask as GanttRow).requiredMachinery || [],
        infraestruturaNecessaria: (fullTask as GanttRow).infraestruturaNecessaria || '',
        linkedFileIds: (fullTask as GanttRow).linkedFileIds || [],
        teamIds: (fullTask as GanttRow).teamIds || [],
        forcedStartDate: toDateStr(newStart),
        assigneeUsername: fullTask.assigneeUsername,
        color: fullTask.color || '#2f6fce'
      })
    }

    // ── SELECTION & PANEL ──
    const selectedTaskId = ref('')
    const panelMode = ref<PanelMode>('view')
    const selectedTask = computed(() => visibleRows.value.find((r) => r.id === selectedTaskId.value) || null)
    const showPanel = computed(() => (!!selectedTaskId.value && !!selectedTask.value) || panelMode.value === 'create')

    const selectTask = (id: string) => {
      if (selectedTaskId.value === id && panelMode.value !== 'create') {
        closePanel()
        return
      }
      selectedTaskId.value = id
      panelMode.value = 'view'
      taskComments.value = []
      newComment.value = ''
      loadComments(id)
    }

    // New unified click handler: toggles multi-select AND manages panel
    const handleRowClick = (id: string) => {
      if (ganttEditMode.value) {
        toggleSelectRow(id)
        return
      }
      showGroupForm.value = false
      const next = new Set(selectedGanttIds.value)
      const wasSelected = next.has(id)
      if (wasSelected) {
        next.delete(id)
      } else {
        next.add(id)
      }
      selectedGanttIds.value = next
      // Only open the panel for the task the user actually clicked; deselecting
      // one task must never reopen the panel of another one that stayed selected
      if (!wasSelected && next.size === 1) {
        selectedTaskId.value = id
        panelMode.value = 'view'
        taskComments.value = []
        newComment.value = ''
        loadComments(id)
      } else {
        selectedTaskId.value = ''
        panelMode.value = 'view'
      }
    }

    const closePanel = () => {
      selectedTaskId.value = ''
      panelMode.value = 'view'
      selectedGanttIds.value = new Set()
      showGroupForm.value = false
    }

    // ── GANTT REPORT ──
    const showGanttReport = ref(false)
    const reportWeekOffset = ref(0)
    const reportRange = ref({ from: '', to: '' })
    const reportOnlyNoDate = ref(false)
    const reportLoading = ref(false)
    const reportError = ref('')
    interface ReportPreview {
      weekStart: string; weekEnd: string; projectName: string;
      tasks: Array<{
        id: string; title: string; assigneeUsername: string; plannedStart: string; plannedEnd: string
        status: string; noDate?: number; groupName: string | null; progressPercent: number;
        progressUpdates: Array<{ id?: string; progressDate: string; notes?: string; progressPercent?: number; photoPath?: string | null }>
      }>;
      upcoming: Array<{ id: string; title: string; assigneeUsername: string; plannedEnd: string; status: string }>;
      summary: { total: number; completed: number; overdue: number; notStarted: number; noDate?: number }
    }
    const reportData = ref<{ _preview: ReportPreview } | null>(null)

    const reportWeekLabel = computed(() => {
      if (reportOnlyNoDate.value) return 'Sem data'
      if (reportRange.value.from && reportRange.value.to) {
        return `${formatDatePt(reportRange.value.from)} – ${formatDatePt(reportRange.value.to)}`
      }
      if (reportWeekOffset.value === 0) return 'Semana atual'
      if (reportWeekOffset.value === -1) return 'Semana passada'
      if (reportWeekOffset.value < -1) return `${Math.abs(reportWeekOffset.value)} semanas atrás`
      return `Semana +${reportWeekOffset.value}`
    })

    const reportHasCustomRange = computed(() => !!(reportRange.value.from && reportRange.value.to))

    const reportPrevWeek = () => {
      reportRange.value = { from: '', to: '' }
      reportWeekOffset.value -= 1
      fetchGanttReport()
    }

    const reportNextWeek = () => {
      reportRange.value = { from: '', to: '' }
      reportWeekOffset.value += 1
      fetchGanttReport()
    }

    const applyReportRange = () => {
      if (!reportRange.value.from || !reportRange.value.to) return
      fetchGanttReport()
    }

    const clearReportRange = () => {
      reportRange.value = { from: '', to: '' }
      fetchGanttReport()
    }

    const toggleReportNoDate = () => {
      reportOnlyNoDate.value = !reportOnlyNoDate.value
      fetchGanttReport()
    }

    const fmtDateShort = (d?: string) => {
      if (!d) return ''
      const [, m, day] = d.slice(0, 10).split('-')
      return `${day}/${m}`
    }

    const taskPctClass = (pct: number, plannedEnd?: string) => {
      const today = new Date().toISOString().slice(0, 10)
      if (pct >= 100) return 'pct--done'
      if (plannedEnd && plannedEnd < today) return 'pct--late'
      if (pct > 0) return 'pct--progress'
      return 'pct--idle'
    }

    const fetchGanttReport = async () => {
      if (!props.projectId) return
      reportLoading.value = true
      reportError.value = ''
      reportData.value = null
      try {
        const data = await apiFetch<{ _preview: ReportPreview }>(
          `/projects/${encodeURIComponent(props.projectId)}/gantt-report`,
          {
            method: 'POST',
            body: JSON.stringify({
              weekOffset: reportWeekOffset.value,
              dateFrom: reportRange.value.from || undefined,
              dateTo: reportRange.value.to || undefined,
              onlyNoDate: reportOnlyNoDate.value || undefined
            })
          }
        )
        reportData.value = data
      } catch (err: unknown) {
        reportError.value = typeof err === 'object' && err !== null && 'message' in err ? String((err as any).message) : 'Erro ao gerar relatório.'
      } finally {
        reportLoading.value = false
      }
    }

    const openGanttReport = () => {
      reportWeekOffset.value = 0
      reportRange.value = { from: '', to: '' }
      reportOnlyNoDate.value = false
      showGanttReport.value = true
      fetchGanttReport()
    }

    // ── GANTT REPORT PRINT PREVIEW ──
    const showPrintPreview = ref(false)
    const printSubmission = ref<Submission | null>(null)
    const printSaving = ref(false)

    const openPrintPreview = async () => {
      if (!reportData.value) return
      printSaving.value = true
      try {
        const rd = reportData.value
        const user = sessionStore.state.user
        const subData: Omit<Submission, 'id' | 'createdAt'> = {
          title: rd.title,
          category: 'relatorio',
          attraction: rd._preview.projectName,
          reportDate: rd.reportDate,
          collaborators: rd.collaborators,
          tools: [],
          activities: rd.activities.map((a: { id: string; description: string; images: string[] }) => ({
            id: a.id,
            description: a.description,
            images: a.images || []
          })),
          observations: rd.observations.map((o: { id: string; text: string; images: string[] }) => ({
            id: o.id,
            text: o.text,
            images: o.images || []
          })),
          reportLanguage: 'pt',
          status: 'rascunho',
          author: user ? { name: user.name, role: user.role, username: user.username, uid: user.uid } : { name: '', role: 'user', username: '', uid: '' }
        }
        const savedId = await submissionsStore.addSubmission(subData)
        await submissionsStore.refresh()
        const saved = submissionsStore.state.items.find(s => s.id === savedId)
        if (saved) {
          printSubmission.value = saved
          showPrintPreview.value = true
        }
      } catch (err: unknown) {
        reportError.value = 'Erro ao salvar rascunho para impressão.'
      } finally {
        printSaving.value = false
      }
    }

    // ── PROGRESS FORM ──
    const progressForm = ref({ date: todayStr, percent: 0, notes: '' })

    const openProgressMode = () => {
      const task = selectedTask.value
      const taskEnd = task?.plannedEnd?.slice(0, 10) || ''
      const defaultDate = taskEnd && todayStr > taskEnd ? taskEnd : todayStr
      progressForm.value = {
        date: defaultDate,
        percent: 0,
        notes: ''
      }
      panelMode.value = 'progress'
    }

    const submitProgress = async () => {
      if (!props.onUpdateProgress) return
      await props.onUpdateProgress(selectedTaskId.value, {
        progressPercent: Number(progressForm.value.percent || 0),
        notes: progressForm.value.notes,
        progressDate: progressForm.value.date,
        photo: progressPhotoData.value || undefined
      } as any)
      panelMode.value = 'view'
      progressForm.value = { date: todayStr, percent: 0, notes: '' }
      progressPhotoPreview.value = ''
      progressPhotoData.value = ''
      selectedGanttIds.value = new Set()
    }

    const deleteProgressUpdate = async (updateId: string) => {
      if (props.isAnalyst) return
      if (!props.onDeleteProgress) return
      if (!await uiStore.confirm('Remover este registro de progresso?')) return
      await props.onDeleteProgress(selectedTaskId.value, updateId)
    }

    // ── PROGRESS PHOTO ──────────────────────────────────────────────────────────
    const progressPhotoInput = ref<HTMLInputElement | null>(null)
    const progressPhotoPreview = ref('')
    const progressPhotoData = ref('')
    const lightboxSrc = ref('')

    // Expandable progress history entries
    type ProgressUpd = NonNullable<GanttRow['progressUpdates']>[number]
    const expandedProgress = ref<Set<string>>(new Set())
    const progressUpdKey = (upd: ProgressUpd, idx: number) => String(upd.id || `idx-${idx}`)
    const hasProgressDetails = (upd: ProgressUpd) => !!(upd.notes || upd.photoPath)
    const isProgressExpanded = (upd: ProgressUpd, idx: number) => expandedProgress.value.has(progressUpdKey(upd, idx))
    const toggleProgressExpanded = (upd: ProgressUpd, idx: number) => {
      if (!hasProgressDetails(upd)) return
      const key = progressUpdKey(upd, idx)
      const next = new Set(expandedProgress.value)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      expandedProgress.value = next
    }

    const onProgressPhoto = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = (ev) => {
        progressPhotoData.value = ev.target?.result as string
        progressPhotoPreview.value = ev.target?.result as string
      }
      reader.readAsDataURL(file)
    }

    const resolvePhotoPath = (path: string) => {
      if (!path) return ''
      if (path.startsWith('/uploads/')) return `${(apiFetch as any).base || 'http://localhost:3001'}${path}`
      return path
    }

    const originalSubmitProgress = async () => {
      if (!props.onUpdateProgress) return
      await props.onUpdateProgress(selectedTaskId.value, {
        progressPercent: Number(progressForm.value.percent || 0),
        notes: progressForm.value.notes,
        progressDate: progressForm.value.date,
        photo: progressPhotoData.value || undefined
      } as any)
      panelMode.value = 'view'
      progressForm.value = { date: todayStr, percent: 0, notes: '' }
      progressPhotoPreview.value = ''
      progressPhotoData.value = ''
    }

    // ── TASK COMMENTS ───────────────────────────────────────────────────────────
    const COMMENT_COLORS = ['#2563eb', '#7c3aed', '#0891b2', '#059669', '#d97706', '#dc2626']
    const taskComments = ref<any[]>([])
    const commentsLoading = ref(false)
    const newComment = ref('')
    const isAdmin = computed(() => ['admin-master', 'admin-obra'].includes(sessionStore.state.user?.role || ''))

    const commentInitials = (name: string) => {
      const parts = name.trim().split(/\s+/)
      return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.slice(0, 2).toUpperCase()
    }
    const commentAvatarColor = (name: string) => COMMENT_COLORS[(name.charCodeAt(0) || 0) % COMMENT_COLORS.length]

    const loadComments = async (taskId: string) => {
      commentsLoading.value = true
      try {
        const data = await apiFetch(`/tasks/${taskId}/comments`)
        taskComments.value = data.comments || []
      } catch { taskComments.value = [] }
      commentsLoading.value = false
    }

    const submitComment = async () => {
      const msg = newComment.value.trim()
      if (!msg || !selectedTaskId.value) return
      try {
        const data = await apiFetch(`/tasks/${selectedTaskId.value}/comments`, { method: 'POST', body: JSON.stringify({ message: msg }) })
        taskComments.value.push(data.comment)
        newComment.value = ''
      } catch { /* silently fail */ }
    }

    const deleteComment = async (commentId: string) => {
      if (!await uiStore.confirm('Remover comentário?')) return
      try {
        await apiFetch(`/tasks/${selectedTaskId.value}/comments/${commentId}`, { method: 'DELETE' })
        taskComments.value = taskComments.value.filter((c) => c.id !== commentId)
      } catch { /* silently fail */ }
    }

    // ── EDIT FORM ──
    const weekdays = [
      { value: 'mon', label: 'Seg' }, { value: 'tue', label: 'Ter' },
      { value: 'wed', label: 'Qua' }, { value: 'thu', label: 'Qui' },
      { value: 'fri', label: 'Sex' }, { value: 'sat', label: 'Sab' },
      { value: 'sun', label: 'Dom' }
    ]

    const editForm = ref({
      title: '', description: '', durationDays: 1, workDays: ['mon', 'tue', 'wed', 'thu', 'fri'],
      dependencyTaskIds: [] as string[], requiredTools: [] as Array<{ itemId: string; quantity: number }>,
      forcedStartDate: '', noDate: false, assigneeUsername: '', color: '#2f6fce', infraestruturaNecessaria: '', linkedFileIds: [] as string[], teamIds: [] as string[],
      requiredMachinery: [] as Array<{ machineryId: string; quantity: number }>
    })
    const editError = ref('')

    // ── Recent colors ──────────────────────────────────────────────────────────
    const RECENT_COLORS_KEY = 'gantt:recentColors'
    let _recentColorsInit: string[] = []
    try { _recentColorsInit = JSON.parse(localStorage.getItem(RECENT_COLORS_KEY) || '[]') } catch { _recentColorsInit = [] }
    const recentColors = ref<string[]>(_recentColorsInit)
    function useColor(hex: string) {
      const list = recentColors.value.filter((c) => c !== hex)
      list.unshift(hex)
      recentColors.value = list.slice(0, 10)
      localStorage.setItem(RECENT_COLORS_KEY, JSON.stringify(recentColors.value))
    }

    const openEditMode = () => {
      if (props.isAnalyst) return
      const task = props.tasks.find((t) => t.id === selectedTaskId.value)
      if (!task) return
      const start = parseDate(task.plannedStart)
      const end = parseDate(task.plannedEnd)
      const taskWorkDays = (task as GanttRow).workDays || ['mon', 'tue', 'wed', 'thu', 'fri']
      const dur = start && end ? countWorkingDays(start, end, taskWorkDays, props.blockedDates) : 1
      editForm.value = {
        title: task.title,
        description: task.description || '',
        durationDays: dur,
        workDays: taskWorkDays,
        dependencyTaskIds: (task as GanttRow).dependencyTaskIds || [],
        requiredTools: (task as GanttRow).requiredTools || [],
        forcedStartDate: task.plannedStart?.slice(0, 10) || '',
        noDate: Number(task.noDate || 0) === 1,
        assigneeUsername: task.assigneeUsername,
        color: task.color || '#2f6fce',
        infraestruturaNecessaria: (task as GanttRow).infraestruturaNecessaria || '',
        linkedFileIds: (task as GanttRow).linkedFileIds || [],
        teamIds: (task as GanttRow).teamIds || [],
        requiredMachinery: (task as GanttRow).requiredMachinery || []
      }
      editError.value = ''
      depSearch.value = ''
      depSearchOpen.value = false
      fileSearch.value = ''
      fileSearchOpen.value = false
      teamSearch.value = ''
      teamSearchOpen.value = false
      toolSearch.value = ''
      toolSearchOpen.value = false
      selectedToolForQuantity.value = ''
      toolQuantityInput.value = 1
      machinerySearch.value = ''
      machinerySearchOpen.value = false
      panelMode.value = 'edit'
    }

    // Opens the edit panel for an undated task, pre-filled to receive a date
    const openScheduleTask = (id: string) => {
      if (props.isAnalyst || props.accessMode !== 'edit') return
      selectedGanttIds.value = new Set([id])
      selectedTaskId.value = id
      taskComments.value = []
      newComment.value = ''
      loadComments(id)
      openEditMode()
      editForm.value.noDate = false
      if (!editForm.value.forcedStartDate) editForm.value.forcedStartDate = todayStr
    }

    // Returns the forced start date derived from dependencies (day after the latest dep end), or null if none
    const startFromDeps = (): string | null => {
      const depIds = editForm.value.dependencyTaskIds
      if (!depIds.length) return null
      let latest: Date | null = null
      for (const id of depIds) {
        const dep = props.tasks.find((t) => t.id === id) || props.rows.find((r) => r.id === id)
        if (!dep) continue
        const end = parseDate(dep.plannedEnd)
        if (!end) continue
        if (!latest || end > latest) latest = end
      }
      return latest ? toDateStr(addDays(latest, 1)) : null
    }

    const submitEdit = async () => {
      if (!editForm.value.title.trim()) { editError.value = 'Titulo obrigatorio.'; return }
      if (!editForm.value.assigneeUsername) { editError.value = 'Selecione um responsavel.'; return }
      if (!props.onSaveTask) return
      editError.value = ''
      const payload = { ...editForm.value }
      if (payload.noDate) {
        payload.forcedStartDate = ''
      } else {
        const depStart = startFromDeps()
        if (depStart) payload.forcedStartDate = depStart
      }
      await props.onSaveTask(selectedTaskId.value, payload)
      panelMode.value = 'view'
      // Release the multi-select so the next click selects the clicked task,
      // instead of stacking on top of the task that was just edited
      selectedGanttIds.value = new Set()
    }

    // ── DEPENDENCY COMBOBOX ──
    const depSearch = ref('')
    const depSearchOpen = ref(false)
    const fileSearch = ref('')
    const fileSearchOpen = ref(false)
    const projectFiles = ref<Array<{ id: string; name: string }>>([])
    const teams = ref<Array<{ id: string; name: string }>>([])
    const teamSearch = ref('')
    const teamSearchOpen = ref(false)
    const warehouseItems = ref<Array<{ id: string; name: string; operationalUnits: number }>>([])
    const heavyMachinery = ref<HeavyMachineryItem[]>([])
    const toolSearch = ref('')
    const toolSearchOpen = ref(false)
    const selectedToolForQuantity = ref('')
    const toolQuantityInput = ref(1)
    const machinerySearch = ref('')
    const machinerySearchOpen = ref(false)

    const depSearchResults = computed(() => {
      const q = depSearch.value.toLowerCase().trim()
      return props.tasks.filter(t => {
        if (panelMode.value === 'edit' && t.id === selectedTaskId.value) return false
        if (editForm.value.dependencyTaskIds.includes(t.id)) return false
        if (q && !t.title.toLowerCase().includes(q)) return false
        return true
      })
    })

    const fileSearchResults = computed(() => {
      const q = fileSearch.value.toLowerCase().trim()
      return projectFiles.value.filter(f => {
        if (editForm.value.linkedFileIds.includes(f.id)) return false
        if (q && !f.name.toLowerCase().includes(q)) return false
        return true
      })
    })

    const teamSearchResults = computed(() => {
      const q = teamSearch.value.toLowerCase().trim()
      return teams.value.filter(t => {
        if (editForm.value.teamIds.includes(t.id)) return false
        if (q && !t.name.toLowerCase().includes(q)) return false
        return true
      })
    })

    const toolSearchResults = computed(() => {
      const q = toolSearch.value.toLowerCase().trim()
      return warehouseItems.value.filter(item => {
        const alreadyAdded = editForm.value.requiredTools.some(t => t.itemId === item.id)
        if (alreadyAdded) return false
        if (q && !item.name.toLowerCase().includes(q)) return false
        return true
      })
    })

    const addTool = (itemId: string, quantity: number) => {
      if (!editForm.value.requiredTools.some(t => t.itemId === itemId)) {
        editForm.value.requiredTools.push({ itemId, quantity: Math.max(1, quantity) })
      }
      toolSearch.value = ''
      selectedToolForQuantity.value = ''
      toolQuantityInput.value = 1
    }

    const removeTool = (itemId: string) => {
      editForm.value.requiredTools = editForm.value.requiredTools.filter(t => t.itemId !== itemId)
    }

    const machinerySearchResults = computed(() => {
      const q = machinerySearch.value.toLowerCase().trim()
      return heavyMachinery.value.filter(item => {
        const alreadyAdded = editForm.value.requiredMachinery.some(m => m.machineryId === item.id)
        if (alreadyAdded) return false
        if (q && !item.title.toLowerCase().includes(q)) return false
        return true
      })
    })

    const addMachinery = (machineryId: string, quantity: number) => {
      if (!editForm.value.requiredMachinery.some(m => m.machineryId === machineryId)) {
        editForm.value.requiredMachinery.push({ machineryId, quantity: Math.max(1, quantity) })
      }
      machinerySearch.value = ''
    }

    const removeMachinery = (machineryId: string) => {
      editForm.value.requiredMachinery = editForm.value.requiredMachinery.filter(m => m.machineryId !== machineryId)
    }

    const closeMachinerySearch = () => {
      machinerySearchOpen.value = false
    }

    const closeToolSearch = () => {
      setTimeout(() => { toolSearchOpen.value = false }, 150)
    }

    const addDep = (id: string) => {
      if (!editForm.value.dependencyTaskIds.includes(id)) {
        editForm.value.dependencyTaskIds.push(id)
      }
      depSearch.value = ''
      depSearchOpen.value = false
    }

    const removeDep = (id: string) => {
      editForm.value.dependencyTaskIds = editForm.value.dependencyTaskIds.filter(x => x !== id)
    }

    const closeDepSearch = () => {
      setTimeout(() => { depSearchOpen.value = false }, 150)
    }

    const addLinkedFile = (id: string) => {
      if (!editForm.value.linkedFileIds.includes(id)) {
        editForm.value.linkedFileIds.push(id)
      }
      fileSearch.value = ''
    }

    const removeLinkedFile = (id: string) => {
      editForm.value.linkedFileIds = editForm.value.linkedFileIds.filter(x => x !== id)
    }

    const closeFileSearch = () => {
      setTimeout(() => { fileSearchOpen.value = false }, 150)
    }

    const addTeam = (id: string) => {
      if (!editForm.value.teamIds.includes(id)) {
        editForm.value.teamIds.push(id)
      }
      teamSearch.value = ''
      teamSearchOpen.value = false
    }

    const removeTeam = (id: string) => {
      editForm.value.teamIds = editForm.value.teamIds.filter(x => x !== id)
    }

    const closeTeamSearch = () => {
      setTimeout(() => { teamSearchOpen.value = false }, 150)
    }

    const openCreatePanel = () => {
      if (props.isAnalyst) return
      selectedTaskId.value = ''
      editForm.value = {
        title: '', description: '', durationDays: 1,
        workDays: ['mon', 'tue', 'wed', 'thu', 'fri'],
        dependencyTaskIds: [], requiredTools: [], requiredMachinery: [],
        forcedStartDate: '', noDate: false, assigneeUsername: '', color: '#2f6fce', infraestruturaNecessaria: '', linkedFileIds: [], teamIds: []
      }
      editError.value = ''
      depSearch.value = ''
      depSearchOpen.value = false
      fileSearch.value = ''
      fileSearchOpen.value = false
      teamSearch.value = ''
      teamSearchOpen.value = false
      toolSearch.value = ''
      toolSearchOpen.value = false
      selectedToolForQuantity.value = ''
      toolQuantityInput.value = 1
      machinerySearch.value = ''
      machinerySearchOpen.value = false
      panelMode.value = 'create'
    }

    const submitCreate = async () => {
      if (!editForm.value.title.trim()) { editError.value = 'Titulo obrigatorio.'; return }
      if (!editForm.value.assigneeUsername) { editError.value = 'Selecione um responsavel.'; return }
      if (!props.onSaveTask) return
      editError.value = ''
      const payload = { ...editForm.value }
      if (payload.noDate) {
        payload.forcedStartDate = ''
      } else {
        const depStart = startFromDeps()
        if (depStart) payload.forcedStartDate = depStart
      }
      await props.onSaveTask('', payload)
      closePanel()
    }

    // ── BLOCKED DAYS MODAL ──
    const showBlockedDaysModal = ref(false)
    const localBlockedDates = ref<string[]>([])
    const newBlockedDate = ref('')

    const openBlockedDaysModal = () => {
      if (props.isAnalyst) return
      localBlockedDates.value = [...props.blockedDates]
      newBlockedDate.value = ''
      showBlockedDaysModal.value = true
    }

    const addBlockedDate = () => {
      const d = newBlockedDate.value
      if (!d || localBlockedDates.value.includes(d)) return
      localBlockedDates.value = [...localBlockedDates.value, d].sort()
      newBlockedDate.value = ''
    }

    const removeBlockedDate = (d: string) => {
      localBlockedDates.value = localBlockedDates.value.filter((x) => x !== d)
    }

    const saveBlockedDatesLocal = async () => {
      if (props.onSaveBlockedDates) {
        await props.onSaveBlockedDates(localBlockedDates.value)
      }
      showBlockedDaysModal.value = false
    }

    // ── SCROLL SYNC ──
    const syncLabelScroll = () => {
      if (labelsRef.value && scrollRef.value) {
        labelsRef.value.scrollTop = scrollRef.value.scrollTop
      }
    }

    const scrollToToday = () => {
      if (todayLeftPx.value !== null && scrollRef.value) {
        scrollRef.value.scrollLeft = Math.max(0, todayLeftPx.value - scrollRef.value.clientWidth / 2)
      }
    }

    // ── EXPORT EXCEL ──
    const exportGanttExcel = async () => {
      const rows = filteredRows.value
      const undated = undatedRows.value
      if (!rows.length && !undated.length) return
      const todayStr = new Date().toISOString().slice(0, 10)

      const allStarts = rows.map(r => r.plannedStart)
      const allEnds   = rows.map(r => r.plannedEnd)
      // Fallback range (only undated tasks): empty one-week grid
      const minDateStr = allStarts.length ? allStarts.reduce((a, b) => a < b ? a : b) : todayStr
      const maxDateStr = allEnds.length ? allEnds.reduce((a, b) => a > b ? a : b) : toDateStr(addDays(new Date(), 7))
      const rangeStart = new Date(minDateStr + 'T00:00:00')
      const rangeEnd   = new Date(maxDateStr + 'T00:00:00')
      const totalDayCount = Math.min(400, Math.round((rangeEnd.getTime() - rangeStart.getTime()) / DAY_MS) + 1)

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ExcelJSModule: any = await import('exceljs')
      const ExcelJS = ExcelJSModule.default || ExcelJSModule
      const wb = new ExcelJS.Workbook()
      wb.creator = 'Obra Tycoon'
      wb.created = new Date()

      const groupNameOf = (row: GanttRow): string => {
        if (!row.groupId) return ''
        return groupById.value.get(row.groupId)?.name || ''
      }
      const requesterNameOf = (row: GanttRow): string => {
        if (row.createdByName) return row.createdByName
        if (!row.createdBy) return ''
        return String(props.assigneeName(row.createdBy) || row.createdBy)
      }
      const latestUpdateTextOf = (row: GanttRow): string => {
        const updates = row.progressUpdates || []
        if (!updates.length) return ''
        const last = updates[updates.length - 1]
        const date = last.progressDate ? formatDatePt(last.progressDate) : ''
        const note = `${last.notes || ''}`.trim()
        return [date, note].filter(Boolean).join(' — ')
      }

      const INFO = 9
      const ws = wb.addWorksheet('Gantt', {
        views: [{ state: 'frozen', xSplit: INFO, ySplit: 3 }],
        pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
      })

      // Column widths
      ws.getColumn(1).width = 28
      ws.getColumn(2).width = 15
      ws.getColumn(3).width = 16
      ws.getColumn(4).width = 16
      ws.getColumn(5).width = 11
      ws.getColumn(6).width = 11
      ws.getColumn(7).width = 6
      ws.getColumn(8).width = 8
      ws.getColumn(9).width = 30
      for (let i = 0; i < totalDayCount; i++) ws.getColumn(INFO + 1 + i).width = 2.6

      const NAVY      = '1F3864'
      const NAVY_DARK = '0F2440'
      const lighten = (hex: string, amt: number) => {
        const h = hex.replace('#', '').padEnd(6, '0')
        return [0, 2, 4].map(o => {
          const v = parseInt(h.slice(o, o + 2), 16)
          return Math.min(255, Math.round(v + (255 - v) * amt)).toString(16).padStart(2, '0')
        }).join('').toUpperCase()
      }

      // ── Row 1: Title (left) + Branding (right) ──
      const BRANDING_GANTT = props.logoText || 'ObraTycoon by GusMachado'
      const totalCols = INFO + totalDayCount
      ws.getRow(1).height = 36

      // Left: project name + export date (cols 1 to totalCols-6)
      const brandingStartCol = Math.max(INFO + 1, totalCols - 5)
      if (brandingStartCol > 1) ws.mergeCells(1, 1, 1, brandingStartCol - 1)
      const titleCell = ws.getCell(1, 1)
      titleCell.value = `${props.projectName || 'Planejamento'} — Exportado em ${new Date().toLocaleDateString('pt-BR')}`
      titleCell.font = { bold: true, size: 13, color: { argb: 'FF' + NAVY } }
      titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDDE5F0' } }
      titleCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 }

      // Right: obra name + branding (last 6 cols)
      if (brandingStartCol <= totalCols) ws.mergeCells(1, brandingStartCol, 1, totalCols)
      const brandCell = ws.getCell(1, brandingStartCol)
      brandCell.value = { richText: [
        { text: props.systemName || props.projectName || 'Obra Tycoon', font: { bold: true, size: 10, color: { argb: 'FF' + NAVY } } },
        { text: '\n' + BRANDING_GANTT, font: { bold: false, size: 8, color: { argb: 'FF888880' } } },
      ] }
      brandCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFDDE5F0' } }
      brandCell.alignment = { vertical: 'middle', horizontal: 'right', indent: 1, wrapText: true }

      // ── Row 2: Info headers + month groupings ──
      ws.getRow(2).height = 18
      const infoHeaders = ['Tarefa', 'Categoria', 'Responsável', 'Solicitante', 'Início', 'Término', 'Dias', '%', 'Última atualização']
      infoHeaders.forEach((label, i) => {
        const cell = ws.getCell(2, i + 1)
        cell.value = label
        cell.font = { bold: true, size: 9, color: { argb: 'FFFFFFFF' } }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + NAVY } }
        cell.alignment = { vertical: 'middle', horizontal: 'center' }
        cell.border = { bottom: { style: 'medium', color: { argb: 'FF2F5496' } }, right: { style: 'thin', color: { argb: 'FF2F5496' } } }
      })

      // Month groups
      type MonthGroup = { col: number; endCol: number; label: string }
      const monthGroups: MonthGroup[] = []
      let curKey = ''
      let curStart = INFO + 1
      for (let i = 0; i <= totalDayCount; i++) {
        const d = new Date(rangeStart.getTime() + i * DAY_MS)
        const key = `${d.getFullYear()}-${d.getMonth()}`
        if (key !== curKey || i === totalDayCount) {
          if (curKey !== '' && i > 0) {
            const prevD = new Date(rangeStart.getTime() + (i - 1) * DAY_MS)
            monthGroups.push({ col: curStart, endCol: INFO + i, label: prevD.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }) })
          }
          curKey = key
          curStart = INFO + i + 1
        }
      }
      monthGroups.forEach(g => {
        if (g.endCol >= g.col) ws.mergeCells(2, g.col, 2, g.endCol)
        const c = ws.getCell(2, g.col)
        c.value = g.label.charAt(0).toUpperCase() + g.label.slice(1)
        c.font = { bold: true, size: 9, color: { argb: 'FFFFFFFF' } }
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + NAVY } }
        c.alignment = { vertical: 'middle', horizontal: 'center' }
        c.border = { bottom: { style: 'medium', color: { argb: 'FF2F5496' } }, right: { style: 'medium', color: { argb: 'FF2F5496' } } }
      })

      // ── Row 3: Day numbers ──
      ws.getRow(3).height = 14
      for (let i = 1; i <= INFO; i++) {
        const c = ws.getCell(3, i)
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + NAVY_DARK } }
        c.border = { bottom: { style: 'medium', color: { argb: 'FF2F5496' } } }
      }
      for (let i = 0; i < totalDayCount; i++) {
        const d = new Date(rangeStart.getTime() + i * DAY_MS)
        const dateStr = d.toISOString().slice(0, 10)
        const dow = d.getDay()
        const isWknd  = dow === 0 || dow === 6
        const isToday = dateStr === todayStr
        const isBlocked = props.blockedDates.includes(dateStr)
        const c = ws.getCell(3, INFO + 1 + i)
        c.value = d.getDate()
        c.font = { size: 7, bold: isToday, color: { argb: isToday ? 'FF0F4C81' : (isWknd ? 'FFBBBBBB' : 'FFFFFFFF') } }
        c.alignment = { horizontal: 'center', vertical: 'middle' }
        c.border = { bottom: { style: 'medium', color: { argb: 'FF2F5496' } } }
        if (isToday)   c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFBDD7EE' } }
        else if (isBlocked) c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFC7CE' } }
        else if (isWknd)    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4472C4' } }
        else            c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + NAVY_DARK } }
      }

      // ── Rows 4+: Tasks ──
      rows.forEach((row, rowIdx) => {
        const exRow = ws.getRow(4 + rowIdx)
        exRow.height = 20
        const pct      = latestProgressPct(row)
        const hexColor = (row.color || '#2f6fce').replace('#', '').padEnd(6, '0').toUpperCase()
        const assignee = String(props.assigneeName(row.assigneeUsername) || row.assigneeUsername || '')
        const tStart   = new Date(row.plannedStart + 'T00:00:00')
        const tEnd     = new Date(row.plannedEnd   + 'T00:00:00')
        const duration = Math.round((tEnd.getTime() - tStart.getTime()) / DAY_MS) + 1
        const isEven   = rowIdx % 2 === 0
        const rowBg    = isEven ? 'FFF5F8FF' : 'FFFFFFFF'

        const styleMeta = (cell: ReturnType<typeof ws.getCell>, val: unknown, opts: { bold?: boolean; center?: boolean } = {}) => {
          cell.value = val
          cell.font = { size: 9, bold: !!opts.bold, color: { argb: 'FF222222' } }
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } }
          cell.alignment = { vertical: 'middle', horizontal: opts.center ? 'center' : 'left', indent: opts.center ? 0 : 1 }
          cell.border = { bottom: { style: 'hair', color: { argb: 'FFDDDDDD' } }, right: { style: 'hair', color: { argb: 'FFDDDDDD' } } }
        }

        styleMeta(exRow.getCell(1), row.title, { bold: true })
        styleMeta(exRow.getCell(2), groupNameOf(row) || '—')
        styleMeta(exRow.getCell(3), assignee)
        styleMeta(exRow.getCell(4), requesterNameOf(row))
        styleMeta(exRow.getCell(5), row.plannedStart.split('-').reverse().join('/'), { center: true })
        styleMeta(exRow.getCell(6), row.plannedEnd.split('-').reverse().join('/'), { center: true })
        styleMeta(exRow.getCell(7), duration, { bold: true, center: true })
        const pctCell = exRow.getCell(8)
        pctCell.value = pct / 100
        pctCell.numFmt = '0%'
        pctCell.font = { size: 9, bold: true, color: { argb: pct >= 100 ? 'FF107C10' : pct > 0 ? 'FF0F4C81' : 'FF888888' } }
        pctCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: rowBg } }
        pctCell.alignment = { vertical: 'middle', horizontal: 'center' }
        pctCell.border = { bottom: { style: 'hair', color: { argb: 'FFDDDDDD' } }, right: { style: 'hair', color: { argb: 'FFDDDDDD' } } }
        styleMeta(exRow.getCell(9), latestUpdateTextOf(row))
        exRow.getCell(9).border = { bottom: { style: 'hair', color: { argb: 'FFDDDDDD' } }, right: { style: 'medium', color: { argb: 'FFAAAAAA' } } }

        const progressDays = Math.round(duration * pct / 100)
        for (let dayIdx = 0; dayIdx < totalDayCount; dayIdx++) {
          const d = new Date(rangeStart.getTime() + dayIdx * DAY_MS)
          const dateStr = d.toISOString().slice(0, 10)
          const dow = d.getDay()
          const isWknd    = dow === 0 || dow === 6
          const isToday   = dateStr === todayStr
          const isBlocked = props.blockedDates.includes(dateStr)
          const inTask    = dateStr >= row.plannedStart && dateStr <= row.plannedEnd
          const cell      = exRow.getCell(INFO + 1 + dayIdx)

          if (inTask) {
            const dayInTask = Math.round((d.getTime() - tStart.getTime()) / DAY_MS)
            const done  = dayInTask < progressDays
            const fill  = done ? hexColor : lighten('#' + hexColor, 0.5)
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + fill } }
          } else {
            let bg = isEven ? 'FFFAFAFA' : 'FFFFFFFF'
            if (isToday)        bg = 'FFE3F0FF'
            else if (isBlocked) bg = 'FFFFF0F0'
            else if (isWknd)    bg = isEven ? 'FFF0F0F0' : 'FFEEEEEE'
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }
          }
          if (isToday) cell.border = { left: { style: 'medium', color: { argb: 'FF0F4C81' } }, right: { style: 'medium', color: { argb: 'FF0F4C81' } } }
        }
      })

      // ── Undated tasks section (below the timeline grid) ──
      if (undated.length > 0) {
        let r = 4 + rows.length
        const hdrRow = ws.getRow(r)
        hdrRow.height = 20
        ws.mergeCells(r, 1, r, INFO)
        const hc = ws.getCell(r, 1)
        hc.value = `SEM DATA DEFINIDA (${undated.length})`
        hc.font = { bold: true, size: 9, color: { argb: 'FF9A6B00' } }
        hc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF4DC' } }
        hc.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 }
        hc.border = { top: { style: 'medium', color: { argb: 'FFD9B65C' } }, bottom: { style: 'thin', color: { argb: 'FFE8D9AE' } } }
        r += 1
        undated.forEach((row, i) => {
          const exRow = ws.getRow(r + i)
          exRow.height = 20
          const pct = latestProgressPct(row)
          const assignee = String(props.assigneeName(row.assigneeUsername) || row.assigneeUsername || '')
          const bg = i % 2 === 0 ? 'FFFFFBF2' : 'FFFFFFFF'
          const put = (col: number, val: unknown, opts: { bold?: boolean; center?: boolean } = {}) => {
            const cell = exRow.getCell(col)
            cell.value = val
            cell.font = { size: 9, bold: !!opts.bold, color: { argb: 'FF222222' } }
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }
            cell.alignment = { vertical: 'middle', horizontal: opts.center ? 'center' : 'left', indent: opts.center ? 0 : 1 }
            cell.border = { bottom: { style: 'hair', color: { argb: 'FFEEE3C8' } }, right: { style: 'hair', color: { argb: 'FFEEE3C8' } } }
          }
          put(1, row.title, { bold: true })
          put(2, groupNameOf(row) || '—')
          put(3, assignee)
          put(4, requesterNameOf(row))
          put(5, 'Sem data', { center: true })
          put(6, 'Sem data', { center: true })
          put(7, '—', { center: true })
          const pctCell = exRow.getCell(8)
          pctCell.value = pct / 100
          pctCell.numFmt = '0%'
          pctCell.font = { size: 9, bold: true, color: { argb: pct >= 100 ? 'FF107C10' : pct > 0 ? 'FF0F4C81' : 'FF888888' } }
          pctCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }
          pctCell.alignment = { vertical: 'middle', horizontal: 'center' }
          pctCell.border = { bottom: { style: 'hair', color: { argb: 'FFEEE3C8' } }, right: { style: 'hair', color: { argb: 'FFEEE3C8' } } }
          put(9, latestUpdateTextOf(row))
          exRow.getCell(9).border = { bottom: { style: 'hair', color: { argb: 'FFEEE3C8' } }, right: { style: 'medium', color: { argb: 'FFAAAAAA' } } }
        })
      }

      // ── Sheet 2: Data table ──
      const ws2 = wb.addWorksheet('Dados')
      ws2.columns = [
        { header: 'Tarefa',         key: 'title',       width: 32 },
        { header: 'Categoria',      key: 'group',       width: 18 },
        { header: 'Responsável',    key: 'assignee',    width: 20 },
        { header: 'Solicitante',    key: 'requester',   width: 20 },
        { header: 'Início',         key: 'start',       width: 12 },
        { header: 'Término',        key: 'end',         width: 12 },
        { header: 'Duração (dias)', key: 'duration',    width: 14 },
        { header: 'Progresso',      key: 'progress',    width: 12 },
        { header: 'Última atualização', key: 'lastUpdate', width: 40 },
        { header: 'Descrição',      key: 'description', width: 45 },
      ]
      ws2.getRow(1).height = 22
      ws2.getRow(1).eachCell(cell => {
        cell.font = { bold: true, size: 10, color: { argb: 'FFFFFFFF' } }
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + NAVY } }
        cell.alignment = { vertical: 'middle', horizontal: 'center' }
        cell.border = { bottom: { style: 'medium', color: { argb: 'FF2F5496' } } }
      })
      ;[...rows, ...undated].forEach((row, idx) => {
        const assignee = String(props.assigneeName(row.assigneeUsername) || row.assigneeUsername || '')
        const pct = latestProgressPct(row)
        const rowUndated = isUndated(row)
        let duration: number | string = '—'
        if (!rowUndated) {
          const tStart = new Date(row.plannedStart + 'T00:00:00')
          const tEnd   = new Date(row.plannedEnd + 'T00:00:00')
          duration = Math.round((tEnd.getTime() - tStart.getTime()) / DAY_MS) + 1
        }
        const dataRow = ws2.addRow({
          title: row.title,
          group: groupNameOf(row) || '—',
          assignee,
          requester: requesterNameOf(row),
          start: rowUndated ? 'Sem data' : row.plannedStart.split('-').reverse().join('/'),
          end: rowUndated ? 'Sem data' : row.plannedEnd.split('-').reverse().join('/'),
          duration,
          progress: pct / 100,
          lastUpdate: latestUpdateTextOf(row),
          description: row.description || ''
        })
        dataRow.height = 18
        const bg = rowUndated ? 'FFFFFBF2' : (idx % 2 === 0 ? 'FFF5F8FF' : 'FFFFFFFF')
        dataRow.eachCell(c => { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } }; c.alignment = { vertical: 'middle' }; c.border = { bottom: { style: 'hair', color: { argb: 'FFDDDDDD' } } } })
        const pc = dataRow.getCell(8); pc.numFmt = '0%'; pc.font = { bold: true }; pc.alignment = { horizontal: 'center', vertical: 'middle' }
      })

      // Download
      const buf  = await wb.xlsx.writeBuffer()
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url  = URL.createObjectURL(blob)
      const a    = document.createElement('a')
      a.href     = url
      a.download = `${(props.projectName || 'gantt').replace(/[^a-zA-Z0-9]/g, '-')}-planejamento.xlsx`
      a.click()
      URL.revokeObjectURL(url)
    }

    // ── EXPORT HTML ──
    const exportGantt = () => {
      const undatedExport = undatedRows.value
      const hasTimeline = !!activeRange.value && filteredRows.value.length > 0
      if (!hasTimeline && !undatedExport.length) return
      const dw = dayWidthPx.value
      const rh = rowHeightPx
      const bh = barHeightPx
      const bt = barTopPx
      const tbh = timelineBodyHeightPx.value
      const esc = (s: string) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

      // Compute a print day width that fills the landscape page width exactly
      const LABEL_W = 220
      const PAGE_W = 1080 // A4 landscape (297mm) at 96dpi minus 8mm margins ≈ 1080px
      const printDayW = Math.max(10, Math.floor((PAGE_W - LABEL_W) / totalDays.value))
      const scale = printDayW / dw
      const printTw = totalDays.value * printDayW

      const px = (v: number) => Math.round(v * scale)
      const parsePx = (s: string) => parseFloat(s) || 0

      const grpsHtml = headerGroups.value.map(g =>
        `<div style="position:absolute;left:${px(g.leftPx)}px;width:${px(g.widthPx)}px;top:0;bottom:0;display:flex;align-items:center;padding:0 8px;font-size:11px;font-weight:700;color:#555;border-right:1px solid #ddd;overflow:hidden;box-sizing:border-box">${esc(g.label)}</div>`
      ).join('')

      const daysHtml = headerDays.value.map(d => {
        let extra = ''
        if (d.isToday) extra = 'background:#e3f0ff;color:#0f4c81;font-weight:700;'
        else if (d.isBlocked) extra = 'background:rgba(200,0,0,0.06);color:#a00;'
        else if (d.isWeekend) extra = 'background:rgba(0,0,0,0.03);'
        // Only show label if there's room (>=14px per day)
        const label = printDayW >= 14 ? esc(d.label) : (d.isToday ? '·' : '')
        return `<div style="width:${printDayW}px;flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:9px;color:#777;border-right:1px solid #eee;height:26px;${extra}">${label}</div>`
      }).join('')

      const wkndHtml = weekendDays.value.map(d =>
        `<div style="position:absolute;left:${px(d.leftPx)}px;width:${printDayW}px;top:0;height:${tbh}px;background:rgba(0,0,0,0.025)"></div>`
      ).join('')

      const blkHtml = blockedDayCells.value.map(d =>
        `<div style="position:absolute;left:${px(d.leftPx)}px;width:${printDayW}px;top:0;height:${tbh}px;background:rgba(200,0,0,0.06);border-left:1px dashed rgba(200,0,0,0.25)"></div>`
      ).join('')

      const todayHtml = todayLeftPx.value !== null
        ? `<div style="position:absolute;left:${px(todayLeftPx.value)}px;width:1.5px;top:0;height:${tbh}px;background:#0f4c81;opacity:0.7"></div>`
        : ''

      const barsHtml = filteredRows.value.map((row, idx) => {
        const m = barMetrics(row)
        const pct = latestProgressPct(row)
        const color = esc(row.color || '#2f6fce')
        const os = overdueBarStyle(row)
        const overdueHtml = os.display !== 'none'
          ? `<div style="position:absolute;left:${px(parsePx(os.left))}px;width:${Math.max(6, px(parsePx(os.width)))}px;top:${esc(os.top)};height:${esc(os.height)};background:${esc(os.background)};border-radius:0 5px 5px 0"></div>`
          : ''
        const barHtml = m
          ? `<div style="position:absolute;left:${px(m.leftPx)}px;width:${Math.max(10, px(m.widthPx))}px;top:${bt}px;height:${bh}px;background:${color};border-radius:5px;overflow:visible;display:flex;align-items:center">
            <div style="position:absolute;left:0;top:0;bottom:0;width:${pct}%;background:rgba(0,0,0,0.22);border-radius:5px;max-width:100%"></div>
            <span style="position:absolute;left:50%;transform:translateX(-50%);font-size:10px;font-weight:700;color:#fff;text-shadow:0 1px 3px rgba(0,0,0,.55);white-space:nowrap;z-index:3">${pct}%</span>
          </div>`
          : ''
        return `<div style="position:absolute;left:0;right:0;top:${idx * rh}px;height:${rh}px;border-bottom:1px solid #eee">${barHtml}${overdueHtml}</div>`
      }).join('')

      const labelsHtml = filteredRows.value.map(row => {
        const pct = latestProgressPct(row)
        const color = esc(row.color || '#2f6fce')
        const assignee = esc(String(props.assigneeName(row.assigneeUsername) || row.assigneeUsername || ''))
        return `<div style="height:${rh}px;padding:6px 10px;border-bottom:1px solid #eee;display:flex;flex-direction:column;justify-content:center;box-sizing:border-box">
          <div style="font-size:13px;font-weight:600;color:#201f1e;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${esc(row.title)}</div>
          <div style="font-size:11px;color:#605e5c;margin-top:2px">${assignee}</div>
          <div style="display:flex;align-items:center;gap:6px;margin-top:4px">
            <div style="flex:1;height:4px;background:#eee;border-radius:2px;overflow:hidden">
              <div style="height:100%;width:${pct}%;background:${color};border-radius:2px"></div>
            </div>
            <span style="font-size:10px;font-weight:700;color:#605e5c;min-width:28px;text-align:right">${pct}%</span>
          </div></div>`
      }).join('')

      const title = esc(props.projectName || 'Planejamento')
      const exportDate = new Date().toLocaleDateString('pt-BR')
      const scriptClose = '</' + 'script>'

      // Build a map of id → title for dependency lookups
      const taskTitleMap: Record<string, string> = {}
      props.tasks.forEach(t => { taskTitleMap[t.id] = t.title })

      // Build a map of team id → name for team lookups
      const teamNameMap: Record<string, string> = {}
      teams.value.forEach(t => { teamNameMap[t.id] = t.name })

      // Build a map of tool id → name for tool lookups
      const toolNameMap: Record<string, string> = {}
      warehouseItems.value.forEach(t => { toolNameMap[t.id] = t.name })

      // Build a map of machinery id → title for machinery lookups
      const machineryNameMap: Record<string, string> = {}
      heavyMachinery.value.forEach(m => { machineryNameMap[m.id] = m.title })

      const requesterNameOfRow = (row: GanttRow): string => {
        if (row.createdByName) return row.createdByName
        if (!row.createdBy) return ''
        return String(props.assigneeName(row.createdBy) || row.createdBy)
      }
      const lastUpdateHtmlOf = (row: GanttRow): string => {
        const updates = row.progressUpdates || []
        if (!updates.length) return ''
        const upd = updates[updates.length - 1]
        const dateStr = esc(formatDatePt(upd.progressDate))
        const pctStr = `${upd.progressPercent ?? 0}%`
        const notesStr = upd.notes ? ` — ${esc(upd.notes)}` : ''
        return `<div class="tl-last-update"><span class="tl-label">Último progresso:</span> ${dateStr} · <strong>${pctStr}</strong>${notesStr}</div>`
      }
      const groupRequesterHtmlOf = (row: GanttRow): string => {
        const groupName = row.groupId ? (groupById.value.get(row.groupId)?.name || '') : ''
        const requester = requesterNameOfRow(row)
        const parts: string[] = []
        if (groupName) parts.push(`<div class="tl-meta"><span class="tl-label">Categoria:</span> <span class="tl-tag">${esc(groupName)}</span></div>`)
        if (requester) parts.push(`<div class="tl-meta"><span class="tl-label">Solicitante:</span> ${esc(requester)}</div>`)
        return parts.join('')
      }

      const taskListHtml = filteredRows.value.map((row, idx) => {
        const pct = latestProgressPct(row)
        const color = esc(row.color || '#2f6fce')
        const assignee = esc(String(props.assigneeName(row.assigneeUsername) || row.assigneeUsername || ''))
        const desc = row.description ? `<div class="tl-desc">${esc(row.description)}</div>` : ''
        const deps = (row.dependencyTaskIds || []).map(id => esc(taskTitleMap[id] || id))
        const depsHtml = deps.length
          ? `<div class="tl-meta"><span class="tl-label">Pendências:</span> ${deps.map(d => `<span class="tl-tag">${d}</span>`).join(' ')}</div>`
          : ''
        const infraestrutura = row.infraestruturaNecessaria ? `<div class="tl-meta"><span class="tl-label">Infraestrutura:</span> ${esc(row.infraestruturaNecessaria)}</div>` : ''
        const teamNames = (row.teamIds || []).map(id => esc(teamNameMap[id] || id))
        const teamsHtml = teamNames.length
          ? `<div class="tl-meta"><span class="tl-label">Equipes:</span> ${teamNames.map(t => `<span class="tl-tag">${t}</span>`).join(' ')}</div>`
          : ''
        const toolsInfo = (row.requiredTools || []).map(tool => {
          const toolRequest = (row.toolRequests || []).find(r => r.itemId === tool.itemId)
          const toolStatus = toolRequest?.status || 'pendente'
          const statusLabel = toolStatus === 'aprovado' ? ' ✓' : toolStatus === 'reprovado' ? ' ✗' : ''
          return `${esc(toolNameMap[tool.itemId] || tool.itemId)}×${tool.quantity}${statusLabel}`
        })
        const toolsHtml = toolsInfo.length
          ? `<div class="tl-meta"><span class="tl-label">Ferramentas:</span> ${toolsInfo.map(t => `<span class="tl-tag">${t}</span>`).join(' ')}</div>`
          : ''
        const machineryInfo = (row.requiredMachinery || []).map(m => `${esc(machineryNameMap[m.machineryId] || m.machineryId)}×${m.quantity}`)
        const machineryHtml = machineryInfo.length
          ? `<div class="tl-meta"><span class="tl-label">Maquinário:</span> ${machineryInfo.map(m => `<span class="tl-tag">${m}</span>`).join(' ')}</div>`
          : ''
        const lastUpdate = lastUpdateHtmlOf(row)
        const start = esc(formatDatePt(row.plannedStart))
        const end = esc(formatDatePt(row.plannedEnd))
        const bg = idx % 2 === 0 ? '#fff' : '#f9f9f7'
        return `<tr style="background:${bg}">
  <td class="tl-num">${idx + 1}</td>
  <td class="tl-main">
    <div class="tl-title"><span class="tl-dot" style="background:${color}"></span>${esc(row.title)}</div>
    ${desc}${lastUpdate}${depsHtml}${infraestrutura}${toolsHtml}${machineryHtml}${teamsHtml}
    <div class="tl-meta"><span class="tl-label">Responsavel:</span> ${assignee}</div>
    ${groupRequesterHtmlOf(row)}
  </td>
  <td class="tl-date">${start}</td>
  <td class="tl-date">${end}</td>
  <td class="tl-pct">
    <div class="tl-bar-wrap"><div class="tl-bar-fill" style="width:${pct}%;background:${color}"></div></div>
    <span class="tl-pct-val">${pct}%</span>
  </td>
</tr>`
      }).join('')

      const undatedListHtml = undatedExport.map((row, idx) => {
        const pct = latestProgressPct(row)
        const color = esc(row.color || '#2f6fce')
        const assignee = esc(String(props.assigneeName(row.assigneeUsername) || row.assigneeUsername || ''))
        const desc = row.description ? `<div class="tl-desc">${esc(row.description)}</div>` : ''
        const bg = idx % 2 === 0 ? '#fffdf5' : '#fff'
        return `<tr style="background:${bg}">
  <td class="tl-num">${idx + 1}</td>
  <td class="tl-main">
    <div class="tl-title"><span class="tl-dot" style="background:${color}"></span>${esc(row.title)}</div>
    ${desc}${lastUpdateHtmlOf(row)}
    <div class="tl-meta"><span class="tl-label">Responsavel:</span> ${assignee}</div>
    ${groupRequesterHtmlOf(row)}
  </td>
  <td class="tl-date" style="color:#9a6b00;font-weight:600">Sem data</td>
  <td class="tl-date" style="color:#9a6b00;font-weight:600">Sem data</td>
  <td class="tl-pct">
    <div class="tl-bar-wrap"><div class="tl-bar-fill" style="width:${pct}%;background:${color}"></div></div>
    <span class="tl-pct-val">${pct}%</span>
  </td>
</tr>`
      }).join('')

      const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Gantt - ${title}</title>
<style>*{box-sizing:border-box;margin:0;padding:0;font-family:-apple-system,sans-serif;-webkit-print-color-adjust:exact;print-color-adjust:exact}body{background:#fff;color:#201f1e}
.page-header{display:flex;align-items:flex-start;justify-content:space-between;padding:16px 16px 4px}.page-header-left{}.page-header-right{text-align:right;flex-shrink:0;padding-left:16px}
h1{font-size:20px;font-weight:700;margin:0}.sub{font-size:12px;color:#888;padding:4px 0 12px}
.brand-obra{font-size:13px;font-weight:700;color:#1F3864;margin-bottom:2px}.brand-tag{font-size:10px;color:#888}
.gantt{display:flex;border:1px solid #ddd;border-radius:6px;margin:0 16px 16px;overflow:hidden}
.labels{width:220px;min-width:220px;flex-shrink:0;border-right:1px solid #ddd;background:#f9f9f7}
.lhdr{height:52px;display:flex;align-items:center;padding:0 10px;font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#888;border-bottom:1px solid #ddd;background:#fff}
.section-title{font-size:15px;font-weight:700;padding:16px 16px 8px;border-bottom:2px solid #e8e8e6;margin:0 16px 0}
.task-table{width:calc(100% - 32px);margin:0 16px 24px;border-collapse:collapse;font-size:13px}
.task-table th{background:#f5f5f3;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.04em;color:#888;padding:8px 12px;text-align:left;border-bottom:2px solid #ddd}
.task-table td{padding:10px 12px;vertical-align:top;border-bottom:1px solid #eee}
.tl-num{width:32px;color:#aaa;font-size:11px;text-align:center;padding-top:12px}
.tl-main{max-width:380px}
.tl-title{display:flex;align-items:center;gap:7px;font-weight:600;font-size:13px;color:#201f1e;margin-bottom:4px}
.tl-dot{width:10px;height:10px;border-radius:50%;flex-shrink:0}
.tl-desc{font-size:12px;color:#605e5c;margin:2px 0 4px 17px;line-height:1.4}
.tl-meta{font-size:11px;color:#888;margin-top:3px;margin-left:17px}
.tl-label{font-weight:600;color:#605e5c}
.tl-tag{display:inline-block;background:#e8e8e6;border-radius:3px;padding:1px 6px;margin:1px 2px 1px 0;color:#444;font-size:11px}
.tl-date{width:90px;font-size:12px;color:#444;white-space:nowrap;padding-top:12px}
.tl-pct{width:90px;padding-top:14px}
.tl-bar-wrap{height:5px;background:#eee;border-radius:3px;overflow:hidden;margin-bottom:3px}
.tl-bar-fill{height:100%;border-radius:3px}
.tl-pct-val{font-size:11px;font-weight:700;color:#444}
.tl-last-update{font-size:11px;color:#1F3864;margin:5px 0 3px 17px;padding:4px 8px;background:#eef3fa;border-left:3px solid #2f6fce;border-radius:0 4px 4px 0;line-height:1.4}
@media print{@page{size:landscape;margin:8mm}*{-webkit-print-color-adjust:exact!important;print-color-adjust:exact!important}}</style></head>
<body><div class="page-header"><div class="page-header-left"><h1>${title}</h1><div class="sub">Exportado em ${exportDate}</div></div><div class="page-header-right"><div class="brand-obra">${esc(props.systemName || props.projectName || 'Obra Tycoon')}</div><div class="brand-tag">${esc(props.logoText || 'ObraTycoon by GusMachado')}</div></div></div>
${hasTimeline ? `<div class="gantt">
  <div class="labels"><div class="lhdr">Tarefa</div>${labelsHtml}</div>
  <div style="overflow:visible">
    <div style="width:${printTw}px">
      <div style="height:52px;background:#fff;border-bottom:1px solid #ddd;width:${printTw}px">
        <div style="height:26px;position:relative;background:#f5f5f3;border-bottom:1px solid #ddd;overflow:hidden">${grpsHtml}</div>
        <div style="height:26px;display:flex;overflow:hidden">${daysHtml}</div>
      </div>
      <div style="width:${printTw}px;height:${tbh}px;position:relative">${wkndHtml}${blkHtml}${todayHtml}${barsHtml}</div>
    </div>
  </div>
</div>
<div class="section-title">Detalhamento das tarefas</div>
<table class="task-table">
  <thead><tr><th></th><th>Tarefa</th><th>Início</th><th>Fim</th><th>Progresso</th></tr></thead>
  <tbody>${taskListHtml}</tbody>
</table>` : ''}
${undatedExport.length ? `<div class="section-title" style="color:#9a6b00">Atividades sem data definida (${undatedExport.length})</div>
<table class="task-table">
  <thead><tr><th></th><th>Tarefa</th><th>Início</th><th>Fim</th><th>Progresso</th></tr></thead>
  <tbody>${undatedListHtml}</tbody>
</table>` : ''}
<script>window.addEventListener('load',function(){setTimeout(function(){window.print()},300)})${scriptClose}
</body></html>`

      const win = window.open('', '_blank')
      if (!win) { uiStore.toast('Permita pop-ups para exportar o diagrama.', 'error'); return }
      win.document.open()
      win.document.write(html)
      win.document.close()
    }

    // ── OUTSIDE CLICK (filters) ──
    const importFileInput = ref<HTMLInputElement | null>(null)
    const showImportModal = ref(false)
    const advancedImportMode = ref(false)
    const importDrafts = ref<ImportedTaskDraft[]>([])
    const importError = ref('')
    const isImporting = ref(false)
    const currentUsername = computed(() => sessionStore.state.user?.username || props.assignableUsers[0]?.username || '')

    const createImportedDraft = (row: unknown[], index: number): ImportedTaskDraft | null => {
      const title = `${row[0] || ''}`.trim()
      if (!title) return null
      const durationDays = Math.max(1, Number(row[2] || 1))
      return {
        id: newId('import'),
        title,
        description: `${row[1] || ''}`.trim(),
        durationDays: Number.isFinite(durationDays) ? durationDays : 1,
        forcedStartDate: normalizeImportedDate(row[3]),
        color: normalizeImportedColor(row[4]),
        assigneeUsername: currentUsername.value,
        workDays: ['mon', 'tue', 'wed', 'thu', 'fri'],
        dependencyTaskIds: [],
        requiredTools: [],
        requiredMachinery: [],
        infraestruturaNecessaria: '',
        linkedFileIds: [],
        teamIds: [],
        toolToAdd: '',
        toolQuantity: 1,
        machineryToAdd: '',
        machineryQuantity: 1
      }
    }

    const openImportModal = () => {
      if (props.isAnalyst || props.accessMode !== 'edit') return
      showImportModal.value = true
      importError.value = ''
    }

    const closeImportModal = () => {
      showImportModal.value = false
      advancedImportMode.value = false
      importError.value = ''
      isImporting.value = false
      importDrafts.value = []
      if (importFileInput.value) importFileInput.value.value = ''
    }

    const triggerImportFile = () => {
      importFileInput.value?.click()
    }

    const handleImportFile = async (event: Event) => {
      const input = event.target as HTMLInputElement
      const file = input.files?.[0]
      if (!file) return
      importError.value = ''
      try {
        const buffer = await file.arrayBuffer()
        const workbook = XLSX.read(buffer, { type: 'array' })
        const sheetName = workbook.SheetNames[0]
        if (!sheetName) throw new Error('A planilha enviada nao possui abas.')
        const sheet = workbook.Sheets[sheetName]
        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true }) as unknown[][]
        const drafts = rows
          .slice(1)
          .map((row, index) => createImportedDraft(row, index))
          .filter((row): row is ImportedTaskDraft => Boolean(row))
        if (drafts.length === 0) throw new Error('Nenhuma tarefa valida encontrada na planilha.')
        importDrafts.value = drafts
      } catch (err: unknown) {
        importDrafts.value = []
        importError.value = err instanceof Error ? err.message : 'Falha ao ler a planilha.'
      } finally {
        input.value = ''
      }
    }

    const downloadImportTemplate = () => {
      const workbook = XLSX.utils.book_new()
      const sheet = XLSX.utils.aoa_to_sheet([
        ['Titulo da tarefa', 'Descricao', 'Duracao em Dias', 'Data de inicio', 'Cor'],
        ['Escavacao inicial', 'Preparar frente de servico', 5, '2026-03-23', '#2f6fce']
      ])
      sheet['!cols'] = [{ wch: 28 }, { wch: 42 }, { wch: 16 }, { wch: 16 }, { wch: 12 }]
      XLSX.utils.book_append_sheet(workbook, sheet, 'Template')
      const content = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' })
      const blob = new Blob([content], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = 'template-importacao-planejamento.xlsx'
      link.click()
      window.URL.revokeObjectURL(url)
    }

    const removeImportedTask = (draftId: string) => {
      importDrafts.value = importDrafts.value.filter((draft) => draft.id !== draftId)
    }

    const findImportedDraft = (draftId: string) => importDrafts.value.find((draft) => draft.id === draftId) || null

    const toggleImportedWorkday = (draftId: string, workday: string) => {
      const draft = findImportedDraft(draftId)
      if (!draft) return
      draft.workDays = draft.workDays.includes(workday)
        ? draft.workDays.filter((entry) => entry !== workday)
        : [...draft.workDays, workday]
    }

    const dependencyOptionsForImport = () => props.tasks
    const availableToolsForImport = (draft: ImportedTaskDraft) => warehouseItems.value.filter((item) => !draft.requiredTools.some((entry) => entry.itemId === item.id))
    const availableMachineryForImport = (draft: ImportedTaskDraft) => heavyMachinery.value.filter((item) => !draft.requiredMachinery.some((entry) => entry.machineryId === item.id))
    const toolName = (itemId: string) => warehouseItems.value.find((entry) => entry.id === itemId)?.name || itemId
    const machineryLabel = (machineryId: string) => heavyMachinery.value.find((entry) => entry.id === machineryId)?.title || machineryId

    const addImportedTool = (draftId: string) => {
      const draft = findImportedDraft(draftId)
      if (!draft || !draft.toolToAdd) return
      draft.requiredTools.push({ itemId: draft.toolToAdd, quantity: Math.max(1, Number(draft.toolQuantity || 1)) })
      draft.toolToAdd = ''
      draft.toolQuantity = 1
    }

    const removeImportedTool = (draftId: string, itemId: string) => {
      const draft = findImportedDraft(draftId)
      if (!draft) return
      draft.requiredTools = draft.requiredTools.filter((entry) => entry.itemId !== itemId)
    }

    const addImportedMachinery = (draftId: string) => {
      const draft = findImportedDraft(draftId)
      if (!draft || !draft.machineryToAdd) return
      draft.requiredMachinery.push({ machineryId: draft.machineryToAdd, quantity: Math.max(1, Number(draft.machineryQuantity || 1)) })
      draft.machineryToAdd = ''
      draft.machineryQuantity = 1
    }

    const removeImportedMachinery = (draftId: string, machineryId: string) => {
      const draft = findImportedDraft(draftId)
      if (!draft) return
      draft.requiredMachinery = draft.requiredMachinery.filter((entry) => entry.machineryId !== machineryId)
    }

    const confirmImportTasks = async () => {
      if (!props.onImportTasks || importDrafts.value.length === 0) return
      for (const draft of importDrafts.value) {
        if (!draft.title.trim()) { importError.value = 'Todas as tarefas precisam de titulo.'; return }
        if (!draft.forcedStartDate) { importError.value = 'Todas as tarefas precisam de data de inicio valida.'; return }
        if (!draft.assigneeUsername) { importError.value = 'Selecione um responsavel para todas as tarefas.'; return }
      }
      importError.value = ''
      isImporting.value = true
      try {
        await props.onImportTasks(importDrafts.value.map((draft) => ({
          title: draft.title.trim(),
          description: draft.description.trim(),
          durationDays: Math.max(1, Number(draft.durationDays || 1)),
          workDays: draft.workDays.length ? [...draft.workDays] : ['mon', 'tue', 'wed', 'thu', 'fri'],
          dependencyTaskIds: [...draft.dependencyTaskIds],
          requiredTools: draft.requiredTools.map((entry) => ({ itemId: entry.itemId, quantity: Math.max(1, Number(entry.quantity || 1)) })),
          requiredMachinery: draft.requiredMachinery.map((entry) => ({ machineryId: entry.machineryId, quantity: Math.max(1, Number(entry.quantity || 1)) })),
          forcedStartDate: draft.forcedStartDate,
          assigneeUsername: draft.assigneeUsername,
          color: normalizeImportedColor(draft.color),
          infraestruturaNecessaria: draft.infraestruturaNecessaria.trim(),
          linkedFileIds: [...draft.linkedFileIds],
          teamIds: [...draft.teamIds]
        })))
        uiStore.toast('Tarefas importadas com sucesso.', 'success')
        closeImportModal()
      } catch (err: unknown) {
        importError.value = err instanceof Error ? err.message : 'Falha ao importar tarefas.'
      } finally {
        isImporting.value = false
      }
    }

    const handleOutsideClick = (e: MouseEvent) => {
      if (showFilters.value && filterRef.value && !filterRef.value.contains(e.target as Node)) {
        showFilters.value = false
      }
      if (showExportMenu.value && exportMenuRef.value && !exportMenuRef.value.contains(e.target as Node)) {
        showExportMenu.value = false
      }
    }

    const loadProjectFiles = async () => {
      if (!props.projectId) return
      try {
        const files = await apiFetch<Array<{ id: string; name: string; extension: string }>>(`/projects/${encodeURIComponent(props.projectId)}/files`)
        projectFiles.value = files.map(f => ({ id: f.id, name: f.name }))
      } catch (_err) {
        projectFiles.value = []
      }
    }

    const loadTeams = async () => {
      if (!props.projectId) return
      try {
        const fetchedTeams = await apiFetch<Array<{ id: string; name: string }>>(`/projects/${encodeURIComponent(props.projectId)}/teams`)
        teams.value = fetchedTeams
      } catch (_err) {
        teams.value = []
      }
    }

    const loadWarehouseItems = async () => {
      try {
        const items = await apiFetch<Array<{ id: string; name: string; operationalUnits: number }>>('/warehouse/items')
        warehouseItems.value = items
      } catch (_err) {
        warehouseItems.value = []
      }
    }

    const loadHeavyMachinery = async () => {
      try {
        const items = await apiFetch<HeavyMachineryItem[]>('/machinery')
        heavyMachinery.value = items
      } catch (_err) {
        heavyMachinery.value = []
      }
    }

    watch(() => props.openNewTask, (val) => {
      if (val && props.accessMode === 'edit' && !props.isAnalyst) {
        openCreatePanel()
      }
    }, { immediate: true })

    onMounted(() => {
      window.addEventListener('click', handleOutsideClick)
      nextTick(scrollToToday)
      loadProjectFiles()
      loadTeams()
      loadWarehouseItems()
      loadHeavyMachinery()
    })

    onBeforeUnmount(() => {
      window.removeEventListener('click', handleOutsideClick)
    })

    watch(() => props.projectId, () => {
      loadProjectFiles()
      loadTeams()
    })

    watch(() => props.rows, () => {
      nextTick(scrollToToday)
    }, { deep: false })

    // ── SHARE LINKS ──
    interface ShareLink {
      id: string
      email: string | null
      expiresAt: string | null
      revokedAt: string | null
      createdAt: string
      createdBy: string
    }

    const showShareModal = ref(false)
    const showDurationModal = ref(false)
    const showManageModal = ref(false)
    const durationTarget = ref<'link' | 'email'>('link')
    const shareEmail = ref('')
    const selectedDuration = ref<string>('7d')
    const shareLinks = ref<ShareLink[]>([])
    const shareLoading = ref(false)
    const shareError = ref('')
    const generatedLink = ref('')
    const generatedExpiry = ref('')
    const linkCopied = ref(false)
    const copiedHash = ref('')
    const extendingLink = ref<ShareLink | null>(null)
    const extendDuration = ref<string>('7d')

    const durationPresets = [
      { label: '1 dia', value: '1d' },
      { label: '7 dias', value: '7d' },
      { label: '1 mês', value: '30d' },
      { label: 'Indefinido', value: 'never' }
    ]

    function durationToExpiry(dur: string): string | null {
      if (dur === 'never') return null
      const days = dur === '1d' ? 1 : dur === '7d' ? 7 : 30
      const d = new Date()
      d.setDate(d.getDate() + days)
      return d.toISOString()
    }

    function buildShareUrl(hash: string): string {
      return `${window.location.origin}/gantt/${hash}`
    }

    function isExpired(link: ShareLink): boolean {
      if (!link.expiresAt) return false
      return new Date(link.expiresAt) < new Date()
    }

    async function loadShareLinks() {
      if (!props.projectId) return
      try {
        const data = await apiFetch<ShareLink[]>(`/gantt-share/project/${encodeURIComponent(props.projectId)}`)
        shareLinks.value = data
      } catch (_e) {
        // silently fail
      }
    }

    async function openShareModal() {
      showShareModal.value = true
      await loadShareLinks()
    }

    function closeShareModal() {
      showShareModal.value = false
    }

    function startShareLink(target: 'link' | 'email') {
      durationTarget.value = target
      shareEmail.value = ''
      selectedDuration.value = '7d'
      shareError.value = ''
      showDurationModal.value = true
      showShareModal.value = false
    }

    async function confirmShare() {
      if (!props.projectId) return
      shareLoading.value = true
      shareError.value = ''
      try {
        const expiresAt = durationToExpiry(selectedDuration.value)
        const body: Record<string, string | null> = { projectId: props.projectId, expiresAt }
        if (durationTarget.value === 'email' && shareEmail.value.trim()) {
          body.email = shareEmail.value.trim()
        }
        const data = await apiFetch<{ hash: string; expiresAt: string | null }>('/gantt-share', { method: 'POST', body: JSON.stringify(body) })
        await loadShareLinks()
        showDurationModal.value = false
        generatedLink.value = buildShareUrl(data.hash)
        generatedExpiry.value = data.expiresAt ? new Date(data.expiresAt).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) : ''
        linkCopied.value = false
      } catch (e: unknown) {
        shareError.value = e instanceof Error ? e.message : 'Erro ao gerar link.'
      } finally {
        shareLoading.value = false
      }
    }

    async function copyLink(url: string) {
      try {
        await navigator.clipboard.writeText(url)
        // figure out which copy indicator to set
        const hashMatch = url.match(/\/gantt\/([^?#]+)$/)
        if (hashMatch) {
          copiedHash.value = hashMatch[1]
          setTimeout(() => { copiedHash.value = '' }, 2000)
        } else {
          linkCopied.value = true
          setTimeout(() => { linkCopied.value = false }, 2000)
        }
      } catch (_e) {
        // fallback
      }
    }

    async function revokeLink(hash: string) {
      try {
        await apiFetch(`/gantt-share/${hash}`, { method: 'DELETE' })
        await loadShareLinks()
      } catch (_e) {
        // silently fail
      }
    }

    function startExtend(link: ShareLink) {
      extendingLink.value = link
      extendDuration.value = '7d'
      showManageModal.value = false
    }

    async function confirmExtend() {
      if (!extendingLink.value) return
      try {
        const expiresAt = durationToExpiry(extendDuration.value)
        await apiFetch(`/gantt-share/${extendingLink.value.id}`, { method: 'PATCH', body: JSON.stringify({ expiresAt }) })
        await loadShareLinks()
        extendingLink.value = null
        showManageModal.value = true
      } catch (_e) {
        // silently fail
      }
    }

    // Larguras variadas só para o skeleton não parecer uma grade uniforme demais.
    const skeletonBarWidths = ['55%', '75%', '40%', '65%', '85%', '50%']
    const skeletonBarWidth = (i: number) => skeletonBarWidths[(i - 1) % skeletonBarWidths.length]

    return {
      skeletonBarWidth,
      scrollRef, labelsRef, filterRef, exportMenuRef, selectAllEl,
      showExportMenu,
      zoom, zoomScale, dayWidthPx, setZoom,
      rowHeightPx, headerHeightPx,
      showFilters, filters, assigneeOptions, filteredRows, applyThisWeek, applyLast2Weeks, applyLastMonth, applyNextWeek, clearFilters, activeFilterCount,
      undatedRows, undatedCollapsed, statusCounts, overallPct, setStatusFilter, statusOf, statusLabel, isUndated, openScheduleTask,
      groupById, showGroupForm, groupSaving, groupForm, saveGroup, removeGroup, clearSelection, handleRowClick,
      ganttEditMode, selectedGanttIds, isAllSelected, toggleSelectRow, toggleSelectAll, cancelEditMode, deleteSelected,
      activeRange, totalWidthPx, timelineBodyHeightPx,
      todayLeftPx,
      headerDays, headerGroups, weekendDays, blockedDayCells,
      computedBarStyle, latestProgressPct, todayBarPct, overdueBarStyle,
      dependencyLines,
      dragState, startDrag, onMouseMove, onMouseUp, startLabelResize, startLabelResizeTouch, onTouchMove, onTouchEnd, labelsWidth, panelExpanded,
      selectedTaskId, selectedTask, panelMode, showPanel, selectTask, closePanel,
      progressForm, openProgressMode, submitProgress, deleteProgressUpdate,
      progressPhotoInput, progressPhotoPreview, progressPhotoData, onProgressPhoto,
      lightboxSrc, resolvePhotoPath,
      hasProgressDetails, isProgressExpanded, toggleProgressExpanded,
      taskComments, commentsLoading, newComment, isAdmin,
      commentInitials, commentAvatarColor, submitComment, deleteComment,
      recentColors, useColor,
      editForm, editError, weekdays, openEditMode, submitEdit, openCreatePanel, submitCreate,
      depSearch, depSearchOpen, depSearchResults, addDep, removeDep, closeDepSearch,
      fileSearch, fileSearchOpen, fileSearchResults, projectFiles, addLinkedFile, removeLinkedFile, closeFileSearch,
      teamSearch, teamSearchOpen, teamSearchResults, teams, addTeam, removeTeam, closeTeamSearch,
      toolSearch, toolSearchOpen, toolSearchResults, warehouseItems, addTool, removeTool, closeToolSearch, selectedToolForQuantity, toolQuantityInput,
      machinerySearch, machinerySearchOpen, machinerySearchResults, heavyMachinery, addMachinery, removeMachinery, closeMachinerySearch,
      showBlockedDaysModal, localBlockedDates, newBlockedDate,
      openBlockedDaysModal, addBlockedDate, removeBlockedDate, saveBlockedDatesLocal,
      showImportModal, advancedImportMode, importDrafts, importError, isImporting, importFileInput,
      openImportModal, closeImportModal, triggerImportFile, handleImportFile, downloadImportTemplate,
      removeImportedTask, toggleImportedWorkday, dependencyOptionsForImport,
      availableToolsForImport, addImportedTool, removeImportedTool, toolName,
      availableMachineryForImport, addImportedMachinery, removeImportedMachinery, machineryLabel,
      confirmImportTasks,
      syncLabelScroll, scrollToToday, exportGantt, exportGanttExcel,
      formatDate: formatDatePt,
      formatDatePt,
      showShareModal, showDurationModal, showManageModal, durationTarget, shareEmail,
      selectedDuration, shareLinks, shareLoading, shareError, generatedLink, generatedExpiry,
      linkCopied, copiedHash, extendingLink, extendDuration, durationPresets,
      openShareModal, closeShareModal, startShareLink, confirmShare, copyLink,
      revokeLink, startExtend, confirmExtend, buildShareUrl, isExpired,
      showGanttReport, reportWeekOffset, reportWeekLabel, reportLoading, reportError, reportData,
      reportRange, reportOnlyNoDate, reportHasCustomRange,
      reportPrevWeek, reportNextWeek, applyReportRange, clearReportRange, toggleReportNoDate,
      openGanttReport, fetchGanttReport, fmtDateShort, taskPctClass,
      showPrintPreview, printSubmission, printSaving, openPrintPreview
    }
  }
})
</script>

<style scoped lang="scss">
/* ── SHELL ── */
.planning-shell {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  user-select: none;
}


.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 6px;
}

.zoom-group {
  display: flex;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  overflow: hidden;
}

.zoom-btn {
  background: var(--surface);
  border: none;
  padding: 5px 12px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: var(--ink-muted);
  border-right: 1px solid var(--border);
  transition: background 0.1s ease, color 0.1s ease;
}

.zoom-btn:last-child { border-right: none; }

.zoom-btn:hover { background: var(--surface-3); color: var(--ink); }

.zoom-btn.active {
  background: var(--system-navbar, #0f4c81);
  color: #fff;
  font-weight: 600;
}

.toolbar-btn {
  background: var(--surface);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  padding: 5px 12px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  color: var(--ink);
  transition: background 0.1s ease;
  white-space: nowrap;
}

.toolbar-btn:hover, .toolbar-btn.active { background: var(--surface-3); }

/* ── TAB-STYLE ACTION BUTTONS (teleported to tabs bar) ── */
.tab-action-btn {
  border: 1px solid rgba(31, 30, 26, 0.18);
  background: transparent;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 500;
  font-size: 12px;
  color: var(--ink-muted, #605e5c);
  transition: background 0.12s, color 0.12s;
  white-space: nowrap;
}
.tab-action-btn:hover {
  background: rgba(31, 30, 26, 0.06);
  color: var(--ink, #201f1e);
}
.tab-action-btn.active {
  background: rgba(31, 30, 26, 0.08);
  color: var(--ink, #201f1e);
  border-color: rgba(31, 30, 26, 0.3);
}

.tab-action-btn--danger {
  color: #a4262c;
  border-color: rgba(164, 38, 44, 0.3);
}
.tab-action-btn--danger:hover { background: rgba(164, 38, 44, 0.08); }

/* ── EXPORT DROPDOWN ── */
.export-wrap { position: relative; display: inline-flex; }
.tab-action-btn { display: inline-flex; align-items: center; }

.export-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 300;
  background: var(--card, #fff);
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14), 0 2px 6px rgba(0, 0, 0, 0.08);
  padding: 6px;
  min-width: 210px;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.export-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: none;
  background: transparent;
  border-radius: 7px;
  cursor: pointer;
  text-align: left;
  width: 100%;
  transition: background .12s;
  &:hover { background: var(--surface, rgba(0,0,0,.04)); }
}
.export-option-icon {
  width: 32px; height: 32px;
  border-radius: 7px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  &--excel { background: rgba(16, 124, 65, .1); }
  &--pdf   { background: rgba(192, 0, 0, .1); }
}
.export-option-text { display: flex; flex-direction: column; gap: 1px; }
.export-option-label { font-size: 13px; font-weight: 600; color: var(--ink, #201f1e); }
.export-option-sub   { font-size: 11px; color: var(--ink-muted, #888); }

.tab-action-cb-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0 2px;
}
.tab-action-cb {
  width: 15px;
  height: 15px;
  cursor: pointer;
  accent-color: var(--accent, #0078d4);
}
.tab-action-hint {
  font-size: 12px;
  color: var(--ink-muted);
  white-space: nowrap;
}

/* ── FILTER DROPDOWN / BOTTOM SHEET ── */
.filter-wrap { position: relative; }

.filter-backdrop {
  display: none;
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.44);
  z-index: 90;
}

.filter-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-md);
  z-index: 95;
  width: 300px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.filter-sheet-grip { display: none; }

.filter-sheet-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px 8px;
}
.filter-sheet-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--ink);
}
.filter-sheet-close {
  border: none;
  background: var(--surface-3);
  color: var(--ink-muted);
  width: 28px;
  height: 28px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.12s, color 0.12s;
}
.filter-sheet-close:hover { background: var(--border); color: var(--ink); }

.filter-sheet-body {
  display: grid;
  gap: 14px;
  padding: 4px 16px 12px;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  flex: 1;
  min-height: 0;
  max-height: 66vh;
}

.filter-section {
  display: grid;
  gap: 8px;
}
.filter-section-label {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--ink-faint, var(--ink-muted));
}

.filter-field {
  display: grid;
  gap: 4px;
}
.filter-field label {
  font-size: 12px;
  font-weight: 600;
  color: var(--ink-muted);
}

.filter-dates {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.filter-input {
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  padding: 7px 10px;
  font-size: 13px;
  background: var(--surface);
  color: var(--ink);
  width: 100%;
  min-height: 36px;
}
.filter-input:focus {
  outline: none;
  border-color: var(--accent, #0f4c81);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent, #0f4c81) 16%, transparent);
}

.filter-check-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: var(--ink);
  cursor: pointer;
  padding: 4px 0;
}
.filter-check-row input {
  cursor: pointer;
  accent-color: var(--accent, #0078d4);
  width: 16px;
  height: 16px;
}

.filter-shortcuts {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.filter-chip {
  border: 1px solid var(--border-strong);
  background: var(--surface);
  color: var(--ink-muted);
  border-radius: 99px;
  padding: 5px 11px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}
.filter-chip:hover {
  background: var(--surface-3);
  color: var(--ink);
}

.filter-sheet-foot {
  display: flex;
  gap: 8px;
  padding: 10px 16px 12px;
  border-top: 1px solid var(--border);
  background: var(--surface);
  flex-shrink: 0;
}
.filter-foot-btn {
  flex: 1;
  border: 1px solid var(--border-strong);
  background: var(--surface);
  color: var(--ink);
  border-radius: var(--radius, 6px);
  padding: 9px 12px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s, opacity 0.12s;
}
.filter-foot-btn:hover:not(:disabled) { background: var(--surface-3); }
.filter-foot-btn:disabled { opacity: 0.45; cursor: default; }
.filter-foot-btn--primary {
  flex: 1.6;
  background: var(--system-navbar, #0f4c81);
  border-color: var(--system-navbar, #0f4c81);
  color: #fff;
}
.filter-foot-btn--primary:hover:not(:disabled) { background: var(--system-navbar, #0f4c81); opacity: 0.9; }

.filter-zoom-row {
  display: flex;
  gap: 4px;
}
.filter-zoom-row .zoom-btn {
  flex: 1;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  padding: 6px 6px;
  font-size: 12px;
  min-height: 32px;
}
.filter-zoom-row .zoom-btn:last-child { border-right: 1px solid var(--border-strong); }

.filter-scale-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.scale-slider {
  flex: 1;
  accent-color: var(--system-navbar, #0f4c81);
  height: 4px;
  cursor: pointer;
}

.scale-value {
  font-size: 12px;
  font-weight: 600;
  color: var(--ink);
  min-width: 40px;
  text-align: right;
}

/* Mobile: filter becomes a bottom sheet */
@media (max-width: 640px) {
  .filter-backdrop { display: block; }

  .filter-dropdown {
    position: fixed;
    top: auto;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    max-height: min(84dvh, 640px);
    border-radius: 18px 18px 0 0;
    border: none;
    box-shadow: 0 -12px 40px rgba(15, 23, 42, 0.25);
    padding-bottom: env(safe-area-inset-bottom, 0);
    animation: filter-sheet-up 0.22s cubic-bezier(0.32, 0.72, 0.28, 1);
  }

  .filter-sheet-grip {
    display: block;
    width: 40px;
    height: 4px;
    border-radius: 99px;
    background: var(--border-strong);
    margin: 8px auto 0;
    flex-shrink: 0;
  }

  .filter-sheet-body { gap: 16px; }

  /* touch-friendly targets */
  .filter-input,
  .filter-zoom-row .zoom-btn { min-height: 42px; font-size: 14px; }
  .filter-chip { padding: 8px 14px; font-size: 13px; }
  .filter-check-row { padding: 8px 0; font-size: 14px; }
  .filter-check-row input { width: 18px; height: 18px; }
  .filter-foot-btn { padding: 12px; font-size: 14px; }
  .filter-import-btn { min-height: 42px; }
}

@keyframes filter-sheet-up {
  from { transform: translateY(24%); opacity: 0.6; }
  to   { transform: translateY(0);   opacity: 1; }
}

/* ── EMPTY STATE ── */
.empty-state {
  padding: 48px 0;
  text-align: center;
  color: var(--ink-muted);
}
.gantt-skeleton {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px 4px;
}
.gantt-skeleton-row {
  display: flex;
  align-items: center;
  gap: 24px;
}
.empty-add-btn {
  margin-top: 16px;
  padding: 8px 20px;
  background: var(--accent);
  color: #fff;
  border: none;
  border-radius: var(--radius, 6px);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
}
.empty-add-btn:hover { opacity: 0.85; }

/* ── TOPBAR (search + status chips + overall progress) ── */
.planning-topbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding: 10px 2px 0;
  flex-shrink: 0;
}

.topbar-search {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 180px;
  flex: 0 1 240px;
}
.topbar-search-icon {
  position: absolute;
  left: 9px;
  color: var(--ink-muted);
  pointer-events: none;
}
.topbar-search-input {
  width: 100%;
  border: 1px solid var(--border-strong);
  border-radius: 99px;
  padding: 6px 26px 6px 28px;
  font-size: 13px;
  background: var(--surface);
  color: var(--ink);
  transition: border-color 0.12s, box-shadow 0.12s;
}
.topbar-search-input:focus {
  outline: none;
  border-color: var(--accent, #0f4c81);
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent, #0f4c81) 18%, transparent);
}
.topbar-search-clear {
  position: absolute;
  right: 6px;
  border: none;
  background: transparent;
  color: var(--ink-muted);
  cursor: pointer;
  font-size: 11px;
  padding: 4px;
  border-radius: 50%;
}
.topbar-search-clear:hover { color: var(--ink); }

.status-chips {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  flex: 1;
  min-width: 0;
}
.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid var(--border-strong);
  background: var(--surface);
  color: var(--ink-muted);
  border-radius: 99px;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}
.status-chip:hover { background: var(--surface-3); color: var(--ink); }
.status-chip.active {
  background: var(--system-navbar, #0f4c81);
  border-color: var(--system-navbar, #0f4c81);
  color: #fff;
}
.chip-count {
  background: color-mix(in srgb, currentColor 14%, transparent);
  border-radius: 99px;
  padding: 0 6px;
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
  min-width: 16px;
  text-align: center;
}
.status-chip--progress:not(.active) { color: #0f6cbd; border-color: rgba(15, 108, 189, 0.35); }
.status-chip--done:not(.active)     { color: #107c10; border-color: rgba(16, 124, 16, 0.35); }
.status-chip--late:not(.active)     { color: #b42318; border-color: rgba(180, 35, 24, 0.35); }
.status-chip--nodate:not(.active)   { color: #9a6b00; border-color: rgba(154, 107, 0, 0.4); }
.status-chip--progress.active { background: #0f6cbd; border-color: #0f6cbd; }
.status-chip--done.active     { background: #107c10; border-color: #107c10; }
.status-chip--late.active     { background: #b42318; border-color: #b42318; }
.status-chip--nodate.active   { background: #9a6b00; border-color: #9a6b00; }

.topbar-overall {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
  flex-shrink: 0;
}
.topbar-overall-track {
  width: 110px;
  height: 6px;
  border-radius: 99px;
  background: var(--surface-3);
  overflow: hidden;
}
.topbar-overall-fill {
  height: 100%;
  border-radius: 99px;
  background: var(--system-navbar, #0f4c81);
  transition: width 0.3s ease;
}
.topbar-overall-pct {
  font-size: 12px;
  font-weight: 700;
  color: var(--ink-muted);
  min-width: 34px;
  text-align: right;
}

/* ── STATUS PILL ── */
.status-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 99px;
  padding: 2px 9px;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0;
}
.status-pill--idle     { background: color-mix(in srgb, var(--ink-muted) 14%, transparent); color: var(--ink-muted); }
.status-pill--progress { background: rgba(15, 108, 189, 0.13); color: #0f6cbd; }
.status-pill--done     { background: rgba(16, 124, 16, 0.13); color: #107c10; }
.status-pill--late     { background: rgba(180, 35, 24, 0.12); color: #b42318; }

/* ── UNDATED TASKS SECTION ── */
.undated-section {
  flex-shrink: 0;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  margin-top: 10px;
  background: var(--surface);
  overflow: hidden;
  max-height: 42%;
  display: flex;
  flex-direction: column;
}
.undated-header {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  background: color-mix(in srgb, #9a6b00 5%, var(--surface));
  padding: 9px 12px;
  cursor: pointer;
  text-align: left;
  flex-shrink: 0;
}
.undated-chevron { color: var(--ink-muted); transition: transform 0.15s ease; flex-shrink: 0; }
.undated-chevron.open { transform: rotate(90deg); }
.undated-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--ink);
}
.undated-count {
  background: rgba(154, 107, 0, 0.15);
  color: #9a6b00;
  border-radius: 99px;
  padding: 0 7px;
  font-size: 11px;
  font-weight: 700;
  line-height: 17px;
}
.undated-hint {
  font-size: 11px;
  color: var(--ink-muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.undated-list { overflow-y: auto; }
.undated-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border-top: 1px solid var(--border);
  cursor: pointer;
  transition: background 0.1s ease;
}
.undated-row:hover { background: var(--surface-3); }
.undated-row.selected,
.undated-row--multi { background: color-mix(in srgb, var(--system-navbar, #0f4c81) 8%, var(--surface)); }
.undated-row--checked { background: rgba(31, 30, 26, 0.06); }
.undated-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}
.undated-info { flex: 1; min-width: 0; }
.undated-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.undated-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: var(--ink-muted);
  margin-top: 1px;
}
.undated-progress {
  display: flex;
  align-items: center;
  gap: 6px;
  width: 130px;
  flex-shrink: 0;
}
.undated-progress-track {
  flex: 1;
  height: 5px;
  border-radius: 99px;
  background: var(--surface-3);
  overflow: hidden;
}
.undated-progress-fill { height: 100%; border-radius: 99px; }
.undated-progress-pct {
  font-size: 11px;
  font-weight: 700;
  color: var(--ink-muted);
  min-width: 30px;
  text-align: right;
}
.undated-schedule-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid var(--border-strong);
  background: var(--surface);
  color: var(--ink);
  border-radius: var(--radius, 6px);
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.undated-schedule-btn:hover {
  background: var(--system-navbar, #0f4c81);
  border-color: var(--system-navbar, #0f4c81);
  color: #fff;
}

/* ── "SEM DATA" FORM FIELD ── */
.nodate-check {
  display: flex;
  align-items: center;
  gap: 7px;
  margin-top: 6px;
  font-size: 12.5px;
  color: var(--ink);
  cursor: pointer;
}
.nodate-check input { cursor: pointer; accent-color: var(--accent, #0078d4); }
.nodate-hint {
  margin-top: 4px;
  font-size: 11px;
  color: var(--ink-muted);
}
.input-disabled { opacity: 0.5; }
.info-nodate { color: #9a6b00; }

/* ── REPORT RANGE CONTROLS ── */
.report-range-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  padding: 10px 18px 0;
}
.report-range-label {
  font-size: 12px;
  color: var(--ink-muted);
  font-weight: 600;
}
.report-range-input {
  border: 1px solid var(--border-strong);
  border-radius: var(--radius, 6px);
  padding: 4px 8px;
  font-size: 12.5px;
  background: var(--surface);
  color: var(--ink);
}
.report-range-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: 1px solid var(--border-strong);
  background: var(--surface);
  color: var(--ink);
  border-radius: 99px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s, color 0.12s, border-color 0.12s;
}
.report-range-btn:hover:not(:disabled) { background: var(--surface-3); }
.report-range-btn:disabled { opacity: 0.5; cursor: default; }
.report-range-btn--ghost { color: var(--ink-muted); }
.report-range-btn--nodate {
  margin-left: auto;
  color: #9a6b00;
  border-color: rgba(154, 107, 0, 0.4);
}
.report-range-btn--nodate.active,
.report-range-btn--nodate:hover:not(:disabled) {
  background: #9a6b00;
  border-color: #9a6b00;
  color: #fff;
}
.report-chip--nodate { background: rgba(154, 107, 0, 0.12); color: #9a6b00; }
.report-task-dates--nodate { color: #9a6b00; font-weight: 700; }
.report-week-label--nodate { font-size: 12.5px; font-weight: 700; color: #9a6b00; }
.report-range-hint { font-size: 12px; color: var(--ink-muted); }

/* ── FILTER COUNT BADGE ── */
.filter-count-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: 6px;
  min-width: 16px;
  height: 16px;
  border-radius: 99px;
  padding: 0 4px;
  background: var(--accent, #0f4c81);
  color: #fff;
  font-size: 10px;
  font-weight: 700;
}

/* ── ROW WRAPPER ── */
.planning-row {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.planning-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

/* ── MAIN BODY ── */
.planning-body {
  display: flex;
  flex: 1;
  min-height: 0;
  overflow: hidden;
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  margin-top: 10px;
}

/* ── LABEL RESIZE HANDLE ── */
.label-resize-handle {
  width: 5px;
  flex-shrink: 0;
  cursor: col-resize;
  background: transparent;
  border-right: 1px solid var(--border);
  position: relative;
  z-index: 3;
  transition: background 0.15s;
  touch-action: none;
}
.label-resize-handle::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 3px;
  height: 28px;
  border-left: 1px solid var(--border-strong);
  border-right: 1px solid var(--border-strong);
  border-radius: 2px;
}
.label-resize-handle:hover,
.label-resize-handle:active {
  background: color-mix(in srgb, var(--accent) 12%, transparent);
}

/* ── LABELS ── */
.gantt-labels {
  flex-shrink: 0;
  overflow: hidden;
  background: var(--surface-2);
  display: flex;
  flex-direction: column;
}

.label-header {
  position: sticky;
  top: 0;
  z-index: 2;
  height: 52px;
  min-height: 52px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 10px;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--ink-muted);
  border-bottom: 1px solid var(--border);
  background: var(--surface);
  flex-shrink: 0;
}

.label-header-progress { color: var(--ink-faint); }

.label-header-title { flex: 1; }

.label-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
  margin-right: 6px;
}

.label-hdr-btn {
  background: none;
  border: 1px solid var(--border);
  border-radius: 4px;
  padding: 2px 7px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  color: var(--ink-muted);
  transition: background 0.1s, color 0.1s;
}
.label-hdr-btn:hover {
  background: var(--surface-3);
  color: var(--ink);
}

.lbl-cb-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  padding: 0 6px 0 0;
  flex-shrink: 0;
}
.lbl-cb {
  width: 14px;
  height: 14px;
  cursor: pointer;
  accent-color: var(--accent, #0078d4);
}

.label-row--checked {
  background: rgba(31, 30, 26, 0.06) !important;
}

.label-row {
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 6px 10px;
  border-bottom: 1px solid var(--border);
  border-left: 3px solid transparent;
  cursor: pointer;
  transition: background 0.1s ease, border-left-color 0.15s ease;
  flex-shrink: 0;
}

.label-row:hover { background: var(--surface-3); }
.label-row.selected { background: color-mix(in srgb, var(--system-navbar, #0f4c81) 8%, var(--surface)); }

.label-row--multi {
  background: color-mix(in srgb, var(--system-navbar, #0f4c81) 6%, var(--surface)) !important;
}

.track-row--multi .bar { box-shadow: 0 0 0 2px var(--system-navbar, #0f4c81); }
.bar--multi { box-shadow: 0 0 0 2px var(--system-navbar, #0f4c81) !important; }

.group-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  padding: 1px 6px;
  border-radius: 99px;
  letter-spacing: 0.02em;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ── Group form popover ──────────────────────────────────── */
.group-toolbar-wrap { position: relative; }

.group-form-popover {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 200;
  background: var(--surface, #fff);
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 10px;
  padding: 14px 16px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.13);
  min-width: 260px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.group-form-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--ink-muted, #64748b);
  text-transform: uppercase;
  margin-bottom: 2px;
}

.group-form-input {
  border: 1px solid var(--border, #e2e8f0);
  border-radius: 6px;
  padding: 6px 10px;
  font-size: 13px;
  background: var(--bg, #fff);
  color: var(--ink, #1e293b);
  outline: none;
  width: 100%;
  box-sizing: border-box;
  &:focus { border-color: var(--system-navbar, #0f4c81); }
}

.group-form-actions { display: flex; gap: 8px; }

.group-form-btn {
  flex: 1;
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid var(--border, #e2e8f0);
  background: var(--surface, #fff);
  color: var(--ink, #1e293b);
  transition: background 0.15s;
  &:hover:not(:disabled) { background: var(--surface-3, #f1f5f9); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
  &--primary {
    background: var(--system-navbar, #0f4c81);
    color: #fff;
    border-color: transparent;
    &:hover:not(:disabled) { filter: brightness(1.1); }
  }
}

.tab-action-btn--group {
  display: flex;
  align-items: center;
}

.label-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.label-meta {
  font-size: 11px;
  color: var(--ink-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
}

.label-progress-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 5px;
}

.label-progress-track {
  flex: 1;
  height: 4px;
  background: var(--surface-3);
  border-radius: 2px;
  overflow: hidden;
}

.label-progress-fill {
  height: 100%;
  border-radius: 2px;
  transition: width 0.3s ease;
}

.label-progress-pct {
  font-size: 10px;
  font-weight: 700;
  color: var(--ink-muted);
  min-width: 28px;
  text-align: right;
}

/* ── SCROLLABLE TIMELINE ── */
.gantt-scroll {
  flex: 1;
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: var(--border-strong) transparent;
  min-width: 0;
}

.timeline {
  display: flex;
  flex-direction: column;
  min-width: max-content;
}

/* ── TIMELINE HEADER ── */
.timeline-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background: var(--surface);
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}

.header-groups {
  position: relative;
  overflow: hidden;
  background: var(--surface-2);
  border-bottom: 1px solid var(--border);
}

.header-group {
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  padding: 0 8px;
  font-size: 11px;
  font-weight: 700;
  color: var(--ink-muted);
  border-right: 1px solid var(--border);
  white-space: nowrap;
  overflow: hidden;
}

.header-days {
  display: flex;
}

.header-day {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: var(--ink-muted);
  border-right: 1px solid var(--border);
  height: 100%;
  transition: background 0.1s ease;
}

.header-day.weekend { background: rgba(0, 0, 0, 0.03); }
.header-day.today { background: color-mix(in srgb, var(--system-navbar, #0f4c81) 12%, transparent); color: var(--system-navbar, #0f4c81); font-weight: 700; }
.header-day.blocked { background: rgba(200, 0, 0, 0.06); color: var(--danger); }

/* ── TIMELINE BODY ── */
.timeline-body {
  position: relative;
}

.col-weekend {
  position: absolute;
  top: 0;
  background: rgba(0, 0, 0, 0.025);
  pointer-events: none;
}

.col-blocked {
  position: absolute;
  top: 0;
  background: rgba(200, 0, 0, 0.06);
  pointer-events: none;
  border-left: 1px dashed rgba(200, 0, 0, 0.25);
}

.today-line {
  position: absolute;
  top: 0;
  width: 1.5px;
  background: var(--system-navbar, #0f4c81);
  opacity: 0.7;
  pointer-events: none;
  z-index: 5;
}

.today-label {
  position: absolute;
  top: -18px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 10px;
  font-weight: 700;
  color: var(--system-navbar, #0f4c81);
  background: var(--surface);
  padding: 1px 5px;
  border-radius: 3px;
  white-space: nowrap;
}

/* ── TRACK ROWS ── */
.track-row {
  position: absolute;
  left: 0;
  right: 0;
  border-bottom: 1px solid var(--border);
  transition: background 0.1s ease;
}

.track-row.selected {
  background: color-mix(in srgb, var(--system-navbar, #0f4c81) 5%, transparent);
}

/* ── BAR ── */
.bar {
  position: absolute;
  border-radius: 6px;
  cursor: grab;
  overflow: visible;
  display: flex;
  align-items: center;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.12);
  transition: box-shadow 0.15s ease, filter 0.15s ease, transform 0.15s ease;
  z-index: 2;
  min-width: 18px;
}

.bar:hover {
  box-shadow: 0 3px 12px rgba(0, 0, 0, 0.22);
  filter: brightness(1.05);
  transform: translateY(-1px);
}

.bar.selected {
  box-shadow: 0 0 0 2px white, 0 0 0 3.5px var(--bar-color, var(--accent)), 0 4px 14px rgba(0,0,0,0.2);
  z-index: 3;
}

.bar.dragging {
  cursor: grabbing;
  opacity: 0.85;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

.bar-overdue {
  border-radius: 0 5px 5px 0;
  cursor: pointer;
  z-index: 1;
  opacity: 0.85;
}

.bar-progress {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.22);
  border-radius: 5px;
  pointer-events: none;
  transition: width 0.3s ease;
  overflow: hidden;
  max-width: 100%;
}

.bar-today-marker {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: rgba(255, 255, 255, 0.6);
  pointer-events: none;
  transform: translateX(-50%);
}

.bar-center-pct {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  font-size: 11px;
  font-weight: 700;
  color: #fff;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.55), 0 0 6px rgba(0, 0, 0, 0.3);
  pointer-events: none;
  z-index: 3;
  white-space: nowrap;
}

/* ── DEPENDENCY SVG ── */
.deps-svg {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
  z-index: 4;
  overflow: visible;
}

/* ── EDIT PANEL ── */
.planning-row.has-panel {
  /* labels width is now driven by labelsWidth ref via inline style */
}

.edit-panel {
  width: 300px;
  min-width: 300px;
  flex-shrink: 0;
  border-left: 1px solid var(--border);
  background: var(--surface);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.edit-panel--expanded {
  flex: 1;
  width: auto;
  min-width: 0;
}

.gantt-scroll--hidden {
  display: none;
}

.panel-expand-btn {
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 4px;
  cursor: pointer;
  color: var(--ink-muted);
  font-size: 14px;
  padding: 2px 7px;
  line-height: 1;
  transition: background 0.1s, color 0.1s;
}
.panel-expand-btn:hover {
  background: var(--surface-2);
  color: var(--ink);
}

.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 12px 14px;
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}

.panel-title {
  font-size: 13px;
  font-weight: 700;
  color: var(--ink);
  padding-left: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.panel-close {
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--ink-muted);
  font-size: 14px;
  padding: 2px 6px;
  border-radius: 3px;
  transition: background 0.1s ease;
  flex-shrink: 0;
}

.panel-close:hover { background: var(--surface-3); }

.panel-body {
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.panel-info { display: grid; gap: 6px; }

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
}

.info-row span { color: var(--ink-muted); }
.info-row strong { color: var(--ink); }

.info-row--block {
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
}

.info-description {
  font-size: 13px;
  color: var(--ink);
  line-height: 1.4;
  margin: 0;
}

.info-dep-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.info-dep-tag {
  background: var(--surface-3, rgba(0,0,0,0.06));
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 12px;
  color: var(--ink);
}

.info-team-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.info-team-tag {
  background: #6b8e99;
  color: #fff;
  border-radius: 4px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 500;
}

.info-tool-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.info-tool-tag {
  background: #7cb342;
  color: #fff;
  border-radius: 4px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 500;
}

.info-tool-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.info-tool-item {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.info-tool-status {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 3px;
  font-weight: 500;
}

.info-tool-status.status-pendente {
  background: #fff3cd;
  color: #856404;
}

.info-tool-status.status-aprovado {
  background: #d4edda;
  color: #155724;
}

.info-tool-status.status-reprovado {
  background: #f8d7da;
  color: #721c24;
}

.info-machinery-section {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.info-machinery-item {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.info-machinery-tag {
  background: #6b8e99;
  color: #fff;
  border-radius: 4px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 500;
}

.panel-track { margin: 2px 0; }

.panel-progress-bar {
  height: 6px;
  background: var(--surface-3);
  border-radius: 3px;
  overflow: hidden;
}

.panel-progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.panel-section-title {
  font-size: 13px;
  font-weight: 700;
  margin: 0 0 6px;
  color: var(--ink);
}

.panel-actions { display: flex; gap: 8px; flex-wrap: wrap; }
.panel-actions .btn { font-size: 13px; padding: 6px 12px; }

.edit-error { color: var(--danger); font-size: 12px; margin: 0; }

.workdays-row { display: flex; flex-wrap: wrap; gap: 6px; }

.wd-check {
  display: flex;
  align-items: center;
  gap: 3px;
  font-size: 12px;
  cursor: pointer;
}

.color-input {
  width: 44px;
  height: 30px;
  padding: 2px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  cursor: pointer;
}

.color-picker-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.recent-colors {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.recent-colors-label {
  font-size: 11px;
  color: var(--ink-muted, #888);
  white-space: nowrap;
}

.recent-color-swatch {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  border: 1.5px solid rgba(0,0,0,.12);
  cursor: pointer;
  padding: 0;
  background: transparent;
  transition: transform .1s, border-color .1s;
  &:hover {
    transform: scale(1.2);
    border-color: var(--ink-muted, #888);
  }
}

.dep-combo { display: flex; flex-direction: column; gap: 6px; }

.dep-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.dep-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: var(--accent, #2f6fce);
  color: #fff;
  border-radius: 4px;
  padding: 2px 6px 2px 8px;
  font-size: 12px;
}

.dep-tag-remove {
  background: none;
  border: none;
  color: rgba(255,255,255,0.8);
  cursor: pointer;
  padding: 0;
  font-size: 11px;
  line-height: 1;
  display: flex;
  align-items: center;
}
.dep-tag-remove:hover { color: #fff; }

.dep-search-wrap { position: relative; }

.dep-search-input {
  width: 100%;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  padding: 6px 10px;
  font-size: 13px;
  background: var(--bg);
  color: var(--ink);
  outline: none;
}
.dep-search-input:focus { border-color: var(--accent, #2f6fce); }

.dep-search-dropdown {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  background: var(--surface);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
  z-index: 50;
  max-height: 160px;
  overflow-y: auto;
}

.dep-search-item {
  padding: 7px 12px;
  font-size: 13px;
  cursor: pointer;
  color: var(--ink);
}
.dep-search-item:hover { background: var(--surface-3, rgba(0,0,0,0.05)); }

.dep-search-empty {
  padding: 8px 12px;
  font-size: 12px;
  color: var(--ink-muted);
}

/* ── FILE SELECTION ── */
.file-combo { display: flex; flex-direction: column; gap: 6px; }

.file-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.file-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #8b6f47;
  color: #fff;
  border-radius: 4px;
  padding: 2px 6px 2px 8px;
  font-size: 12px;
}

.file-tag-remove {
  background: none;
  border: none;
  color: rgba(255,255,255,0.8);
  cursor: pointer;
  padding: 0;
  font-size: 11px;
  line-height: 1;
  display: flex;
  align-items: center;
}
.file-tag-remove:hover { color: #fff; }

.file-search-wrap { position: relative; }

.file-search-input {
  width: 100%;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  padding: 6px 10px;
  font-size: 13px;
  background: var(--bg);
  color: var(--ink);
  outline: none;
}
.file-search-input:focus { border-color: #8b6f47; }

.file-search-dropdown {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  background: var(--surface);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
  z-index: 50;
  max-height: 160px;
  overflow-y: auto;
}

.file-search-item {
  padding: 7px 12px;
  font-size: 13px;
  cursor: pointer;
  color: var(--ink);
}
.file-search-item:hover { background: var(--surface-3, rgba(0,0,0,0.05)); }

.file-search-empty {
  padding: 8px 12px;
  font-size: 12px;
  color: var(--ink-muted);
}

/* ── TOOL SELECTION ── */
.tool-combo { display: flex; flex-direction: column; gap: 6px; }

.tool-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.tool-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #7cb342;
  color: #fff;
  border-radius: 4px;
  padding: 2px 6px 2px 8px;
  font-size: 12px;
}

.tool-tag-remove {
  background: none;
  border: none;
  color: rgba(255,255,255,0.8);
  cursor: pointer;
  padding: 0;
  font-size: 11px;
  line-height: 1;
  display: flex;
  align-items: center;
}
.tool-tag-remove:hover { color: #fff; }

.tool-search-wrap { position: relative; }

.tool-search-input {
  width: 100%;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  padding: 6px 10px;
  font-size: 13px;
  background: var(--bg);
  color: var(--ink);
  outline: none;
}
.tool-search-input:focus { border-color: #7cb342; }

.tool-search-dropdown {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  background: var(--surface);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
  z-index: 50;
  max-height: 160px;
  overflow-y: auto;
}

.tool-search-item {
  padding: 7px 12px;
  font-size: 13px;
  cursor: pointer;
  color: var(--ink);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.tool-search-item:hover { background: var(--surface-3, rgba(0,0,0,0.05)); }

.tool-operational {
  font-size: 11px;
  color: var(--ink-muted);
}

.tool-search-empty {
  padding: 8px 12px;
  font-size: 12px;
  color: var(--ink-muted);
}

.tool-quantity-input-wrap {
  display: flex;
  gap: 6px;
  align-items: center;
  padding-top: 4px;
}

.tool-quantity-input {
  width: 60px;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  padding: 6px 8px;
  font-size: 13px;
  background: var(--bg);
  color: var(--ink);
}

.tool-add-btn {
  background: #7cb342;
  color: #fff;
  border: none;
  border-radius: var(--radius);
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.2s;
}
.tool-add-btn:hover { background: #689f38; }

.team-combo { display: flex; flex-direction: column; gap: 6px; }

.team-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.team-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #6b8e99;
  color: #fff;
  border-radius: 4px;
  padding: 2px 6px 2px 8px;
  font-size: 12px;
}

.team-tag-remove {
  background: none;
  border: none;
  color: rgba(255,255,255,0.8);
  cursor: pointer;
  padding: 0;
  font-size: 11px;
  line-height: 1;
  display: flex;
  align-items: center;
}
.team-tag-remove:hover { color: #fff; }

.team-search-wrap { position: relative; }

.team-search-input {
  width: 100%;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  padding: 6px 10px;
  font-size: 13px;
  background: var(--bg);
  color: var(--ink);
  outline: none;
}
.team-search-input:focus { border-color: #6b8e99; }

.team-search-dropdown {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  background: var(--surface);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
  z-index: 50;
  max-height: 160px;
  overflow-y: auto;
}

.team-search-item {
  padding: 7px 12px;
  font-size: 13px;
  cursor: pointer;
  color: var(--ink);
}
.team-search-item:hover { background: var(--surface-3, rgba(0,0,0,0.05)); }

.team-search-empty {
  padding: 8px 12px;
  font-size: 12px;
  color: var(--ink-muted);
}

.hint-sm { font-size: 12px; color: var(--ink-muted); }

/* ── MACHINERY SELECTOR ── */
.machinery-combo { display: flex; flex-direction: column; gap: 6px; }

.machinery-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

.machinery-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #6b8e99;
  color: #fff;
  border-radius: 4px;
  padding: 2px 6px 2px 8px;
  font-size: 12px;
}

.machinery-tag-remove {
  background: none;
  border: none;
  color: rgba(255,255,255,0.8);
  cursor: pointer;
  padding: 0;
  font-size: 11px;
  line-height: 1;
  display: flex;
  align-items: center;
}
.machinery-tag-remove:hover { color: #fff; }

.machinery-search-wrap { position: relative; }

.machinery-search-input {
  width: 100%;
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  padding: 6px 10px;
  font-size: 13px;
  background: var(--bg);
  color: var(--ink);
  outline: none;
}
.machinery-search-input:focus { border-color: #6b8e99; }

.machinery-search-dropdown {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  background: var(--surface);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius);
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
  z-index: 50;
  max-height: 160px;
  overflow-y: auto;
}

.machinery-search-item {
  padding: 7px 12px;
  font-size: 13px;
  cursor: pointer;
  color: var(--ink);
}
.machinery-search-item:hover { background: var(--surface-3, rgba(0,0,0,0.05)); }

.machinery-search-empty {
  padding: 8px 12px;
  font-size: 12px;
  color: var(--ink-muted);
}

.machinery-category {
  font-size: 11px;
  color: var(--ink-muted);
}


/* ── BLOCKED DAYS MODAL ── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 200;
  display: grid;
  place-items: center;
}

.modal-card {
  background: var(--surface);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-lg);
  padding: 24px;
  width: min(420px, 90vw);
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.modal-header h3 { margin: 0; font-size: 16px; }

.blocked-add-row { display: flex; gap: 8px; align-items: center; }
.blocked-add-row .filter-input { flex: 1; }

.blocked-list { display: flex; flex-direction: column; gap: 4px; max-height: 180px; overflow-y: auto; }

.blocked-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 5px 8px;
  background: var(--surface-3);
  border-radius: var(--radius);
  font-size: 13px;
}

.remove-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--ink-muted);
  padding: 2px 5px;
  border-radius: 3px;
  font-size: 12px;
}

.remove-btn:hover { background: var(--surface-2); color: var(--danger); }

.modal-footer { display: flex; gap: 8px; justify-content: flex-end; }

/* ── PROGRESS HISTORY ── */
.progress-history {
  margin-top: 12px;
  border-top: 1px solid var(--border);
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.progress-history-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--ink-muted);
  margin: 0 0 4px 0;
}

.progress-history-item {
  border: 1px solid var(--border);
  background: var(--surface-2, rgba(0,0,0,0.02));
  border-radius: var(--radius-lg, 10px);
  font-size: 12px;
  overflow: hidden;
  transition: border-color 0.15s, background 0.15s;
}
.progress-history-item--open {
  border-color: color-mix(in srgb, var(--system-navbar, #0f4c81) 40%, var(--border));
  background: var(--surface);
}

.progress-history-head {
  display: flex;
  align-items: center;
  gap: 2px;
}

.progress-history-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  padding: 8px 10px;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: pointer;
  transition: background 0.1s;
}
.progress-history-row:hover { background: var(--surface-3, rgba(0,0,0,0.04)); }
.progress-history-row--static { cursor: default; }
.progress-history-row--static:hover { background: transparent; }

.progress-history-pct {
  font-weight: 700;
  color: var(--system-navbar, #0f4c81);
  min-width: 38px;
  font-size: 13px;
}

.progress-history-date {
  color: var(--ink-muted);
  white-space: nowrap;
}

.progress-history-badges {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--ink-faint, var(--ink-muted));
  margin-left: auto;
}
.progress-badge-icon { flex-shrink: 0; }

.progress-history-chevron {
  flex-shrink: 0;
  color: var(--ink-muted);
  transition: transform 0.18s ease;
}
.progress-history-item--open .progress-history-chevron { transform: rotate(180deg); }

.progress-history-detail {
  display: grid;
  gap: 8px;
  padding: 0 10px 10px;
  border-top: 1px dashed var(--border);
  padding-top: 8px;
  margin: 0 0 0;
  animation: progress-detail-in 0.16s ease;
}
.progress-detail-note {
  margin: 0;
  color: var(--ink);
  font-size: 12.5px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}
.progress-detail-photo {
  width: 100%;
  max-height: 220px;
  object-fit: cover;
  border-radius: var(--radius, 8px);
  cursor: zoom-in;
  border: 1px solid var(--border);
}

@keyframes progress-detail-in {
  from { opacity: 0; transform: translateY(-3px); }
  to   { opacity: 1; transform: translateY(0); }
}

.progress-history-del {
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--ink-muted);
  padding: 2px 5px;
  margin-right: 6px;
  border-radius: 3px;
  font-size: 11px;
  flex-shrink: 0;
  transition: background 0.1s, color 0.1s;
}
.progress-history-del:hover {
  background: rgba(164, 38, 44, 0.1);
  color: #a4262c;
}

/* ── Progress photo ── */
.progress-photo-thumb {
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: 4px;
  cursor: pointer;
  flex-shrink: 0;
  &:hover { opacity: .85; }
}

.photo-upload-area {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 72px;
  border: 2px dashed var(--border, rgba(0,0,0,.15));
  border-radius: 8px;
  cursor: pointer;
  transition: border-color .15s;
  overflow: hidden;
  &:hover { border-color: var(--accent, #2563eb); }
}
.photo-preview {
  width: 100%;
  max-height: 140px;
  object-fit: cover;
  display: block;
}
.photo-upload-hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  color: var(--ink-muted, #888);
  font-size: 12px;
}
.photo-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(0,0,0,.55);
  color: #fff;
  border: none;
  border-radius: 50%;
  width: 22px;
  height: 22px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* ── Comments ── */
.comments-section {
  margin-top: 12px;
  border-top: 1px solid var(--border);
  padding-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.comments-count {
  background: var(--surface-3, rgba(0,0,0,.06));
  border-radius: 99px;
  padding: 1px 6px;
  font-size: 10px;
  font-weight: 700;
  color: var(--ink-muted);
  margin-left: 4px;
}

.comments-loading, .comments-empty {
  font-size: 12px;
  color: var(--ink-muted);
  text-align: center;
  padding: 8px 0;
}

.comments-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 260px;
  overflow-y: auto;
}

.comment-item {
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.comment-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  overflow: hidden;
}
.comment-avatar-img { width: 100%; height: 100%; object-fit: cover; }

.comment-body {
  flex: 1;
  min-width: 0;
  background: var(--surface-3, rgba(0,0,0,.04));
  border-radius: 8px;
  padding: 6px 10px;
}

.comment-meta {
  display: flex;
  gap: 8px;
  align-items: baseline;
  margin-bottom: 3px;
}
.comment-author { font-size: 11px; font-weight: 700; color: var(--ink); }
.comment-date { font-size: 10px; color: var(--ink-muted); }

.comment-text {
  margin: 0;
  font-size: 12px;
  color: var(--ink);
  white-space: pre-wrap;
  word-break: break-word;
}

.comment-del {
  background: transparent;
  border: none;
  cursor: pointer;
  color: var(--ink-muted);
  font-size: 10px;
  padding: 2px 4px;
  border-radius: 3px;
  flex-shrink: 0;
  margin-top: 2px;
  &:hover { color: #a4262c; }
}

.comment-input-row {
  display: flex;
  gap: 6px;
  align-items: flex-end;
}

.comment-input {
  flex: 1;
  padding: 7px 10px;
  border: 1.5px solid var(--border, rgba(0,0,0,.12));
  border-radius: 8px;
  font-size: 12px;
  resize: none;
  color: var(--ink);
  background: var(--bg);
  font-family: inherit;
  &:focus { outline: none; border-color: var(--accent, #2563eb); }
}

.comment-send {
  padding: 7px 14px;
  font-size: 12px;
  white-space: nowrap;
  &:disabled { opacity: .45; cursor: not-allowed; }
}

/* ── Lightbox ── */
.lightbox-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.85);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
}
.lightbox-img {
  max-width: 90vw;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 8px 48px rgba(0,0,0,.5);
  cursor: default;
}
.lightbox-close {
  position: absolute;
  top: 16px;
  right: 20px;
  background: rgba(255,255,255,.15);
  border: none;
  color: #fff;
  font-size: 20px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: rgba(255,255,255,.3); }
}

/* ── PRINT EXPORT ── */
@media print {
  .edit-panel { display: none; }
  .planning-shell { height: auto; overflow: visible; }
  .planning-body { overflow: visible; }
  .gantt-scroll { overflow: visible; }
}

/* ── FORM FIELDS ── */
.form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
  margin-bottom: 16px;
}

.input-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.input-field.full-width {
  grid-column: 1 / -1;
}

.input-field label {
  font-size: 13px;
  font-weight: 500;
  color: var(--ink);
}

.input-field input,
.input-field textarea,
.input-field select {
  padding: 8px 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius, 4px);
  font-size: 13px;
  font-family: inherit;
  background: var(--surface);
  color: var(--ink);
}

.input-field textarea {
  resize: vertical;
  min-height: 80px;
  font-family: monospace;
}

/* ── RESPONSIVE ── */
@media (max-width: 900px) {
  .planning-row.has-panel {
    flex-direction: column;
    .planning-content { display: none; }
    .edit-panel {
      width: 100%;
      min-width: 0;
      max-height: none;
      flex: 1;
      border-left: none;
      border-top: none;
    }
  }

  .panel-expand-btn { display: none; }
}

@media (max-width: 640px) {
  /* Topbar: search on its own row, chips scroll horizontally in a single line */
  .planning-topbar {
    gap: 8px;
    padding: 8px 0 0;
  }
  .topbar-search {
    flex: 1 1 100%;
    min-width: 0;
  }
  .topbar-search-input {
    padding-top: 8px;
    padding-bottom: 8px;
    font-size: 14px;
  }
  .status-chips {
    flex: 1 1 auto;
    flex-wrap: nowrap;
    overflow-x: auto;
    scrollbar-width: none;
    padding-bottom: 2px;
    -webkit-overflow-scrolling: touch;
  }
  .status-chips::-webkit-scrollbar { display: none; }
  .status-chip { padding: 6px 12px; }
  .topbar-overall {
    margin-left: 0;
    flex: 0 0 auto;
  }
  .topbar-overall-track { width: 64px; }

  /* Narrower task column so the timeline gets more room */
  .gantt-labels {
    width: 150px !important;
    min-width: 150px !important;
  }
  .label-header { padding: 0 8px; }
  .label-row { padding: 6px 8px; }

  .planning-body { margin-top: 8px; }

  /* Sem data definida: empilha as informações verticalmente em linhas mais altas */
  .undated-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 12px 12px 12px 32px;
    position: relative;
  }
  .undated-dot {
    position: absolute;
    left: 12px;
    top: 16px;
  }
  .undated-row .lbl-cb-label {
    position: absolute;
    right: 12px;
    top: 12px;
  }
  .undated-info { width: 100%; }
  .undated-name {
    white-space: normal;
    line-height: 1.3;
  }
  .undated-meta {
    flex-wrap: wrap;
    margin-top: 3px;
  }
  .undated-progress { width: 100%; }
  .undated-schedule-btn {
    width: 100%;
    justify-content: center;
    padding: 8px 10px;
  }
}
.hidden-file-input {
  display: none;
}

.import-overlay {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.42);
  display: grid;
  place-items: center;
  z-index: 80;
  padding: 20px;
}

.import-modal {
  width: min(1180px, 100%);
  max-height: min(88vh, 920px);
  overflow: auto;
  background: #fffdf8;
  border-radius: 20px;
  border: 1px solid rgba(32, 31, 30, 0.12);
  box-shadow: 0 28px 80px rgba(15, 23, 42, 0.18);
  padding: 22px;
}

.import-modal-head {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  align-items: flex-start;
}

.import-modal-head h3 {
  margin: 0;
  font-size: 24px;
}

.import-subtitle,
.import-helper {
  margin: 6px 0 0;
  color: var(--ink-muted, #605e5c);
}

.import-upload-card {
  margin-top: 18px;
  border: 1px dashed rgba(15, 76, 129, 0.28);
  border-radius: 16px;
  background: linear-gradient(180deg, rgba(245, 248, 252, 0.96), rgba(255, 255, 255, 0.96));
  padding: 18px;
}

.import-upload-actions,
.import-review-head,
.import-inline-row,
.import-tag-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.import-review-head {
  justify-content: space-between;
  margin: 20px 0 12px;
}

.import-template-link {
  margin-top: 12px;
  border: none;
  background: transparent;
  color: var(--system-navbar, #0f4c81);
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
}

.import-error {
  margin-top: 12px;
  color: #b42318;
  font-weight: 600;
}

.import-table-wrap {
  overflow: auto;
  border: 1px solid rgba(32, 31, 30, 0.1);
  border-radius: 14px;
}

.import-table {
  width: 100%;
  border-collapse: collapse;
  min-width: 980px;
}

.import-table th,
.import-table td {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(32, 31, 30, 0.08);
  vertical-align: top;
  text-align: left;
}

.import-table th {
  position: sticky;
  top: 0;
  background: #f6f3eb;
  z-index: 1;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: .04em;
  color: #6b6458;
}

.import-cell-input,
.import-cell-textarea,
.import-multi-select {
  width: 100%;
  border: 1px solid rgba(32, 31, 30, 0.14);
  border-radius: 10px;
  background: #fff;
  padding: 9px 10px;
  font: inherit;
  color: inherit;
}

.import-cell-textarea {
  min-height: 72px;
  resize: vertical;
}

.import-cell-input--sm,
.import-qty-input {
  width: 88px;
}

.import-actions-col {
  white-space: nowrap;
}

.import-advanced-row td {
  background: #fbfaf7;
}

.import-advanced-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 16px;
}

.import-multi-select {
  min-height: 120px;
}

.import-tag-list {
  display: grid;
  gap: 8px;
  margin-top: 10px;
}

.import-tag-item {
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 8px 10px;
  border-radius: 10px;
  background: rgba(15, 76, 129, 0.06);
}

.import-color-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
}

.import-color-chip i {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  display: inline-block;
  border: 1px solid rgba(32, 31, 30, 0.12);
}

/* ── FILTER IMPORT BUTTON ── */
.filter-divider {
  height: 1px;
  background: var(--border, #e0ded8);
  margin: 8px 0 4px;
}

.filter-import-btn {
  display: flex;
  align-items: center;
  gap: 7px;
  width: 100%;
  text-align: left;
  border: none;
  background: transparent;
  padding: 8px 12px;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink, #1f1e1a);
  cursor: pointer;
  border-radius: var(--radius, 6px);
  &:hover { background: var(--surface-3, #f0ede8); }
}

/* ── SHARE BUTTON ── */
.tab-action-btn--share {
  display: inline-flex;
  align-items: center;
  background: var(--system-navbar, #0f4c81);
  color: #fff;
  border-color: transparent;
  font-weight: 600;
  &:hover { background: color-mix(in srgb, var(--system-navbar, #0f4c81) 85%, #000 15%); color: #fff; }
}

/* ── SHARE BACKDROP & MODAL ── */
.share-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  z-index: 9000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.share-modal {
  background: var(--surface, #fff);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.22);
  width: 100%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;

  &--sm { max-width: 380px; }
  &--lg { max-width: 600px; }
}

.share-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 18px 20px 14px;
  border-bottom: 1px solid var(--border, #e0ded8);
  flex-shrink: 0;
}

.share-modal-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 700;
  color: var(--ink, #1f1e1a);
}

.share-modal-close {
  background: none;
  border: none;
  font-size: 16px;
  color: var(--ink-muted, #605e5c);
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  line-height: 1;
  &:hover { background: var(--surface-3, #f0ede8); }
}

.share-modal-footer {
  display: flex;
  gap: 10px;
  padding: 14px 20px 18px;
  border-top: 1px solid var(--border, #e0ded8);
  flex-shrink: 0;
}

.share-section {
  padding: 16px 20px;
  &--manage { padding-top: 4px; }
}

.share-section-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--ink-muted, #605e5c);
  margin: 0 0 10px;
}

.share-divider {
  height: 1px;
  background: var(--border, #e0ded8);
  margin: 0 20px;
}

.share-action-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.share-action-btn {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 12px 14px;
  border: 1px solid var(--border, #e0ded8);
  border-radius: 8px;
  background: var(--surface, #fff);
  cursor: pointer;
  text-align: left;
  transition: background 0.12s, border-color 0.12s;
  &:hover {
    background: var(--surface-2, #f7f5f0);
    border-color: var(--border-strong, #c8c6c0);
  }
}

.share-action-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  &--excel { background: #e8f5ee; color: #107C41; }
  &--pdf   { background: #fce8e8; color: #C00000; }
  &--link  { background: #e8eef5; color: #0f4c81; }
  &--email { background: #f0e8f5; color: #6b21a8; }
}

.share-action-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.share-action-label {
  font-size: 14px;
  font-weight: 600;
  color: var(--ink, #1f1e1a);
}

.share-action-sub {
  font-size: 12px;
  color: var(--ink-muted, #605e5c);
}

.share-manage-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;
  border: 1px dashed var(--border-strong, #c8c6c0);
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-muted, #605e5c);
  width: 100%;
  justify-content: center;
  &:hover { background: var(--surface-2, #f7f5f0); color: var(--ink, #1f1e1a); }
}

/* ── DURATION PRESETS ── */
.duration-presets {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.duration-btn {
  padding: 7px 16px;
  border: 1px solid var(--border-strong, #c8c6c0);
  border-radius: 20px;
  background: var(--surface, #fff);
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: var(--ink-muted, #605e5c);
  transition: background 0.12s, border-color 0.12s, color 0.12s;
  &:hover { border-color: var(--system-navbar, #0f4c81); color: var(--system-navbar, #0f4c81); }
  &.active {
    background: var(--system-navbar, #0f4c81);
    border-color: var(--system-navbar, #0f4c81);
    color: #fff;
    font-weight: 600;
  }
}

/* ── GENERATED LINK ── */
.share-link-row {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.share-link-input {
  flex: 1;
  min-width: 0;
  padding: 8px 12px;
  border: 1px solid var(--border-strong, #c8c6c0);
  border-radius: 6px;
  font-size: 12px;
  background: var(--surface-2, #f7f5f0);
  color: var(--ink, #1f1e1a);
  font-family: monospace;
}

.share-copy-btn {
  padding: 8px 14px;
  border: 1px solid var(--border-strong, #c8c6c0);
  border-radius: 6px;
  background: var(--surface, #fff);
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  &:hover { background: var(--surface-3, #f0ede8); }
}

.share-expiry-note {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  color: var(--ink-muted, #605e5c);
  margin: 0;
}

.share-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid var(--border-strong, #c8c6c0);
  border-radius: 6px;
  font-size: 13px;
  background: var(--surface, #fff);
  color: var(--ink, #1f1e1a);
  box-sizing: border-box;
}

.share-error {
  font-size: 12px;
  color: #c00;
  padding: 0 20px 14px;
  margin: 0;
}

/* ── MANAGE LIST ── */
.manage-list {
  padding: 12px 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 50vh;
  overflow-y: auto;
}

.manage-empty {
  text-align: center;
  font-size: 13px;
  color: var(--ink-muted, #605e5c);
  padding: 24px 0;
}

.manage-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid var(--border, #e0ded8);
  border-radius: 8px;
  background: var(--surface, #fff);

  &.revoked {
    opacity: 0.5;
    background: var(--surface-2, #f7f5f0);
  }
  &.expired:not(.revoked) {
    border-color: #f0c050;
    background: #fffdf0;
  }
}

.manage-item-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.manage-item-hash {
  display: flex;
  align-items: center;
  gap: 8px;
}

.manage-chip {
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  background: var(--surface-3, #f0ede8);
  color: var(--ink-muted, #605e5c);
  &--email { background: #f0e8f5; color: #6b21a8; }
}

.manage-hash-code {
  font-size: 11px;
  font-family: monospace;
  color: var(--ink-muted, #605e5c);
}

.manage-item-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  color: var(--ink-muted, #605e5c);
  flex-wrap: wrap;
}

.manage-status {
  font-size: 11px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 10px;
  &--active  { background: #e8f5ee; color: #107C41; }
  &--expired { background: #fff3cd; color: #856404; }
  &--revoked { background: #f5e8e8; color: #c00; }
}

.manage-item-actions {
  display: flex;
  gap: 6px;
  flex-shrink: 0;
}

.manage-btn {
  padding: 5px 10px;
  border: 1px solid var(--border-strong, #c8c6c0);
  border-radius: 5px;
  background: var(--surface, #fff);
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: var(--ink-muted, #605e5c);
  white-space: nowrap;
  &:hover { background: var(--surface-3, #f0ede8); }
  &--extend { color: var(--system-navbar, #0f4c81); border-color: var(--system-navbar, #0f4c81); }
  &--revoke { color: #c00; border-color: #c00; &:hover { background: #fce8e8; } }
}

/* ── REPORT BUTTON ── */
.tab-action-btn--report {
  background: var(--system-navbar, #0f4c81);
  color: #fff;
  border-color: transparent;
  &:hover { opacity: .88; }
}

/* ── GANTT REPORT MODAL ── */
.report-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.45);
  z-index: 1200;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 60px 16px 32px;
  overflow-y: auto;
}
.report-modal {
  background: var(--bg, #faf9f7);
  border-radius: 12px;
  width: 100%;
  max-width: 680px;
  box-shadow: 0 8px 40px rgba(0,0,0,.22);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.report-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  border-bottom: 1px solid var(--border, #e2e0dc);
  background: var(--surface, #f3f2f0);
  gap: 12px;
}
.report-modal-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  font-size: 14px;
  color: var(--ink, #201f1e);
}
.report-modal-controls {
  display: flex;
  align-items: center;
  gap: 10px;
}
.report-week-nav {
  display: flex;
  align-items: center;
  gap: 6px;
}
.report-week-btn {
  background: none;
  border: 1px solid var(--border, #e2e0dc);
  border-radius: 6px;
  width: 26px;
  height: 26px;
  cursor: pointer;
  font-size: 14px;
  color: var(--ink, #201f1e);
  display: flex;
  align-items: center;
  justify-content: center;
  &:disabled { opacity: .35; cursor: default; }
  &:not(:disabled):hover { background: var(--surface-3, #f0ede8); }
}
.report-week-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--ink-muted, #605e5c);
  min-width: 110px;
  text-align: center;
}
.report-modal-close {
  background: none;
  border: none;
  cursor: pointer;
  color: var(--ink-muted, #605e5c);
  font-size: 16px;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { background: var(--surface-3, #f0ede8); }
}
.report-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 40px 24px;
  color: var(--ink-muted, #605e5c);
  font-size: 13px;
}
.report-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid var(--border, #e2e0dc);
  border-top-color: var(--system-navbar, #0f4c81);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  flex-shrink: 0;
}
@keyframes spin { to { transform: rotate(360deg); } }
.report-error {
  padding: 24px;
  color: #c00;
  font-size: 13px;
}
.report-body {
  padding: 16px 18px 24px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-height: 72vh;
  overflow-y: auto;
}
.report-summary-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.report-chip {
  padding: 3px 10px;
  border-radius: 100px;
  font-size: 11.5px;
  font-weight: 600;
  &--total { background: #e8edf4; color: #1a4b82; }
  &--done { background: #d4edda; color: #1a6e35; }
  &--late { background: #fde8e8; color: #b50000; }
  &--pending { background: #fff3cd; color: #7a5500; }
}
.report-section-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .06em;
  color: var(--ink-muted, #605e5c);
  margin: 6px 0 0;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--border, #e2e0dc);
}
.report-empty {
  font-size: 13px;
  color: var(--ink-muted, #605e5c);
  text-align: center;
  padding: 12px 0;
}
.report-task-card {
  background: var(--surface, #f3f2f0);
  border-radius: 8px;
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  border: 1px solid var(--border, #e2e0dc);
}
.report-task-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}
.report-task-left {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.report-task-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--ink, #201f1e);
}
.report-task-group {
  font-size: 11px;
  color: var(--ink-muted, #605e5c);
  font-style: italic;
}
.report-task-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  flex-shrink: 0;
}
.report-task-pct {
  font-size: 13px;
  font-weight: 700;
  &.pct--done { color: #1a6e35; }
  &.pct--late { color: #b50000; }
  &.pct--progress { color: var(--system-navbar, #0f4c81); }
  &.pct--idle { color: var(--ink-muted, #605e5c); }
}
.report-task-dates {
  font-size: 11px;
  color: var(--ink-muted, #605e5c);
}
.report-progress-bar {
  height: 4px;
  background: var(--border, #e2e0dc);
  border-radius: 2px;
  overflow: hidden;
}
.report-progress-fill {
  height: 100%;
  border-radius: 2px;
  transition: width .3s;
  &.pct--done { background: #22a86e; }
  &.pct--late { background: #e53935; }
  &.pct--progress { background: var(--system-navbar, #0f4c81); }
  &.pct--idle { background: var(--border, #e2e0dc); }
}
.report-updates {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-top: 4px;
}
.report-update-row {
  display: flex;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 12px;
}
.report-update-meta {
  color: var(--ink-muted, #605e5c);
  font-weight: 500;
  white-space: nowrap;
}
.report-update-note {
  color: var(--ink, #201f1e);
  flex: 1;
}
.report-update-photo {
  width: 56px;
  height: 56px;
  object-fit: cover;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid var(--border, #e2e0dc);
  &:hover { opacity: .85; }
}
.report-upcoming-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 7px 10px;
  background: var(--surface, #f3f2f0);
  border-radius: 6px;
  border: 1px solid var(--border, #e2e0dc);
  gap: 8px;
}
.report-upcoming-name {
  font-size: 13px;
  font-weight: 500;
  color: var(--ink, #201f1e);
}
.report-upcoming-date {
  font-size: 11.5px;
  color: var(--ink-muted, #605e5c);
  white-space: nowrap;
}

.report-print-row {
  display: flex;
  justify-content: flex-end;
  padding-top: 4px;
  border-top: 1px solid var(--border, #e2e0dc);
  margin-top: 4px;
}
.report-print-btn {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 8px 18px;
  background: var(--system-navbar, #0f4c81);
  color: #fff;
  border: none;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity .15s;
  &:hover:not(:disabled) { opacity: .88; }
  &:disabled { opacity: .5; cursor: default; }
}

/* Print preview takes over full screen */
.gantt-print-overlay {
  position: fixed;
  inset: 0;
  z-index: 1400;
  background: var(--bg, #faf9f7);
  display: flex;
  flex-direction: column;
}
.gantt-print-overlay :deep(.prev-shell) {
  flex: 1;
  height: 100%;
}
</style>
